import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "../config/apiConfig";
import StressTestingWizard from "../components/StressTestingWizard";

// Inline Premium CSS styles for Performance Engineering
const peCss = `
  .pe-container {
    background-color: #f8fafc;
    color: #0f172a;
    font-family: 'Outfit', 'Inter', -apple-system, sans-serif;
    min-height: 100vh;
    padding: 24px;
    text-align: left;
  }
  .pe-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }
  .pe-header h1 {
    font-size: 26px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
  }
  .pe-header p {
    color: #64748b;
    margin: 4px 0 0 0;
    font-size: 14px;
  }
  .pe-kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
  }
  .pe-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
  }
  .pe-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.05);
    border-color: rgba(124, 58, 237, 0.2);
  }
  .pe-kpi-label {
    font-size: 13px;
    color: #64748b;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.05em;
  }
  .pe-kpi-value {
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
    margin-top: 8px;
  }
  .pe-kpi-trend {
    font-size: 11px;
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .trend-up { color: #10b981; }
  .trend-down { color: #ef4444; }
  
  .pe-tabs-container {
    display: flex;
    border-bottom: 1px solid #e2e8f0;
    margin-bottom: 24px;
    gap: 8px;
  }
  .pe-tab-btn {
    padding: 12px 18px;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    color: #64748b;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
  }
  .pe-tab-btn:hover {
    color: #FD6101;
  }
  .pe-tab-btn.active {
    color: #FD6101;
    border-bottom-color: #FD6101;
    font-weight: 600;
  }

  .pe-wizard-step {
    border-left: 3px solid #e2e8f0;
    padding-left: 20px;
    position: relative;
    padding-bottom: 20px;
  }
  .pe-wizard-step.active {
    border-left-color: #FD6101;
  }
  .pe-wizard-dot {
    position: absolute;
    left: -9px;
    top: 0;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: #e2e8f0;
    border: 3px solid #ffffff;
  }
  .pe-wizard-step.active .pe-wizard-dot {
    background: #FD6101;
  }

  .pe-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }
  .pe-grid-3 {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }
  .pe-input {
    width: 100%;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    color: #0f172a;
    padding: 10px 14px;
    outline: none;
    transition: all 0.2s;
    font-size: 14px;
  }
  .pe-input:focus {
    border-color: #FD6101;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.08);
  }
  .pe-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #475569;
    margin-bottom: 6px;
  }
  .pe-btn-primary {
    background: #FD6101;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
  }
  .pe-btn-primary:hover {
    background: #e05300;
  }
  .pe-btn-secondary {
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
    padding: 10px 20px;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
  }
  .pe-btn-secondary:hover {
    background: #e2e8f0;
  }
  
  .type-card {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    cursor: pointer;
    background: #fff;
    transition: all 0.2s;
  }
  .type-card:hover {
    border-color: #FD6101;
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.06);
  }
  .type-card.active {
    border-color: #FD6101;
    background: rgba(124, 58, 237, 0.04);
  }
  
  .ai-box {
    background: #faf5ff;
    border: 1px solid #e9d5ff;
    border-radius: 8px;
    padding: 16px;
    margin-top: 16px;
  }
`;

function getDynamicJourneyData(url) {
  let hostname = "Qintra.com";
  try {
    if (url) {
      const parsed = new URL(url.startsWith("http") ? url : "https://" + url);
      hostname = parsed.hostname || url;
    }
  } catch (e) {
    hostname = url || "Qintra.com";
  }

  // Clean hostname for labels
  const siteName = hostname.replace("www.", "").split(".")[0];
  const capitalizedSite = siteName.charAt(0).toUpperCase() + siteName.slice(1);

  // Determine category
  const urlLower = (url || "").toLowerCase();
  let category = "saas"; // default
  const currentHostname = typeof window !== "undefined" && window.location && window.location.hostname ? window.location.hostname.toLowerCase() : "";

  if (
    (currentHostname && urlLower.includes(currentHostname)) ||
    urlLower.includes("localhost") ||
    urlLower.includes("127.0.0.1") ||
    urlLower.includes("Qintra") ||
    urlLower.includes("rabbit_ai")
  ) {
    category = "Qintra";
  } else if (urlLower.includes("letsfame") || urlLower.includes("weebly") || urlLower.includes("wix") || urlLower.includes("portfolio") || urlLower.includes("agency") || urlLower.includes("personal")) {
    category = "portfolio";
  } else if (urlLower.includes("shop") || urlLower.includes("store") || urlLower.includes("cart") || urlLower.includes("amazon") || urlLower.includes("ebay") || urlLower.includes("shopify") || urlLower.includes("checkout")) {
    category = "ecommerce";
  } else if (urlLower.includes("crm") || urlLower.includes("admin") || urlLower.includes("student") || urlLower.includes("school") || urlLower.includes("dashboard")) {
    category = "saas";
  } else {
    category = "generic";
  }

  if (category === "portfolio") {
    return {
      screens: [
        { id: "landing", name: "1. Home Page", time: "1.8s", ttfb: "150ms", fcp: "0.8s", lcp: "1.6s", apis: 4, status: "Good", rating: "94/100", icon: "🏠", size: "1.2 MB", details: "Landing page index load" },
        { id: "about", name: "2. About Us", time: "1.1s", ttfb: "90ms", fcp: "0.4s", lcp: "0.9s", apis: 2, status: "Excellent", rating: "98/100", icon: "ℹ️", size: "450 KB", details: "About Us biography section" },
        { id: "features", name: "3. Portfolio Grid", time: "3.2s", ttfb: "320ms", fcp: "1.4s", lcp: "2.9s", apis: 8, status: "Warning", rating: "78/100", icon: "🎨", size: "4.8 MB", details: "Project grids & case studies" },
        { id: "testimonials", name: "4. Testimonials", time: "1.5s", ttfb: "110ms", fcp: "0.6s", lcp: "1.2s", apis: 3, status: "Good", rating: "95/100", icon: "💬", size: "620 KB", details: "Client feedback testimonials list" },
        { id: "contact", name: "5. Contact Form", time: "1.3s", ttfb: "100ms", fcp: "0.5s", lcp: "1.1s", apis: 5, status: "Excellent", rating: "96/100", icon: "✉️", size: "380 KB", details: "Contact page with submit triggers" }
      ],
      apis: [
        "/assets/images/banner_hero.jpg",
        "/api/v1/portfolio/details",
        "/api/v1/portfolio/projects",
        "/api/v1/feedback/reviews",
        "/api/v1/contact/submit"
      ],
      bottlenecks: [
        {
          severity: "CRITICAL",
          confidence: "98%",
          title: "Large Uncompressed Images (Portfolio Grid)",
          rootCause: "Project images in the Portfolio Grid are uploaded as raw PNG files instead of optimized formats.",
          evidence: "Hero image banner_hero.jpg is 4.8MB. Static network transfer takes 2.9 seconds.",
          fix: "Convert all project images to WebP or AVIF format and enable lazy loading.",
          improvement: "Load time reduced from 3.2s to 600ms (80% faster)"
        },
        {
          severity: "WARNING",
          confidence: "88%",
          title: "Render-blocking Third Party Stylesheets",
          rootCause: "Google Fonts and FontAwesome CSS files are loaded synchronously in the document head.",
          evidence: "First Contentful Paint is blocked for 450ms while stylesheet assets download.",
          fix: "Use preconnect headers for Google Fonts and defer loading of non-critical icons styles.",
          improvement: "Speed Index improves by 350ms"
        }
      ],
      recommendations: [
        { title: "Compress Banner Images", desc: "Convert static banner PNGs to optimized WebP format.", time: "Save 2.3s", diff: "Easy", prio: "High", imp: "80%" },
        { title: "Defer Third-Party Icons CSS", desc: "Load FontAwesome asynchronously after DOM content is ready.", time: "Save 0.4s", diff: "Easy", prio: "Medium", imp: "15%" },
        { title: "Enable Brotli Compression", desc: "Configure server to serve assets using Brotli compression.", time: "Save 0.5s", diff: "Medium", prio: "Medium", imp: "30%" }
      ],
      topApis: [
        { path: "GET /api/v1/portfolio/projects", time: "820ms" },
        { path: "POST /api/v1/contact/submit", time: "410ms" },
        { path: "GET /api/v1/feedback/reviews", time: "280ms" }
      ],
      resources: {
        images: "4.8 MB (31 requests)",
        js: "820 KB (8 requests)",
        css: "240 KB (4 requests)",
        fonts: "180 KB (3 requests)",
        thirdparty: "120 KB (2 requests)",
        pie: "conic-gradient(#FD6101 0% 80%, #a855f7 80% 90%, #3b82f6 90% 95%, #e2e8f0 95% 100%)",
        pieLabel: "Images (80%), JS (10%), CSS (5%), Others (5%)"
      }
    };
  } else if (category === "ecommerce") {
    return {
      screens: [
        { id: "home", name: "1. Storefront Home", time: "1.9s", ttfb: "160ms", fcp: "0.8s", lcp: "1.7s", apis: 14, status: "Good", rating: "91/100", icon: "🛒", size: "1.5 MB", details: "E-Commerce homepage storefront catalog" },
        { id: "catalog", name: "2. Product Search", time: "2.8s", ttfb: "240ms", fcp: "1.2s", lcp: "2.4s", apis: 36, status: "Warning", rating: "82/100", icon: "🔍", size: "2.1 MB", details: "Dynamic query products search grid" },
        { id: "details", name: "3. Product Details", time: "2.2s", ttfb: "190ms", fcp: "0.9s", lcp: "1.9s", apis: 18, status: "Good", rating: "90/100", icon: "🏷️", size: "1.1 MB", details: "Specific product SKU reviews and details" },
        { id: "cart", name: "4. Shopping Cart", time: "1.5s", ttfb: "120ms", fcp: "0.6s", lcp: "1.3s", apis: 11, status: "Excellent", rating: "97/100", icon: "🛍️", size: "640 KB", details: "Items checkout shopping cart summary" },
        { id: "checkout", name: "5. Payment Gateway", time: "4.8s", ttfb: "820ms", fcp: "1.9s", lcp: "4.1s", apis: 42, status: "Critical", rating: "55/100", icon: "💳", size: "3.2 MB", details: "Stripe payment authentication processing" }
      ],
      apis: [
        "/api/v1/products/featured",
        "/api/v1/products/search?q=shoes",
        "/api/v1/products/sku-92842",
        "/api/v1/cart/items",
        "/api/v1/checkout/charge"
      ],
      bottlenecks: [
        {
          severity: "CRITICAL",
          confidence: "95%",
          title: "Slow Payment Endpoint Handshake (POST /api/v1/checkout/charge)",
          rootCause: "Synchronous verification loop with third-party payment provider and database locking.",
          evidence: "Database connection pool waits for 3.4 seconds during transaction processing.",
          fix: "Implement asynchronous queueing or optimize webhook execution handlers.",
          improvement: "Latency reduced from 4.8s to 900ms (81% faster)"
        },
        {
          severity: "WARNING",
          confidence: "90%",
          title: "Missing DB Index on Products Search",
          rootCause: "Full-text scan on product catalogs instead of optimized index querying.",
          evidence: "GET /api/v1/products/search takes 2.4 seconds under 10 concurrent requests.",
          fix: "Apply composited index: CREATE INDEX idx_prod_search ON products(name, category_id, price).",
          improvement: "Query load time reduced by 1.6s"
        }
      ],
      recommendations: [
        { title: "Optimize Cart Database", desc: "Index items lookup queries inside the shopping cart tables.", time: "Save 1.2s", diff: "Easy", prio: "High", imp: "70%" },
        { title: "Enable Redis Caching", desc: "Cache catalog query results to offload repeated database reads.", time: "Save 1.6s", diff: "Hard", prio: "High", imp: "80%" },
        { title: "Add CDN Edge Nodes", desc: "Cache static product image variants closer to global visitors.", time: "Save 0.9s", diff: "Medium", prio: "High", imp: "60%" }
      ],
      topApis: [
        { path: "POST /api/v1/checkout/charge", time: "3400ms" },
        { path: "GET /api/v1/products/search", time: "1820ms" },
        { path: "GET /api/v1/products/sku-92842", time: "720ms" }
      ],
      resources: {
        images: "3.6 MB (48 requests)",
        js: "1.6 MB (22 requests)",
        css: "380 KB (6 requests)",
        fonts: "120 KB (2 requests)",
        thirdparty: "420 KB (9 requests)",
        pie: "conic-gradient(#FD6101 0% 60%, #a855f7 60% 85%, #3b82f6 85% 95%, #e2e8f0 95% 100%)",
        pieLabel: "Images (60%), JS (25%), CSS (10%), Others (5%)"
      }
    };
  } else if (category === "saas") {
    return {
      screens: [
        { id: "login", name: "1. Login Page", time: "1.4s", ttfb: "120ms", fcp: "0.5s", lcp: "1.2s", apis: 12, status: "Excellent", rating: "98/100", icon: "🔑", size: "320 KB", details: "Enterprise sign-in security page" },
        { id: "dashboard", name: "2. Dashboard", time: "2.1s", ttfb: "180ms", fcp: "0.9s", lcp: "1.8s", apis: 28, status: "Good", rating: "92/100", icon: "📊", size: "1.2 MB", details: "Main charts analytics dashboard" },
        { id: "students", name: "3. Students List", time: "3.6s", ttfb: "340ms", fcp: "1.6s", lcp: "3.2s", apis: 45, status: "Warning", rating: "74/100", icon: "👥", size: "2.4 MB", details: "Paginated lists database query view" },
        { id: "create_stud", name: "4. Create Student", time: "1.8s", ttfb: "130ms", fcp: "0.7s", lcp: "1.5s", apis: 8, status: "Excellent", rating: "96/100", icon: "📝", size: "480 KB", details: "Insert forms dialog layout validation" },
        { id: "details", name: "5. Student Details", time: "2.9s", ttfb: "210ms", fcp: "1.1s", lcp: "2.4s", apis: 22, status: "Good", rating: "89/100", icon: "📇", size: "980 KB", details: "Profile files catalog records" },
        { id: "reports", name: "6. Reports", time: "6.9s", ttfb: "1.4s", fcp: "2.8s", lcp: "6.1s", apis: 84, status: "Critical", rating: "42/100", icon: "📈", size: "5.8 MB", details: "Complex reporting database aggregation" },
        { id: "settings", name: "7. Settings", time: "1.6s", ttfb: "110ms", fcp: "0.6s", lcp: "1.3s", apis: 5, status: "Excellent", rating: "97/100", icon: "⚙️", size: "290 KB", details: "Global variables config manager" },
        { id: "logout", name: "8. Logout Page", time: "0.8s", ttfb: "80ms", fcp: "0.3s", lcp: "0.7s", apis: 2, status: "Excellent", rating: "99/100", icon: "🚪", size: "120 KB", details: "Clear session cookies redirect" }
      ],
      apis: [
        "/api/v1/auth/token",
        "/api/v1/user/profile",
        "/api/v1/students/list",
        "/api/v1/students/create",
        "/api/v1/students/29482",
        "/api/v1/reports/finance",
        "/api/v1/settings/config",
        "/api/v1/auth/logout"
      ],
      bottlenecks: [
        {
          severity: "CRITICAL",
          confidence: "98%",
          title: "Slow Database Query Block (GET /api/v1/reports/finance)",
          rootCause: "Lack of composited index on finance_records(student_id, date, campus_id) during multi-table joins.",
          evidence: "Database thread pool execution locked for 4,120ms.",
          fix: "CREATE INDEX idx_fin_composite ON finance_records(student_id, date, campus_id);",
          improvement: "Latency reduced from 4.3s to 120ms (97% faster)"
        },
        {
          severity: "WARNING",
          confidence: "85%",
          title: "Uncompressed High-Resolution Image Assets",
          rootCause: "Banner images on Students page are using raw PNG file structures.",
          evidence: "Assets payload total size: 1.2 MB.",
          fix: "Convert image assets to WebP or AVIF compression standard.",
          improvement: "Page payload reduced by 920 KB. FCP speed index will improve by 1.4s."
        }
      ],
      recommendations: [
        { title: "Optimize SQL Queries", desc: "Add composite index to finance_records table to prevent joins lock.", time: "Save 4.1s", diff: "Easy", prio: "High", imp: "97%" },
        { title: "Compress Banner Images", desc: "Convert static banner PNGs to optimized WebP format.", time: "Save 1.4s", diff: "Easy", prio: "High", imp: "70%" },
        { title: "Enable Brotli Compression", desc: "Configure server to serve text assets (JS, CSS) via Brotli.", time: "Save 0.8s", diff: "Medium", prio: "Medium", imp: "45%" }
      ],
      topApis: [
        { path: "GET /api/v1/reports/finance", time: "4120ms" },
        { path: "GET /api/v1/students/list", time: "1820ms" },
        { path: "POST /api/v1/students/create", time: "980ms" }
      ],
      resources: {
        images: "2.4 MB (42 requests)",
        js: "1.8 MB (15 requests)",
        css: "410 KB (8 requests)",
        fonts: "180 KB (4 requests)",
        thirdparty: "320 KB (6 requests)",
        pie: "conic-gradient(#FD6101 0% 50%, #a855f7 50% 80%, #3b82f6 80% 95%, #e2e8f0 95% 100%)",
        pieLabel: "Images (50%), JS (30%), CSS (15%), Others (5%)"
      }
    };
  } else if (category === "Qintra") {
    return {
      screens: [
        { id: "login", name: "1. Login Portal", time: "1.2s", ttfb: "100ms", fcp: "0.5s", lcp: "1.1s", apis: 4, status: "Excellent", rating: "98/100", icon: "🔐", size: "280 KB", details: "Qintra platform gateway authentication" },
        { id: "dashboard", name: "2. Dashboard", time: "1.8s", ttfb: "140ms", fcp: "0.7s", lcp: "1.5s", apis: 12, status: "Good", rating: "95/100", icon: "📊", size: "950 KB", details: "Analytics executive metrics dashboard" },
        { id: "responsive", name: "3. Responsive Simulator", time: "2.2s", ttfb: "190ms", fcp: "0.8s", lcp: "1.9s", apis: 6, status: "Good", rating: "92/100", icon: "📱", size: "1.2 MB", details: "Multi-viewport device testing sandbox" },
        { id: "record", name: "4. Record from Screen", time: "2.8s", ttfb: "240ms", fcp: "1.2s", lcp: "2.5s", apis: 8, status: "Good", rating: "90/100", icon: "🔴", size: "1.8 MB", details: "Interactive custom user flow recorder" },
        { id: "fromurl", name: "5. From URL Scan", time: "1.5s", ttfb: "120ms", fcp: "0.6s", lcp: "1.3s", apis: 5, status: "Excellent", rating: "96/100", icon: "🌐", size: "540 KB", details: "Single input URL audit execution path" },
        { id: "screenshots", name: "6. From Screenshots", time: "3.2s", ttfb: "310ms", fcp: "1.4s", lcp: "2.9s", apis: 16, status: "Warning", rating: "78/100", icon: "🖼️", size: "4.8 MB", details: "Visual regression vision analysis" },
        { id: "prompt", name: "7. From Prompt (AI)", time: "2.4s", ttfb: "210ms", fcp: "0.9s", lcp: "2.1s", apis: 10, status: "Good", rating: "91/100", icon: "☀️", size: "1.6 MB", details: "Generative AI test script constructor" },
        { id: "lowcode", name: "8. Low-Code Builder", time: "3.5s", ttfb: "320ms", fcp: "1.5s", lcp: "3.2s", apis: 22, status: "Warning", rating: "75/100", icon: "✏️", size: "3.4 MB", details: "Low-code test case visual designer" },
        { id: "suites", name: "9. Test Suites", time: "1.7s", ttfb: "130ms", fcp: "0.7s", lcp: "1.5s", apis: 8, status: "Excellent", rating: "95/100", icon: "📁", size: "490 KB", details: "Test suites organization panels" },
        { id: "runs", name: "10. Test Runs", time: "2.1s", ttfb: "180ms", fcp: "0.9s", lcp: "1.8s", apis: 14, status: "Good", rating: "92/100", icon: "▶️", size: "1.1 MB", details: "Active pipeline execution records" },
        { id: "perfeng", name: "11. Performance Engineering", time: "2.6s", ttfb: "220ms", fcp: "1.1s", lcp: "2.4s", apis: 18, status: "Good", rating: "89/100", icon: "🚀", size: "1.3 MB", details: "Enterprise performance test flows" },
        { id: "loadtesting", name: "12. Load Testing", time: "3.1s", ttfb: "290ms", fcp: "1.3s", lcp: "2.8s", apis: 25, status: "Warning", rating: "81/100", icon: "⚡", size: "2.7 MB", details: "High concurrency JMeter scenario models" },
        { id: "perftesting", name: "13. Performance Testing", time: "2.3s", ttfb: "190ms", fcp: "0.8s", lcp: "2.0s", apis: 12, status: "Good", rating: "90/100", icon: "⏱️", size: "1.4 MB", details: "General transaction latencies charts" },
        { id: "automated", name: "14. Automated Test Cases", time: "1.9s", ttfb: "150ms", fcp: "0.7s", lcp: "1.7s", apis: 9, status: "Excellent", rating: "94/100", icon: "📝", size: "620 KB", details: "Automated scripts metadata catalog" },
        { id: "summary", name: "15. Result Summary", time: "3.8s", ttfb: "410ms", fcp: "1.8s", lcp: "3.5s", apis: 32, status: "Warning", rating: "72/100", icon: "📊", size: "3.1 MB", details: "Historical pipelines aggregations grid" },
        { id: "kb", name: "16. Knowledge Base (PKB)", time: "1.4s", ttfb: "110ms", fcp: "0.5s", lcp: "1.2s", apis: 6, status: "Excellent", rating: "97/100", icon: "📚", size: "480 KB", details: "Shared repository documentation files" },
        { id: "manual", name: "17. Manual Cases", time: "1.6s", ttfb: "120ms", fcp: "0.6s", lcp: "1.4s", apis: 5, status: "Excellent", rating: "96/100", icon: "📋", size: "390 KB", details: "Manual script scenario descriptors" },
        { id: "testdata", name: "18. Test Data", time: "2.0s", ttfb: "170ms", fcp: "0.8s", lcp: "1.8s", apis: 11, status: "Good", rating: "91/100", icon: "🗄️", size: "840 KB", details: "Configured parameter files database" },
        { id: "bugreports", name: "19. Bug Reports", time: "3.4s", ttfb: "360ms", fcp: "1.5s", lcp: "3.0s", apis: 20, status: "Warning", rating: "76/100", icon: "💵", size: "2.2 MB", details: "Visual discrepancies and defects log" },
        { id: "reports", name: "20. Reports", time: "4.5s", ttfb: "520ms", fcp: "1.9s", lcp: "4.1s", apis: 42, status: "Critical", rating: "48/100", icon: "📄", size: "5.1 MB", details: "Aggregated pipeline test metrics reports" },
        { id: "insights", name: "21. Insights", time: "2.5s", ttfb: "210ms", fcp: "1.0s", lcp: "2.2s", apis: 15, status: "Good", rating: "90/100", icon: "📊", size: "1.5 MB", details: "AI-driven test recommendations & trends" },
        { id: "team", name: "22. Team Workspace", time: "1.3s", ttfb: "110ms", fcp: "0.5s", lcp: "1.1s", apis: 6, status: "Excellent", rating: "97/100", icon: "👥", size: "320 KB", details: "Member roles & project access permissions" },
        { id: "integrations", name: "23. Integrations", time: "1.5s", ttfb: "130ms", fcp: "0.6s", lcp: "1.3s", apis: 8, status: "Excellent", rating: "96/100", icon: "🔌", size: "450 KB", details: "CI/CD & Slack/Jira third-party webhooks" },
        { id: "help", name: "24. Help & Resources", time: "1.0s", ttfb: "80ms", fcp: "0.4s", lcp: "0.9s", apis: 3, status: "Excellent", rating: "99/100", icon: "❓", size: "150 KB", details: "Documentation links & chat support helper" }
      ],
      apis: [
        "/api/v1/auth/session",
        "/api/v1/projects/summary",
        "/api/v1/responsive/viewport",
        "/api/v1/recorder/session",
        "/api/v1/scanner/url",
        "/api/v1/vision/compare",
        "/api/v1/openai/prompt",
        "/api/v1/builder/elements",
        "/api/v1/suites/list",
        "/api/v1/runs/list",
        "/api/v1/performance/profiles",
        "/api/v1/loadtest/k6",
        "/api/v1/perftest/profiles",
        "/api/v1/cases/automated",
        "/api/v1/analytics/results",
        "/api/v1/kb/documents",
        "/api/v1/cases/manual",
        "/api/v1/testdata/parameters",
        "/api/v1/bugs/visual",
        "/api/v1/reports/pdf",
        "/api/v1/analytics/insights",
        "/api/v1/team/members",
        "/api/v1/webhooks/list",
        "/api/v1/help/resources"
      ],
      bottlenecks: [
        {
          severity: "CRITICAL",
          confidence: "98%",
          title: "Slow Builder Payload Processing (POST /api/v1/builder/elements)",
          rootCause: "Parsing complex low-code elements tree sequentially in JSON format instead of differential increments.",
          evidence: "Low-code workspace thread execution blocked for 1,420ms on project save actions.",
          fix: "Apply a patch-based sync mechanism for visual flow element definitions.",
          improvement: "Builder workspace latency reduced to 140ms (90% faster)"
        },
        {
          severity: "WARNING",
          confidence: "90%",
          title: "Unoptimized Analytics Payload size (GET /api/v1/analytics/results)",
          rootCause: "Endpoint returns full trace logs of the past 30 days instead of pre-aggregated summaries.",
          evidence: "Returned JSON response size is 3.4 MB, causing FCP delay on client screens.",
          fix: "Utilize database-level group aggregations and caching for historical test run telemetry.",
          improvement: "Reduced payload to 120 KB, saving 2.3 seconds index paint time."
        }
      ],
      recommendations: [
        { title: "Optimize Builder Sync API", desc: "Change low-code element tree saving to a partial patch endpoint.", time: "Save 2.1s", diff: "Easy", prio: "High", imp: "90%" },
        { title: "Pre-aggregate Run Logs", desc: "Add server-side analytics log indexing cache tables.", time: "Save 2.3s", diff: "Medium", prio: "High", imp: "80%" },
        { title: "Defer Monaco Code Editor", desc: "Lazy load raw text editor scripts until elements panel is activated.", time: "Save 1.2s", diff: "Medium", prio: "Medium", imp: "50%" }
      ],
      topApis: [
        { path: "POST /api/v1/builder/elements", time: "3120ms" },
        { path: "GET /api/v1/analytics/results", time: "2400ms" },
        { path: "GET /api/v1/runs/list", time: "850ms" }
      ],
      resources: {
        images: "1.1 MB (18 requests)",
        js: "3.4 MB (28 requests)",
        css: "450 KB (9 requests)",
        fonts: "180 KB (2 requests)",
        thirdparty: "820 KB (12 requests)",
        pie: "conic-gradient(#FD6101 0% 60%, #a855f7 60% 80%, #3b82f6 80% 90%, #e2e8f0 90% 100%)",
        pieLabel: "JavaScript (60%), Images (20%), CSS (10%), Others (10%)"
      }
    };
  } else {
    // generic
    return {
      screens: [
        { id: "landing", name: `1. ${capitalizedSite} Home`, time: "1.5s", ttfb: "110ms", fcp: "0.6s", lcp: "1.3s", apis: 8, status: "Excellent", rating: "96/100", icon: "🌐", size: "540 KB", details: `Landing index page for ${hostname}` },
        { id: "dashboard", name: `2. ${capitalizedSite} Console`, time: "2.5s", ttfb: "210ms", fcp: "1.1s", lcp: "2.2s", apis: 24, status: "Good", rating: "88/100", icon: "📊", size: "1.4 MB", details: `Core administrative console for ${siteName}` },
        { id: "data", name: `3. ${capitalizedSite} Search`, time: "4.2s", ttfb: "510ms", fcp: "1.8s", lcp: "3.9s", apis: 52, status: "Warning", rating: "72/100", icon: "🔍", size: "2.9 MB", details: `${capitalizedSite} query records search list` },
        { id: "settings", name: `4. ${capitalizedSite} Settings`, time: "1.4s", ttfb: "90ms", fcp: "0.5s", lcp: "1.2s", apis: 4, status: "Excellent", rating: "97/100", icon: "⚙️", size: "310 KB", details: `User accounts settings parameters for ${siteName}` }
      ],
      apis: [
        `/api/v1/${siteName}/init`,
        `/api/v1/${siteName}/auth/session`,
        `/api/v1/${siteName}/query/records`,
        `/api/v1/${siteName}/settings/save`
      ],
      bottlenecks: [
        {
          severity: "CRITICAL",
          confidence: "90%",
          title: `Slow Dynamic Records Aggregation (GET /api/v1/${siteName}/query/records)`,
          rootCause: `Wildcard string matching on unindexed table columns inside the ${siteName} backend database.`,
          evidence: "SQL query execution blocked client rendering thread for 3,950ms.",
          fix: `Create database indexes on targeted columns or add a redis cache layer for common searches.`,
          improvement: "Query load time reduced from 4.2s to 450ms (89% faster)"
        }
      ],
      recommendations: [
        { title: `Index ${capitalizedSite} Database`, desc: "Add composite index on records query tables to prevent locks.", time: "Save 3.1s", diff: "Medium", prio: "High", imp: "80%" },
        { title: "Enable Brotli Compression", desc: "Compress all static files served by the site server.", time: "Save 0.6s", diff: "Easy", prio: "Medium", imp: "30%" }
      ],
      topApis: [
        { path: `GET /api/v1/${siteName}/query/records`, time: "3950ms" },
        { path: `GET /api/v1/${siteName}/auth/session`, time: "320ms" }
      ],
      resources: {
        images: "1.2 MB (24 requests)",
        js: "1.4 MB (12 requests)",
        css: "220 KB (4 requests)",
        fonts: "110 KB (2 requests)",
        thirdparty: "80 KB (1 requests)",
        pie: "conic-gradient(#FD6101 0% 45%, #a855f7 45% 85%, #3b82f6 85% 95%, #e2e8f0 95% 100%)",
        pieLabel: "Images (45%), JS (40%), CSS (10%), Others (5%)"
      }
    };
  }
}

export default function PerformanceEngineering({ subPage: propSubPage = "dashboard", setSubPage: propSetSubPage, initialTab }) {
  const [internalSubPage, setInternalSubPage] = useState(initialTab === "spike" || initialTab === "soak" || initialTab === "stress" ? "create" : propSubPage);

  const subPage = propSetSubPage ? propSubPage : internalSubPage;
  const setSubPage = propSetSubPage ? propSetSubPage : setInternalSubPage;

  // Creation Mode & Wizard States
  const [creationMode, setCreationMode] = useState(initialTab === "spike" ? "single" : "journey"); // "journey" | "single"
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedTestType, setSelectedTestType] = useState(initialTab || "stress");

  useEffect(() => {
    if (initialTab) {
      setSelectedTestType(initialTab);
      if (initialTab === "spike") {
        setCreationMode("single");
        setInternalSubPage("create");
      } else if (initialTab === "soak" || initialTab === "stress") {
        setInternalSubPage("create");
      }
    }
  }, [initialTab]);

  // User Journey Wizard Step
  const [journeyStep, setJourneyStep] = useState(1);
  const [selectedJourneyType, setSelectedJourneyType] = useState("auto"); // "auto" | "recorded" | "manual"
  const [selectedReportTab, setSelectedReportTab] = useState("screens"); // overview, screens, apis, resources, bottlenecks, recommendations
  const [expandedAuditCategory, setExpandedAuditCategory] = useState(null);
  // Stress Testing Module States (7 Steps)
  // eslint-disable-next-line no-unused-vars
  const [stressStep, setStressStep] = useState(1);
  // eslint-disable-next-line no-unused-vars
  const [stressForm, setStressForm] = useState({
    name: "Education CRM Stress Test",
    application: "Education CRM",
    environment: "QA",
    url: "https://crm.Qintra.com",
    scope: "Complete User Journey",
    normalUsers: 100,
    testUsers: 5000,
    rampType: "Slowly (Recommended)",
    goal: "Find where the application starts slowing down"
  });
  // eslint-disable-next-line no-unused-vars
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [advancedSettings, setAdvancedSettings] = useState({
    initialUsers: 50,
    userIncrement: 250,
    rampUpInterval: "30s",
    stopConditions: "Error rate > 5%",
    slaThreshold: "3000ms",
    thinkTime: "2s",
    loadGenerators: "2 Distributed Nodes (US East)",
    browser: "Chrome",
    network: "WiFi",
    monitoring: "CPU, Memory, DB Pool",
    envVars: "NODE_ENV=qa",
    customPattern: "Linear Ramp",
    recoveryRules: "Verify 200 OK within 60s"
  });
  // eslint-disable-next-line no-unused-vars
  const [stressLiveUsers, setStressLiveUsers] = useState(3820);
  // eslint-disable-next-line no-unused-vars
  const [stressIsRunning, setStressIsRunning] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [stressElapsed, setStressElapsed] = useState(0);

  const [spikeMode, setSpikeMode] = useState("quick"); // "quick" | "advanced"
  const [quickStep, setQuickStep] = useState(1);
  const [quickForm, setQuickForm] = useState({
    name: "Login Spike Test",
    url: "https://cbs.crmfarm.app/role-dashboard",
    username: "",
    password: "",
    scope: "Complete User Journey",
    normalUsers: 100,
    spikeUsers: 2000,
    duration: "2 Minutes",
    repeats: 3,
    goal: "Check if the application slows down"
  });

  const handleQuickUrlChange = (inputVal) => {
    let user = quickForm.username;
    let pass = quickForm.password;
    let cleanUrl = inputVal;

    if (inputVal.includes("@")) {
      const match = inputVal.match(/^(https?:\/\/)([^:@]+):([^@]+)@(.+)$/i);
      if (match) {
        user = decodeURIComponent(match[2]);
        pass = decodeURIComponent(match[3]);
        cleanUrl = `${match[1]}${match[4]}`;
      }
    } else if (inputVal.includes("?")) {
      try {
        const urlObj = new URL(inputVal.startsWith("http") ? inputVal : `https://${inputVal}`);
        const u = urlObj.searchParams.get("username") || urlObj.searchParams.get("user") || urlObj.searchParams.get("login");
        const p = urlObj.searchParams.get("password") || urlObj.searchParams.get("pass");
        if (u && p) {
          user = u;
          pass = p;
        }
      } catch (e) { }
    }

    setQuickForm(prev => ({
      ...prev,
      url: cleanUrl,
      username: user,
      password: pass
    }));
  };

  const runQuickSpikeTest = () => {
    setQuickStep(4);
    setSpikeIsRunning(true);
    if (quickForm.url) {
      axios.post(`${API_BASE}/api/journey/discover`, {
        url: quickForm.url,
        username: quickForm.username,
        password: quickForm.password
      })
        .then(res => {
          if (res.data && res.data.screens && res.data.screens.length > 0) {
            setJourneyData(res.data);
          }
        })
        .catch(err => {
          console.warn("Spike test discovery warning, using dynamic URL analysis:", err);
        });
    }
  };

  // Spike Testing Wizard State (11 Steps)
  const [spikeStep, setSpikeStep] = useState(1);
  const [spikeForm, setSpikeForm] = useState({
    name: "Qintra Core Ingestion Spike",
    description: "Stress-testing the messaging bus and ingestion API routes under sudden traffic bursts.",
    project: "Qintra Platform",
    application: "FastAPI Core Engine",
    environment: "Staging",
    baseUrl: "http://localhost:3000",
    authType: "Bearer Token",
    username: "superadmin@qintra.ai",
    password: "SuperAdmin@123",
    browser: "Chrome",
    device: "Desktop",
    networkProfile: "WiFi",
    location: "India",
    tags: "spike, ingestion, backend"
  });
  const [selectedScenario, setSelectedScenario] = useState("Complete User Journey");
  const [spikePattern, setSpikePattern] = useState({
    normalUsers: 100,
    spikeUsers: 2000,
    rampUp: 30,
    hold: 120,
    rampDown: 30,
    recovery: 60,
    cycles: 5,
    thinkTime: 2,
    duration: 600
  });
  const [envConfig, setEnvConfig] = useState({
    infra: "Kubernetes",
    browser: "Chrome",
    network: "5G",
    region: "India",
    monitoring: ["CPU", "Memory", "Database", "API"]
  });

  // Execution & Live Sim States
  const [spikeIsRunning, setSpikeIsRunning] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [spikeElapsed, setSpikeElapsed] = useState(0);
  const [spikeCurrentUsers, setSpikeCurrentUsers] = useState(100);
  const [spikeStage, setSpikeStage] = useState("Normal Users"); // Normal Users -> Spike -> Hold -> Ramp Down -> Recovery -> Repeat
  const [spikeCycle, setSpikeCycle] = useState(1);
  const [spikeLogs, setSpikeLogs] = useState([]);

  // User Journey Configuration Form
  const [journeyForm, setJourneyForm] = useState({
    url: typeof window !== "undefined" && window.location ? window.location.origin + (window.location.pathname || "") + (window.location.hash || "") : "http://localhost:3000/#/dashboard",
    authType: "None",
    username: "",
    password: "",
    environment: "Production",
    browser: "Chrome",
    device: "Desktop",
    location: "US",
    network: "WiFi",
    runMode: "Analyze Once"
  });

  // Discovery progress states (Step 3 simulation)
  const [discoveryLog, setDiscoveryLog] = useState([]);
  const [discoveryPercent, setDiscoveryPercent] = useState(0);
  const [isDiscovering, setIsDiscovering] = useState(false);

  // Active run execution states (Step 5 simulation)
  const [runElapsed, setRunElapsed] = useState(0);
  const [runPercent, setRunPercent] = useState(0);
  const [activeScreenIndex, setActiveScreenIndex] = useState(0);
  const [isRunningAnalysis, setIsRunningAnalysis] = useState(false);
  const [expandedScreen, setExpandedScreen] = useState(null); // Step 7 table row expand

  // API_BASE imported from ../config/apiConfig

  const [previousReports, setPreviousReports] = useState([]);
  const [isLoadingReports, setIsLoadingReports] = useState(false);

  useEffect(() => {
    if (journeyStep === 7) {
      setIsLoadingReports(true);
      axios.get(`${API_BASE}/api/performance/reports`)
        .then(res => {
          if (res.data && res.data.reports) {
            setPreviousReports(res.data.reports);
          }
          setIsLoadingReports(false);
        })
        .catch(err => {
          console.error("Failed to load saved reports:", err);
          setIsLoadingReports(false);
        });
    }
  }, [journeyStep]);

  const [journeyData, setJourneyData] = useState(() => getDynamicJourneyData(quickForm.url || journeyForm.url));
  const [selectedMapNode, setSelectedMapNode] = useState(null);

  useEffect(() => {
    const targetUrl = spikeMode === "quick" ? (quickForm.url || journeyForm.url) : journeyForm.url;
    if (targetUrl) {
      setJourneyData(getDynamicJourneyData(targetUrl));
    }
  }, [journeyForm.url, quickForm.url, spikeMode]);

  useEffect(() => {
    let interval;
    if (spikeIsRunning) {
      interval = setInterval(() => {
        setSpikeElapsed(prev => {
          const nextSec = prev + 1;

          if (spikeMode === "quick") {
            // Quick Mode Simulator: 10 seconds total duration
            if (nextSec > 10) {
              clearInterval(interval);
              setSpikeIsRunning(false);
              setQuickStep(5); // Advance to Quick Mode AI Report
              return 0;
            }

            if (nextSec < 3) {
              setSpikeStage("Normal Traffic");
              setSpikeCurrentUsers(Number(quickForm.normalUsers));
            } else if (nextSec >= 3 && nextSec < 6) {
              setSpikeStage("Traffic Spike");
              setSpikeCurrentUsers(Number(quickForm.spikeUsers));
            } else if (nextSec >= 6 && nextSec < 9) {
              setSpikeStage("Maintaining Peak Traffic");
              setSpikeCurrentUsers(Number(quickForm.spikeUsers));
            } else {
              setSpikeStage("Recovery Check");
              setSpikeCurrentUsers(Number(quickForm.normalUsers));
            }

            return nextSec;
          } else {
            // Advanced Mode Simulator
            const loopTime = nextSec % 15;
            const currentCycle = Math.floor(nextSec / 15) + 1;

            if (currentCycle > spikePattern.cycles) {
              clearInterval(interval);
              setSpikeIsRunning(false);
              setSpikeStep(10); // Transition to recovery validation
              return 0;
            }

            setSpikeCycle(currentCycle);

            let stageName = "Normal Users";
            let currentU = Number(spikePattern.normalUsers);

            if (loopTime >= 0 && loopTime < 2) {
              stageName = "Normal Users";
              currentU = Number(spikePattern.normalUsers);
            } else if (loopTime >= 2 && loopTime < 5) {
              stageName = "Spike";
              const ratio = (loopTime - 2) / 3;
              currentU = Math.floor(Number(spikePattern.normalUsers) + ratio * (Number(spikePattern.spikeUsers) - Number(spikePattern.normalUsers)));
            } else if (loopTime >= 5 && loopTime < 9) {
              stageName = "Hold";
              currentU = Number(spikePattern.spikeUsers);
            } else if (loopTime >= 9 && loopTime < 11) {
              stageName = "Ramp Down";
              const ratio = (loopTime - 9) / 2;
              currentU = Math.floor(Number(spikePattern.spikeUsers) - ratio * (Number(spikePattern.spikeUsers) - Number(spikePattern.normalUsers)));
            } else {
              stageName = "Recovery";
              currentU = Number(spikePattern.normalUsers);
            }

            setSpikeStage(stageName);
            setSpikeCurrentUsers(currentU);

            setSpikeLogs(logs => {
              const newLog = `[${new Date().toLocaleTimeString()}] [Cycle ${currentCycle}] Stage: ${stageName} | Users: ${currentU} | Throughput: ${Math.floor(currentU * 12.5)} req/s | Errors: ${stageName === "Spike" || stageName === "Hold" ? "0.4%" : "0.0%"}`;
              return [...logs, newLog].slice(-10);
            });

            return nextSec;
          }
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [spikeIsRunning, spikeMode, quickForm.normalUsers, quickForm.spikeUsers, spikePattern.cycles, spikePattern.normalUsers, spikePattern.spikeUsers]);

  // Dynamic form configs
  // eslint-disable-next-line no-unused-vars
  const [form, setForm] = useState({
    name: "Enterprise Benchmark Flow",
    application: "CRM Platform",
    environment: "Staging",
    url: "https://api.crm.platform/v1",
    protocol: "HTTPS",
    vus: 100,
    concurrent: 50,
    rampup: 30,
    rampdown: 10,
    duration: 300,
    thinkTime: 2,
    arrivalRate: 100,
    headers: '{"Content-Type": "application/json"}',
    auth: "Bearer Token",
    assertions: "Status Code = 200",
    testData: "users.csv",

    // Stress Test specific
    initialUsers: 10,
    maxUsers: 5000,
    userIncrement: 50,
    threshold: 1500,
    breakingPoint: 2000,
    failureCondition: "Error Rate > 5%",
    recoveryValidation: true,

    // Spike Test specific
    normalUsers: 100,
    spikeUsers: 3000,
    spikeDuration: 60,
    recoveryTime: 120,
    repeatCount: 3,

    // Soak Test specific
    longDuration: 14400,
    stableUsers: 200,
    memoryMonitoring: true,
    databaseMonitoring: true,
    leakDetection: true,

    // Volume Test specific
    dataset: "large_payload.json",
    numRecords: 100000,
    importSource: "PostgreSQL DB",
    dbValidation: true,
    processingThreshold: 5000,

    // Scalability Test specific
    startingUsers: 10,
    scalingStrategy: "Linear Step",
    infrastructureMetrics: "CPU, Memory, Disk, Network"
  });

  // Scenario state
  const [scenarios] = useState([
    { id: 1, name: "CRM E2E Checkout Flow", type: "stress", author: "AI Assistant", version: "v1.2", updated: "2 hours ago" },
    { id: 2, name: "Peak Stress Breakpoint", type: "stress", author: "ManiR", version: "v2.0", updated: "Yesterday" },
    { id: 3, name: "Database Load Soak Test", type: "soak", author: "Admin", version: "v1.0", updated: "3 days ago" }
  ]);

  // Filters
  const [resultsFilter, setResultsFilter] = useState("all");
  const [reportFilter, setReportFilter] = useState("stress");

  // Execution status
  const [execStatus, setExecStatus] = useState("idle"); // idle, running, paused, stopped
  const [execMetrics, setExecMetrics] = useState({
    activeVus: 0,
    responseTime: 0,
    throughput: 0,
    errorRate: 0,
    cpu: 12,
    memory: 45,
    network: 15,
    logs: []
  });

  // Simulation of Live execution with 15 seconds auto-completion
  useEffect(() => {
    let interval;
    let elapsed = 0;
    if (execStatus === "running") {
      interval = setInterval(() => {
        elapsed += 1;
        if (elapsed >= 15) {
          clearInterval(interval);
          setExecStatus("stopped");
          return;
        }
        setExecMetrics(prev => {
          const isError = Math.random() > 0.95;
          const latency = Math.floor(200 + Math.random() * 300);
          const rps = Math.floor(150 + Math.random() * 50);
          const newLog = `[${new Date().toLocaleTimeString()}] HTTP GET /api/v1/leads - Status: 200 - Time: ${latency}ms`;

          return {
            ...prev,
            activeVus: Math.min(prev.activeVus + 5, form.vus || 100),
            responseTime: latency,
            throughput: rps,
            errorRate: isError ? Math.floor(Math.random() * 4) : prev.errorRate,
            cpu: Math.min(Math.floor(20 + Math.random() * 40), 95),
            memory: Math.min(Math.floor(45 + Math.random() * 5), 80),
            network: Math.min(Math.floor(15 + Math.random() * 30), 100),
            logs: [newLog, ...prev.logs.slice(0, 20)]
          };
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [execStatus, form.vus]);

  // Simulation for Step 3: Journey Discovery Progress
  useEffect(() => {
    let interval;
    if (isDiscovering) {
      setDiscoveryPercent(0);
      setDiscoveryLog(["[System] Starting headless chromium browser instances..."]);

      // Start actual discovery process on the backend
      axios.post(`${API_BASE}/api/journey/discover`, {
        url: journeyForm.url,
        username: journeyForm.username,
        password: journeyForm.password
      })
        .then(res => {
          if (res.data && res.data.success) {
            setJourneyData(res.data);
            setDiscoveryPercent(100);
            setDiscoveryLog(logs => [
              ...logs,
              `[Crawler] ✓ Successfully fetched actual screens from ${journeyForm.url}`,
              `[Crawler] Discovered ${res.data.screens.length} screens.`,
              `[Crawler] ✓ Discovery completed successfully!`
            ]);
            setTimeout(() => {
              setIsDiscovering(false);
              setJourneyStep(4);
            }, 1500);
          } else {
            throw new Error(res.data?.error || "Unknown backend error");
          }
        })
        .catch(err => {
          console.error("Discovery error:", err);
          setDiscoveryLog(logs => [
            ...logs,
            `[Error] Headless browser scan failed: ${err.message || err}`,
            `[System] Falling back to default generated screens for ${journeyForm.url}...`
          ]);
          setTimeout(() => {
            const fallbackData = getDynamicJourneyData(journeyForm.url);
            setJourneyData(fallbackData);
            setDiscoveryPercent(100);
            setTimeout(() => {
              setIsDiscovering(false);
              setJourneyStep(4);
            }, 1500);
          }, 3000);
        });

      // Animate progress indicators smoothly
      interval = setInterval(() => {
        setDiscoveryPercent(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          const next = prev + 10;
          if (next === 20) {
            const userStr = journeyForm.username ? `username "${journeyForm.username}"` : "default session cookies";
            setDiscoveryLog(logs => [
              ...logs,
              `[Auth] Navigating to ${journeyForm.url}...`,
              `[Auth] Logging in with credentials (${userStr})...`
            ]);
          } else if (next === 40) {
            setDiscoveryLog(logs => [
              ...logs,
              `[Crawler] Scanning DOM trees and internal routes...`
            ]);
          } else if (next === 60) {
            setDiscoveryLog(logs => [
              ...logs,
              `[Crawler] Extracting links, SPA routes, and menus...`
            ]);
          } else if (next === 80) {
            setDiscoveryLog(logs => [
              ...logs,
              `[AI Agent] Mapping page-to-page navigation sequences...`
            ]);
          } else if (next === 90) {
            setDiscoveryLog(logs => [
              ...logs,
              `[AI Agent] Analyzing static assets and aggregations...`
            ]);
          }
          return next;
        });
      }, 1500);
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDiscovering, journeyForm.url, journeyForm.username, journeyForm.password]);

  // Simulation for Step 5: User Journey Execution
  useEffect(() => {
    let interval;
    if (isRunningAnalysis) {
      setRunElapsed(0);
      setRunPercent(0);
      setActiveScreenIndex(0);
      interval = setInterval(() => {
        setRunElapsed(prev => prev + 1);
        setRunPercent(p => {
          const next = p + 2.5;
          if (next >= 100) {
            clearInterval(interval);
            setIsRunningAnalysis(false);
            setJourneyStep(6); // auto-transition to final Report view
            return 100;
          }
          // Increment current screen index periodically
          const nextScreenIdx = Math.min(Math.floor((next / 100) * journeyData.screens.length), journeyData.screens.length - 1);
          setActiveScreenIndex(nextScreenIdx);
          return next;
        });
      }, 300);
    }
    return () => clearInterval(interval);
  }, [isRunningAnalysis, journeyData]);

  const handleStartExec = () => {
    setExecStatus("running");
    setExecMetrics({
      activeVus: 5,
      responseTime: 180,
      throughput: 45,
      errorRate: 0,
      cpu: 15,
      memory: 45,
      network: 10,
      logs: ["Job started successfully", "Provisioning distributed load agents..."]
    });
  };

  return (
    <div className="pe-container">
      <style>{peCss}</style>

      {/* Main Header */}
      <div className="pe-header">
        <div>
          <h1>🔥 Soak & Stress Testing Engine</h1>
          <p>Enterprise-grade soak endurance, stress breaking point, and deep-dive response testing</p>
        </div>
        <div>
          <button className="pe-btn-primary" onClick={() => setSubPage("create")}>
            ➕ Create Test
          </button>
        </div>
      </div>

      {/* Primary KPI Grid (Dashboard View specific but visible / responsive) */}
      <div className="pe-kpi-grid">
        <div className="pe-card">
          <div className="pe-kpi-label">Total Tests</div>
          <div className="pe-kpi-value">48</div>
          <div className="pe-kpi-trend trend-up">▲ 12% vs last month</div>
        </div>
        <div className="pe-card">
          <div className="pe-kpi-label">Running / Scheduled</div>
          <div className="pe-kpi-value">
            {execStatus === "running" ? "1 Running" : "0 Active"} / 4 Scheduled
          </div>
          <div className="pe-kpi-trend">Next: Database Soak (Daily, 00:00)</div>
        </div>
        <div className="pe-card">
          <div className="pe-kpi-label">Avg Response Time</div>
          <div className="pe-kpi-value">342 ms</div>
          <div className="pe-kpi-trend trend-down">▼ 8% (Optimized)</div>
        </div>
        <div className="pe-card">
          <div className="pe-kpi-label">AI Health Score</div>
          <div className="pe-kpi-value" style={{ color: "#10b981" }}>94 / 100</div>
          <div className="pe-kpi-trend trend-up">SLA compliance met</div>
        </div>
      </div>

      {/* Module Tabs (Navigation matching expandable menu items for easy access in viewport) */}
      <div className="pe-tabs-container">
        {[
          { id: "dashboard", label: "📊 Dashboard" },
          { id: "create", label: "➕ Create Test" },
          { id: "scenarios", label: "📋 Scenarios" },
          { id: "execution", label: "▶️ Live Run" },
          { id: "scheduler", label: "📅 Schedule" },
          { id: "results", label: "📑 Results" },
          { id: "reports", label: "📈 Reports" },
          { id: "analytics", label: "📊 Analytics" },
          { id: "ai-insights", label: "🤖 AI Insights" },
          { id: "settings", label: "⚙️ Settings" }
        ].filter(Boolean).map(tab => (
          <button
            key={tab.id}
            className={`pe-tab-btn ${subPage === tab.id ? "active" : ""}`}
            onClick={() => setSubPage(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB CONTENT: 1. DASHBOARD */}
      {subPage === "dashboard" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <div className="pe-grid-2">
            <div className="pe-card">
              <h3 style={{ margin: "0 0 16px 0", fontSize: "16px" }}>Recent Executions</h3>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e2e8f0", color: "#64748b", textAlign: "left" }}>
                    <th style={{ padding: "8px 0" }}>Test Plan</th>
                    <th style={{ padding: "8px 0" }}>Type</th>
                    <th style={{ padding: "8px 0" }}>VUs</th>
                    <th style={{ padding: "8px 0" }}>Avg Latency</th>
                    <th style={{ padding: "8px 0" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "10px 0", fontWeight: 600 }}>CRM_Flow_LoadTest</td>
                    <td style={{ padding: "10px 0" }}>Load</td>
                    <td style={{ padding: "10px 0" }}>500 VUs</td>
                    <td style={{ padding: "10px 0" }}>245 ms</td>
                    <td style={{ padding: "10px 0", color: "#10b981", fontWeight: 600 }}>PASS</td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "10px 0", fontWeight: 600 }}>API_Gateway_SpikeTest</td>
                    <td style={{ padding: "10px 0" }}>Spike</td>
                    <td style={{ padding: "10px 0" }}>3,000 VUs</td>
                    <td style={{ padding: "10px 0" }}>890 ms</td>
                    <td style={{ padding: "10px 0", color: "#10b981", fontWeight: 600 }}>PASS</td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "10px 0", fontWeight: 600 }}>Checkout_Auth_StressTest</td>
                    <td style={{ padding: "10px 0" }}>Stress</td>
                    <td style={{ padding: "10px 0" }}>5,000 VUs</td>
                    <td style={{ padding: "10px 0" }}>2,450 ms</td>
                    <td style={{ padding: "10px 0", color: "#ef4444", fontWeight: 600 }}>FAIL</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="pe-card">
              <h3 style={{ margin: "0 0 16px 0", fontSize: "16px" }}>Top Bottlenecks</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", marginBottom: "4px" }}>
                    <span>POST /api/v1/auth/login</span>
                    <span style={{ color: "#ef4444", fontWeight: 600 }}>842ms avg</span>
                  </div>
                  <div style={{ height: "6px", background: "#f1f5f9", borderRadius: "3px" }}>
                    <div style={{ height: "100%", width: "85%", background: "#ef4444", borderRadius: "3px" }}></div>
                  </div>
                </div>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", marginBottom: "4px" }}>
                    <span>GET /api/v1/leads</span>
                    <span style={{ color: "#f59e0b", fontWeight: 600 }}>450ms avg</span>
                  </div>
                  <div style={{ height: "6px", background: "#f1f5f9", borderRadius: "3px" }}>
                    <div style={{ height: "100%", width: "50%", background: "#f59e0b", borderRadius: "3px" }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pe-grid-3">
            <div className="pe-card">
              <h4 style={{ margin: "0 0 8px 0" }}>Test Type Distribution</h4>
              <div style={{ fontSize: "14px", color: "#64748b" }}>
                • Load Tests: <strong>55%</strong><br />
                • Stress Tests: <strong>20%</strong><br />
                • Spike/Soak Tests: <strong>25%</strong>
              </div>
            </div>
            <div className="pe-card">
              <h4 style={{ margin: "0 0 8px 0" }}>Environment Summary</h4>
              <div style={{ fontSize: "14px", color: "#64748b" }}>
                • Staging: <strong>32 runs</strong><br />
                • QA: <strong>12 runs</strong><br />
                • Production: <strong>4 runs</strong>
              </div>
            </div>
            <div className="pe-card">
              <h4 style={{ margin: "0 0 8px 0" }}>AI Recommendation</h4>
              <p style={{ margin: 0, fontSize: "12px", lineHeight: "1.5", color: "#475569" }}>
                "Memory consumption on Staging DB spiked by 42% during Soak tests. Check query cache parameters."
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: 2. CREATE TEST WIZARD */}
      {subPage === "create" && (
        <div className="pe-card" style={{ padding: "30px", background: "rgba(255, 255, 255, 0.8)", backdropFilter: "blur(12px)", border: "1px solid rgba(255, 255, 255, 0.3)" }}>
          {/* Mode Selector Toggle */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", paddingBottom: "16px", borderBottom: "1.5px dashed #e2e8f0" }}>
            <div>
              <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", margin: 0 }}>Create Performance Analysis</h2>
              <p style={{ color: "#64748b", fontSize: "13px", margin: "4px 0 0 0" }}>Select the scope of performance auditing</p>
            </div>
            <div style={{ display: "flex", background: "#f1f5f9", borderRadius: "8px", padding: "4px" }}>
              <button
                onClick={() => setCreationMode("journey")}
                style={{
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  background: creationMode === "journey" ? "#FD6101" : "transparent",
                  color: creationMode === "journey" ? "#fff" : "#475569",
                  transition: "all 0.2s"
                }}
              >
                🚀 User Journey (AI-Powered)
              </button>
              <button
                onClick={() => setCreationMode("single")}
                style={{
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  background: creationMode === "single" ? "#FD6101" : "transparent",
                  color: creationMode === "single" ? "#fff" : "#475569",
                  transition: "all 0.2s"
                }}
              >
                🎯 Single-Endpoint Profile
              </button>
            </div>
          </div>

          {/* ==================== CREATION MODE: SINGLE ENDPOINT WIZARD ==================== */}
          {creationMode === "single" && selectedTestType === "spike" && (
            <div>
              {/* Mode Selector Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc", padding: "12px 20px", borderRadius: "12px", border: "1px solid #e2e8f0", marginBottom: "20px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <button
                    className="pe-btn-secondary"
                    onClick={() => {
                      setSelectedTestType("stress");
                      setWizardStep(1);
                    }}
                    style={{ fontSize: "12px", padding: "5px 12px", borderRadius: "6px" }}
                  >
                    ← Profile Selection
                  </button>
                  <span style={{ fontSize: "14px", fontWeight: "600", color: "#475569" }}>⚡ Performance Testing Mode</span>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => { setSpikeMode("quick"); setQuickStep(1); }}
                    style={{
                      border: "none",
                      padding: "6px 14px",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      cursor: "pointer",
                      background: spikeMode === "quick" ? "#FD6101" : "transparent",
                      color: spikeMode === "quick" ? "#fff" : "#475569",
                      transition: "all 0.2s"
                    }}
                  >
                    Quick Mode (Recommended)
                  </button>
                  <button
                    onClick={() => { setSpikeMode("advanced"); setSpikeStep(1); }}
                    style={{
                      border: "none",
                      padding: "6px 14px",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      cursor: "pointer",
                      background: spikeMode === "advanced" ? "#FD6101" : "transparent",
                      color: spikeMode === "advanced" ? "#fff" : "#475569",
                      transition: "all 0.2s"
                    }}
                  >
                    Advanced Mode
                  </button>
                </div>
              </div>

              {/* ==================== QUICK MODE (5 STEPS) ==================== */}
              {spikeMode === "quick" && (
                <div>
                  {/* Quick Stepper Tracker */}
                  <div style={{ display: "flex", gap: "20px", marginBottom: "30px", borderBottom: "1.5px solid #f1f5f9", paddingBottom: "12px" }}>
                    {[
                      { step: 1, name: "1. Create Spike Test" },
                      { step: 2, name: "2. Traffic Simulation" },
                      { step: 3, name: "3. Review" },
                      { step: 4, name: "4. Live Test Screen" },
                      { step: 5, name: "5. AI Report" }
                    ].map(s => (
                      <div
                        key={s.step}
                        style={{
                          fontWeight: quickStep === s.step ? "700" : "500",
                          color: quickStep === s.step ? "#FD6101" : "#64748b",
                          borderBottom: quickStep === s.step ? "3px solid #FD6101" : "3px solid transparent",
                          paddingBottom: "8px",
                          cursor: "pointer"
                        }}
                        onClick={() => setQuickStep(s.step)}
                      >
                        {s.name}
                      </div>
                    ))}
                  </div>

                  {/* QUICK STEP 1: CREATE SPIKE TEST */}
                  {quickStep === 1 && (
                    <div style={{ maxWidth: "600px", margin: "0 auto", background: "#ffffff", padding: "30px", borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                      <h3 style={{ margin: "0 0 8px 0", textAlign: "center", color: "#0f172a" }}>📝 Create Spike Test</h3>
                      <p style={{ margin: "0 0 24px 0", textAlign: "center", color: "#64748b", fontSize: "13px" }}>Configure baseline application targets in under a minute</p>

                      <div style={{ marginBottom: "16px" }}>
                        <label className="pe-label">Test Name</label>
                        <input type="text" className="pe-input" value={quickForm.name} onChange={e => setQuickForm({ ...quickForm, name: e.target.value })} />
                      </div>

                      <div style={{ marginBottom: "16px" }}>
                        <label className="pe-label">Application URL</label>
                        <input
                          type="text"
                          className="pe-input"
                          placeholder="https://cbs.crmfarm.app/role-dashboard"
                          value={quickForm.url}
                          onChange={e => handleQuickUrlChange(e.target.value)}
                        />
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                        <div>
                          <label className="pe-label">Username (to login)</label>
                          <input
                            type="text"
                            className="pe-input"
                            placeholder="e.g. admin@company.com"
                            value={quickForm.username}
                            onChange={e => setQuickForm({ ...quickForm, username: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="pe-label">Password (to login)</label>
                          <input
                            type="password"
                            className="pe-input"
                            placeholder="e.g. ••••••••"
                            value={quickForm.password}
                            onChange={e => setQuickForm({ ...quickForm, password: e.target.value })}
                          />
                        </div>
                      </div>

                      <div style={{ marginBottom: "20px" }}>
                        <label className="pe-label">What do you want to test?</label>
                        <select className="pe-input" value={quickForm.scope} onChange={e => setQuickForm({ ...quickForm, scope: e.target.value })}>
                          <option>Complete User Journey</option>
                          <option>Single Page</option>
                        </select>
                      </div>

                      {quickForm.scope === "Single Page" ? (
                        <div style={{ background: "#f8fafc", padding: "12px 16px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px", color: "#1e293b", marginBottom: "24px" }}>
                          🎯 Target Page: <strong>{journeyData?.screens?.[0]?.name || "Login Page"}</strong>
                        </div>
                      ) : (
                        <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0", marginBottom: "24px" }}>
                          <span style={{ fontSize: "11px", color: "#64748b", display: "block", marginBottom: "8px" }}>Discovered User Journey Sequence:</span>
                          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px", fontSize: "12px", color: "#FD6101", fontWeight: "bold" }}>
                            {journeyData?.screens?.length > 0 ? (
                              journeyData.screens.map((screen, idx) => (
                                <React.Fragment key={screen.id || idx}>
                                  {idx > 0 && <span style={{ color: "#94a3b8" }}>➔</span>}
                                  <span>{screen.name.replace(/^\d+\.\s*/, "")}</span>
                                </React.Fragment>
                              ))
                            ) : (
                              <>
                                <span>Login</span>
                                <span style={{ color: "#94a3b8" }}>➔</span>
                                <span>Dashboard</span>
                                <span style={{ color: "#94a3b8" }}>➔</span>
                                <span>Leads Management</span>
                                <span style={{ color: "#94a3b8" }}>➔</span>
                                <span>Reports</span>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
                        <button
                          className="pe-btn-secondary"
                          style={{ flex: 1 }}
                          onClick={() => {
                            setSelectedTestType("stress");
                            setWizardStep(1);
                          }}
                        >
                          ← Select Performance Profile
                        </button>
                        <button
                          className="pe-btn-primary"
                          style={{ flex: 1 }}
                          onClick={() => setQuickStep(2)}
                        >
                          Continue →
                        </button>
                      </div>
                    </div>
                  )}

                  {/* QUICK STEP 2: TRAFFIC SIMULATION */}
                  {quickStep === 2 && (
                    <div style={{ maxWidth: "650px", margin: "0 auto", background: "#ffffff", padding: "30px", borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                      <h3 style={{ margin: "0 0 20px 0", color: "#0f172a" }}>🚦 Traffic Simulation Configuration</h3>

                      <div style={{ marginBottom: "16px" }}>
                        <label className="pe-label">How many users normally use this application?</label>
                        <input type="number" className="pe-input" value={quickForm.normalUsers} onChange={e => setQuickForm({ ...quickForm, normalUsers: parseInt(e.target.value) })} />
                      </div>

                      <div style={{ marginBottom: "16px" }}>
                        <label className="pe-label">During the traffic spike, how many users do you want to simulate?</label>
                        <input type="number" className="pe-input" value={quickForm.spikeUsers} onChange={e => setQuickForm({ ...quickForm, spikeUsers: parseInt(e.target.value) })} />
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
                        <div>
                          <label className="pe-label">How long should Qintra keep high traffic?</label>
                          <select className="pe-input" value={quickForm.duration} onChange={e => setQuickForm({ ...quickForm, duration: e.target.value })}>
                            <option>30 Seconds</option>
                            <option>1 Minute</option>
                            <option>2 Minutes</option>
                            <option>5 Minutes</option>
                            <option>Custom</option>
                          </select>
                        </div>
                        <div>
                          <label className="pe-label">How many times should the spike repeat?</label>
                          <select className="pe-input" value={quickForm.repeats} onChange={e => setQuickForm({ ...quickForm, repeats: parseInt(e.target.value) })}>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="5">5</option>
                            <option value="10">10</option>
                          </select>
                        </div>
                      </div>

                      <div style={{ marginBottom: "24px" }}>
                        <label className="pe-label" style={{ marginBottom: "8px", display: "block" }}>What is your goal?</label>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                          {[
                            "Check if the application slows down",
                            "Find the breaking point",
                            "Validate automatic recovery",
                            "Verify system stability"
                          ].map(g => (
                            <div
                              key={g}
                              onClick={() => setQuickForm({ ...quickForm, goal: g })}
                              style={{
                                border: quickForm.goal === g ? "2px solid #FD6101" : "1px solid #e2e8f0",
                                borderRadius: "10px",
                                padding: "12px",
                                fontSize: "12px",
                                fontWeight: "600",
                                cursor: "pointer",
                                background: quickForm.goal === g ? "#f5f3ff" : "#ffffff",
                                color: quickForm.goal === g ? "#FD6101" : "#475569"
                              }}
                            >
                              {g}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* AI Recommendation Card */}
                      <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", padding: "16px", borderRadius: "12px", marginBottom: "24px" }}>
                        <div style={{ fontWeight: "bold", color: "#FD6101", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                          <span>🤖 Qintra Recommendation</span>
                          <span style={{ marginLeft: "auto", fontSize: "11px", background: "#FD6101", color: "#fff", padding: "2px 6px", borderRadius: "6px" }}>96% Confidence</span>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", fontSize: "12px", color: "#475569", marginBottom: "12px" }}>
                          <div>Recommended Peak: <strong>2,500 Users</strong></div>
                          <div>Expected Breaking Point: <strong>3,100 Users</strong></div>
                          <div>Estimated Total Duration: <strong>12 Minutes</strong></div>
                        </div>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            className="pe-btn-primary"
                            style={{ fontSize: "11px", padding: "6px 12px" }}
                            onClick={() => {
                              setQuickForm({
                                ...quickForm,
                                spikeUsers: 2500,
                                duration: "2 Minutes",
                                repeats: 3
                              });
                              setQuickStep(3);
                            }}
                          >
                            Use AI Recommendation
                          </button>
                          <button
                            className="pe-btn-secondary"
                            style={{ fontSize: "11px", padding: "6px 12px" }}
                            onClick={() => setQuickStep(3)}
                          >
                            Customize
                          </button>
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <button className="pe-btn-secondary" onClick={() => setQuickStep(1)}>Back</button>
                        <button className="pe-btn-primary" onClick={() => setQuickStep(3)}>Continue</button>
                      </div>
                    </div>
                  )}

                  {/* QUICK STEP 3: REVIEW */}
                  {quickStep === 3 && (
                    <div style={{ maxWidth: "550px", margin: "0 auto", background: "#ffffff", padding: "30px", borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                      <h3 style={{ margin: "0 0 16px 0", color: "#0f172a", textAlign: "center" }}>🔍 Review Spike Test Settings</h3>

                      <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", padding: "20px", borderRadius: "12px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", fontSize: "13px", marginBottom: "24px" }}>
                        <div style={{ gridColumn: "span 2" }}>Target URL: <strong>{quickForm.url}</strong></div>
                        {quickForm.username && <div>Login Account: <strong>{quickForm.username}</strong></div>}
                        <div>Scope: <strong>{quickForm.scope}</strong></div>
                        <div>Normal Users: <strong>{quickForm.normalUsers} Users</strong></div>
                        <div>Peak Users: <strong style={{ color: "#ef4444" }}>{quickForm.spikeUsers} Users</strong></div>
                        <div>Spike Duration: <strong>{quickForm.duration}</strong></div>
                        <div>Repeats: <strong>{quickForm.repeats} Cycles</strong></div>
                        <div>Goal: <strong>{quickForm.goal}</strong></div>
                        <div>Est. Test Duration: <strong style={{ color: "#FD6101" }}>10 Minutes</strong></div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <button className="pe-btn-secondary" onClick={() => setQuickStep(2)}>Back</button>
                        <button className="pe-btn-primary" onClick={runQuickSpikeTest}>Run Spike Test ⚡</button>
                      </div>
                    </div>
                  )}

                  {/* QUICK STEP 4: LIVE TEST SCREEN */}
                  {quickStep === 4 && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "24px", borderRadius: "16px" }}>
                      <h3 style={{ margin: "0 0 16px 0", color: "#0f172a" }}>⚡ Live Spike Testing Cockpit</h3>
                      <p style={{ margin: "0 0 20px 0", color: "#64748b", fontSize: "13px" }}>Auditing Target: <strong>{quickForm.url}</strong></p>

                      {/* Large KPI Cards */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "12px", marginBottom: "20px" }}>
                        {[
                          { label: "Current Users", val: spikeCurrentUsers, color: "#FD6101" },
                          { label: "Avg Latency", val: `${(journeyData?.screens ? journeyData.screens.reduce((acc, s) => acc + (parseFloat(s.time) || 1.5), 0) / journeyData.screens.length : 1.8).toFixed(1)}s`, color: "#ef4444" },
                          { label: "Throughput", val: `${(spikeCurrentUsers * 12).toLocaleString()} req/s`, color: "#16a34a" },
                          { label: "Errors", val: spikeStage === "Traffic Spike" ? "0.8%" : "0.0%", color: "#dc2626" },
                          { label: "CPU Usage", val: spikeStage === "Traffic Spike" ? "84%" : "32%", color: "FD6101" },
                          { label: "Memory Usage", val: spikeStage === "Traffic Spike" ? "76%" : "48%", color: "#4f46e5" }
                        ].map((kpi, idx) => (
                          <div key={idx} style={{ border: "1px solid #e2e8f0", padding: "12px", borderRadius: "12px", background: "#f8fafc" }}>
                            <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "4px" }}>{kpi.label}</div>
                            <strong style={{ fontSize: "18px", color: kpi.color }}>{kpi.val}</strong>
                          </div>
                        ))}
                      </div>

                      {/* Simplified Chart */}
                      <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", background: "#f8fafc", marginBottom: "20px" }}>
                        <span style={{ fontSize: "12px", fontWeight: "bold", color: "#475569" }}>📈 Traffic Ingestion wave Graph for {quickForm.url}</span>
                        <div style={{ height: "120px", position: "relative", marginTop: "10px" }}>
                          <svg width="100%" height="100%" style={{ overflow: "visible" }}>
                            <path d="M0,100 L120,100 L200,20 L350,20 L420,100 L550,100" fill="none" stroke="#FD6101" strokeWidth="3" />
                            <text x="5" y="15" fill="#ef4444" fontSize="9" fontWeight="bold">▲ {quickForm.spikeUsers} Peak Users</text>
                            <text x="5" y="95" fill="#64748b" fontSize="9">{quickForm.normalUsers} Baseline Users</text>
                          </svg>
                        </div>
                      </div>

                      {/* Current Stage horizontal checklist */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px", marginBottom: "20px" }}>
                        {[
                          { id: "Normal Traffic", label: "✓ Normal Traffic", active: spikeStage === "Normal Traffic" || spikeStage === "Traffic Spike" || spikeStage === "Maintaining Peak Traffic" || spikeStage === "Recovery Check" },
                          { id: "Traffic Spike", label: "✓ Traffic Spike", active: spikeStage === "Traffic Spike" || spikeStage === "Maintaining Peak Traffic" || spikeStage === "Recovery Check" },
                          { id: "Maintaining Peak Traffic", label: "⏳ Maintaining Peak Traffic", active: spikeStage === "Maintaining Peak Traffic" || spikeStage === "Recovery Check" },
                          { id: "Recovery Check", label: "Next: Recovery Check", active: spikeStage === "Recovery Check" }
                        ].map((st, i) => (
                          <div
                            key={i}
                            style={{
                              padding: "10px",
                              borderRadius: "8px",
                              fontSize: "12px",
                              fontWeight: "bold",
                              background: st.active ? "#FD6101" : "#f1f5f9",
                              color: st.active ? "#ffffff" : "#64748b",
                              textAlign: "center"
                            }}
                          >
                            {st.label}
                          </div>
                        ))}
                      </div>

                      {/* AI Live Insights alerts */}
                      <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", padding: "16px", borderRadius: "12px" }}>
                        <strong style={{ fontSize: "13px", color: "#0f172a", display: "block", marginBottom: "8px" }}>🤖 AI Live Insights Alerts for {quickForm.url}</strong>
                        <div style={{ fontSize: "12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                          <span style={{ color: "#16a34a" }}>✓ Target site connection established with credentials.</span>
                          <span style={{ color: "#16a34a" }}>✓ Discovered {journeyData?.screens?.length || 4} screens audited.</span>
                          {journeyData?.bottlenecks?.[0] && (
                            <span style={{ color: "#d97706" }}>⚠ Bottleneck: {journeyData.bottlenecks[0].title}</span>
                          )}
                          <span style={{ color: "#16a34a" }}>✓ Database and session routes healthy.</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* QUICK STEP 5: AI REPORT */}
                  {quickStep === 5 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

                      <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "30px", borderRadius: "16px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #f1f5f9", paddingBottom: "16px", marginBottom: "20px" }}>
                          <div>
                            <h3 style={{ margin: 0, color: "#0f172a", fontSize: "20px" }}>🤖 Qintra Executive Spike Report</h3>
                            <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: "13px" }}>Target URL: <strong>{quickForm.url}</strong> {quickForm.username && `| Login Account: ${quickForm.username}`}</p>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#22c55e" }}>Grade A</div>
                            <div style={{ fontSize: "11px", color: "#94a3b8" }}>Health Score: 94/100</div>
                          </div>
                        </div>

                        {/* KPI Metrics */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "10px", marginBottom: "20px" }}>
                          {[
                            { label: "Overall Health", val: "94/100" },
                            { label: "Performance", val: "Grade A" },
                            { label: "Max Users Handled", val: `${quickForm.spikeUsers}` },
                            { label: "Avg Load Time", val: `${(journeyData?.screens ? journeyData.screens.reduce((acc, s) => acc + (parseFloat(s.time) || 1.5), 0) / journeyData.screens.length : 1.8).toFixed(1)}s` },
                            { label: "Recovery Time", val: "38 Seconds" },
                            { label: "Availability", val: "99.8%" }
                          ].map((chk, i) => (
                            <div key={i} style={{ background: "#f8fafc", padding: "10px", borderRadius: "10px", border: "1px solid #e2e8f0", textAlign: "center" }}>
                              <span style={{ fontSize: "10px", color: "#64748b", display: "block" }}>{chk.label}</span>
                              <strong style={{ fontSize: "14px", color: "#1e293b" }}>{chk.val}</strong>
                            </div>
                          ))}
                        </div>



                        {/* AI Findings cards */}
                        <h4 style={{ margin: "0 0 12px 0", color: "#0f172a", fontSize: "14px" }}>💡 AI Anomaly Findings</h4>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>
                          {journeyData?.bottlenecks?.map((issue, idx) => (
                            <div key={idx} style={{ border: issue.severity === "CRITICAL" ? "1px solid #fecaca" : "1px solid #fde047", background: issue.severity === "CRITICAL" ? "#fef2f2" : "#fffbeb", padding: "16px", borderRadius: "12px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                                <strong style={{ fontSize: "13px", color: issue.severity === "CRITICAL" ? "#dc2626" : "#d97706" }}>{issue.title}</strong>
                                <span style={{ fontSize: "10px", background: issue.severity === "CRITICAL" ? "#dc2626" : "#d97706", color: "#fff", padding: "2px 6px", borderRadius: "6px" }}>{issue.severity}</span>
                              </div>
                              <div style={{ fontSize: "12px", color: "#1e293b" }}>
                                • <strong>Problem:</strong> {issue.rootCause}<br />
                                • <strong>Impact:</strong> {issue.evidence}<br />
                                • <strong>Fix:</strong> {issue.fix}<br />
                                • <strong>Estimated Improvement:</strong> <strong style={{ color: "#16a34a" }}>{issue.improvement}</strong>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Recommendations */}
                        <h4 style={{ margin: "0 0 12px 0", color: "#0f172a", fontSize: "14px" }}>💡 AI Optimization Recommendations</h4>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>
                          {journeyData?.recommendations?.map((rec, i) => (
                            <div key={i} style={{ border: "1px solid #e2e8f0", padding: "16px", borderRadius: "12px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "12px" }}>
                                <strong style={{ color: "#FD6101" }}>{rec.title}</strong>
                                <span style={{ color: "#16a34a" }}>{rec.imp}</span>
                              </div>
                              <p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#64748b" }}>{rec.desc}</p>
                              <div style={{ fontSize: "11px", color: "#475569" }}>Effort: <strong>{rec.diff}</strong></div>
                            </div>
                          ))}
                        </div>

                        {/* Final Summary */}
                        <h4 style={{ margin: "0 0 12px 0", color: "#0f172a", fontSize: "14px" }}>🏢 Final Management Summary</h4>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", fontSize: "12px", marginBottom: "20px" }}>
                          <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                            <span style={{ color: "#64748b", display: "block" }}>Overall Performance</span>
                            <strong>Excellent</strong>
                          </div>
                          <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                            <span style={{ color: "#64748b", display: "block" }}>Application Stability</span>
                            <strong>Excellent</strong>
                          </div>
                          <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                            <span style={{ color: "#64748b", display: "block" }}>System Recovery</span>
                            <strong style={{ color: "#16a34a" }}>Successful</strong>
                          </div>
                          <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                            <span style={{ color: "#64748b", display: "block" }}>Max Supported Users</span>
                            <strong>{quickForm.spikeUsers} VUs</strong>
                          </div>
                          <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                            <span style={{ color: "#64748b", display: "block" }}>Breaking Point</span>
                            <strong style={{ color: "#ef4444" }}>{Math.floor(quickForm.spikeUsers * 1.25)} Users</strong>
                          </div>
                          <div style={{ background: "#f0fdf4", padding: "10px", borderRadius: "8px", border: "1px solid #bbf7d0", textAlign: "center" }}>
                            <span style={{ color: "#16803d", display: "block" }}>Production Ready</span>
                            <strong style={{ color: "#16a34a" }}>YES</strong>
                          </div>
                        </div>

                        {/* Actions */}
                        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                          <button className="pe-btn-secondary" onClick={() => alert("PDF report generated.")}>Download PDF</button>
                          <button className="pe-btn-secondary" onClick={() => alert("Excel sheet generated.")}>Download Excel</button>
                          <button className="pe-btn-secondary" onClick={() => alert("Link copied to clipboard.")}>🔗 Share</button>
                          <button className="pe-btn-secondary" onClick={() => alert("Comparison initialized.")}>🔄 Compare Runs</button>
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button
                          className="pe-btn-secondary"
                          onClick={() => {
                            setQuickStep(1);
                            setSpikeElapsed(0);
                          }}
                        >
                          🔄 Reset Spike Test
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ==================== ADVANCED MODE (11 STEPS) ==================== */}
              {spikeMode === "advanced" && (
                <div>
                  {/* Spike Wizard Steps Breadcrumbs (11 Steps) */}
                  <div style={{ display: "flex", gap: "15px", marginBottom: "30px", borderBottom: "1.5px solid #e2e8f0", paddingBottom: "16px", overflowX: "auto" }}>
                    {[
                      { step: 1, name: "1. Create Spike Test" },
                      { step: 2, name: "2. Select Scenario" },
                      { step: 3, name: "3. Spike Pattern" },
                      { step: 4, name: "4. Spike Timeline" },
                      { step: 5, name: "5. Environment" },
                      { step: 6, name: "6. AI Recommendations" },
                      { step: 7, name: "7. Execute" },
                      { step: 8, name: "8. Live Monitoring" },
                      { step: 9, name: "9. Spike Analysis" },
                      { step: 10, name: "10. Recovery Validation" },
                      { step: 11, name: "11. Executive Report" }
                    ].map(s => (
                      <div
                        key={s.step}
                        style={{
                          fontWeight: spikeStep === s.step ? "700" : "500",
                          color: spikeStep === s.step ? "#FD6101" : "#64748b",
                          borderBottom: spikeStep === s.step ? "3px solid #FD6101" : "3px solid transparent",
                          paddingBottom: "8px",
                          cursor: "pointer",
                          whiteSpace: "nowrap"
                        }}
                        onClick={() => setSpikeStep(s.step)}
                      >
                        {s.name}
                      </div>
                    ))}
                  </div>

                  {/* STEP 1: CREATE SPIKE TEST */}
                  {spikeStep === 1 && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "24px", borderRadius: "16px" }}>
                      <h4 style={{ margin: "0 0 16px 0", color: "#0f172a" }}>📝 Step 1 - Create Spike Test</h4>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                        <div>
                          <div style={{ marginBottom: "12px" }}>
                            <label className="pe-label">Test Name</label>
                            <input type="text" className="pe-input" value={spikeForm.name} onChange={e => setSpikeForm({ ...spikeForm, name: e.target.value })} />
                          </div>
                          <div style={{ marginBottom: "12px" }}>
                            <label className="pe-label">Description</label>
                            <textarea className="pe-input" style={{ height: "60px" }} value={spikeForm.description} onChange={e => setSpikeForm({ ...spikeForm, description: e.target.value })} />
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
                            <div>
                              <label className="pe-label">Project</label>
                              <input type="text" className="pe-input" value={spikeForm.project} onChange={e => setSpikeForm({ ...spikeForm, project: e.target.value })} />
                            </div>
                            <div>
                              <label className="pe-label">Application</label>
                              <input type="text" className="pe-input" value={spikeForm.application} onChange={e => setSpikeForm({ ...spikeForm, application: e.target.value })} />
                            </div>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
                            <div>
                              <label className="pe-label">Environment</label>
                              <select className="pe-input" value={spikeForm.environment} onChange={e => setSpikeForm({ ...spikeForm, environment: e.target.value })}>
                                <option>Staging</option>
                                <option>QA</option>
                                <option>Production</option>
                              </select>
                            </div>
                            <div>
                              <label className="pe-label">Base URL</label>
                              <input type="text" className="pe-input" value={spikeForm.baseUrl} onChange={e => setSpikeForm({ ...spikeForm, baseUrl: e.target.value })} />
                            </div>
                          </div>
                        </div>

                        <div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
                            <div>
                              <label className="pe-label">Auth Type</label>
                              <select className="pe-input" value={spikeForm.authType} onChange={e => setSpikeForm({ ...spikeForm, authType: e.target.value })}>
                                <option>Bearer Token</option>
                                <option>OAuth 2.0</option>
                                <option>Basic Auth</option>
                                <option>None</option>
                              </select>
                            </div>
                            <div>
                              <label className="pe-label">Location</label>
                              <input type="text" className="pe-input" value={spikeForm.location} onChange={e => setSpikeForm({ ...spikeForm, location: e.target.value })} />
                            </div>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
                            <div>
                              <label className="pe-label">Username</label>
                              <input type="text" className="pe-input" value={spikeForm.username} onChange={e => setSpikeForm({ ...spikeForm, username: e.target.value })} />
                            </div>
                            <div>
                              <label className="pe-label">Password</label>
                              <input type="password" className="pe-input" value={spikeForm.password} onChange={e => setSpikeForm({ ...spikeForm, password: e.target.value })} />
                            </div>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
                            <div>
                              <label className="pe-label">Browser</label>
                              <input type="text" className="pe-input" value={spikeForm.browser} onChange={e => setSpikeForm({ ...spikeForm, browser: e.target.value })} />
                            </div>
                            <div>
                              <label className="pe-label">Device</label>
                              <input type="text" className="pe-input" value={spikeForm.device} onChange={e => setSpikeForm({ ...spikeForm, device: e.target.value })} />
                            </div>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
                            <div>
                              <label className="pe-label">Network Profile</label>
                              <input type="text" className="pe-input" value={spikeForm.networkProfile} onChange={e => setSpikeForm({ ...spikeForm, networkProfile: e.target.value })} />
                            </div>
                            <div>
                              <label className="pe-label">Tags</label>
                              <input type="text" className="pe-input" value={spikeForm.tags} onChange={e => setSpikeForm({ ...spikeForm, tags: e.target.value })} />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                        <button className="pe-btn-secondary" onClick={() => alert("Draft saved.")}>Save Draft</button>
                        <button className="pe-btn-primary" onClick={() => setSpikeStep(2)}>Continue →</button>
                      </div>
                    </div>
                  )}

                  {/* STEP 2: SELECT TEST SCENARIO */}
                  {spikeStep === 2 && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "24px", borderRadius: "16px" }}>
                      <h4 style={{ margin: "0 0 16px 0", color: "#0f172a" }}>🎯 Step 2 - Select Test Scenario</h4>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "20px" }}>
                        {[
                          { id: "Single URL", title: "Single URL", steps: 1, apis: 1, duration: "3 min" },
                          { id: "Complete User Journey", title: "Complete User Journey", steps: 14, apis: 42, duration: "8 min", rec: true },
                          { id: "Recorded Journey", title: "Recorded Journey", steps: 8, apis: 16, duration: "5 min" },
                          { id: "Playwright Script", title: "Playwright Script", steps: 12, apis: 24, duration: "7 min" },
                          { id: "Selenium Script", title: "Selenium Script", steps: 15, apis: 30, duration: "10 min" },
                          { id: "Cypress Script", title: "Cypress Script", steps: 9, apis: 18, duration: "6 min" },
                          { id: "Postman Collection", title: "Postman Collection", steps: 6, apis: 12, duration: "4 min" },
                          { id: "Existing Functional Test", title: "Existing Functional Test", steps: 10, apis: 20, duration: "7 min" },
                          { id: "API Collection", title: "API Collection", steps: 22, apis: 44, duration: "12 min" }
                        ].map(card => (
                          <div
                            key={card.id}
                            onClick={() => setSelectedScenario(card.id)}
                            style={{
                              border: selectedScenario === card.id ? "2px solid #FD6101" : "1px solid #e2e8f0",
                              borderRadius: "12px",
                              padding: "16px",
                              cursor: "pointer",
                              background: selectedScenario === card.id ? "#f8fafc" : "#ffffff",
                              position: "relative"
                            }}
                          >
                            {card.rec && (
                              <span style={{ position: "absolute", top: "10px", right: "10px", background: "#dcfce7", color: "#16803d", padding: "2px 6px", borderRadius: "8px", fontSize: "10px", fontWeight: "bold" }}>
                                AI Recommended
                              </span>
                            )}
                            <h4 style={{ margin: "0 0 10px 0", color: "#1e293b" }}>{card.title}</h4>
                            <div style={{ fontSize: "12px", color: "#64748b", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
                              <div>⏱️ Duration: <strong>{card.duration}</strong></div>
                              <div>🔄 Steps: <strong>{card.steps}</strong></div>
                              <div>📡 APIs: <strong>{card.apis}</strong></div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <button className="pe-btn-secondary" onClick={() => setSpikeStep(1)}>Back</button>
                        <button className="pe-btn-primary" onClick={() => setSpikeStep(3)}>Continue →</button>
                      </div>
                    </div>
                  )}

                  {/* STEP 3: CONFIGURE SPIKE PATTERN */}
                  {spikeStep === 3 && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "24px", borderRadius: "16px" }}>
                      <h4 style={{ margin: "0 0 16px 0", color: "#0f172a" }}>⚙️ Step 3 - Configure Spike Pattern</h4>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "24px" }}>
                        <div>
                          <label className="pe-label">Normal User Count</label>
                          <input type="number" className="pe-input" value={spikePattern.normalUsers} onChange={e => setSpikePattern({ ...spikePattern, normalUsers: parseInt(e.target.value) })} />
                        </div>
                        <div>
                          <label className="pe-label">Spike User Count</label>
                          <input type="number" className="pe-input" value={spikePattern.spikeUsers} onChange={e => setSpikePattern({ ...spikePattern, spikeUsers: parseInt(e.target.value) })} />
                        </div>
                        <div>
                          <label className="pe-label">Spike Ramp-Up Time (s)</label>
                          <input type="number" className="pe-input" value={spikePattern.rampUp} onChange={e => setSpikePattern({ ...spikePattern, rampUp: parseInt(e.target.value) })} />
                        </div>
                        <div>
                          <label className="pe-label">Spike Hold Duration (s)</label>
                          <input type="number" className="pe-input" value={spikePattern.hold} onChange={e => setSpikePattern({ ...spikePattern, hold: parseInt(e.target.value) })} />
                        </div>
                        <div>
                          <label className="pe-label">Ramp-Down Time (s)</label>
                          <input type="number" className="pe-input" value={spikePattern.rampDown} onChange={e => setSpikePattern({ ...spikePattern, rampDown: parseInt(e.target.value) })} />
                        </div>
                        <div>
                          <label className="pe-label">Recovery Duration (s)</label>
                          <input type="number" className="pe-input" value={spikePattern.recovery} onChange={e => setSpikePattern({ ...spikePattern, recovery: parseInt(e.target.value) })} />
                        </div>
                        <div>
                          <label className="pe-label">Number of Spike Cycles</label>
                          <input type="number" className="pe-input" value={spikePattern.cycles} onChange={e => setSpikePattern({ ...spikePattern, cycles: parseInt(e.target.value) })} />
                        </div>
                        <div>
                          <label className="pe-label">Think Time (s)</label>
                          <input type="number" className="pe-input" value={spikePattern.thinkTime} onChange={e => setSpikePattern({ ...spikePattern, thinkTime: parseInt(e.target.value) })} />
                        </div>
                        <div>
                          <label className="pe-label">Test Duration (s)</label>
                          <input type="number" className="pe-input" value={spikePattern.duration} onChange={e => setSpikePattern({ ...spikePattern, duration: parseInt(e.target.value) })} />
                        </div>
                      </div>

                      {/* Config Flow Path */}
                      <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0", marginBottom: "20px" }}>
                        <strong style={{ fontSize: "12px", color: "#475569", display: "block", marginBottom: "10px" }}>🔄 Configured Spike Cycle Sequence</strong>
                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", fontSize: "12px", color: "#1e293b", fontWeight: "bold" }}>
                          <span>Normal: {spikePattern.normalUsers} Users</span>
                          <span>➔</span>
                          <span style={{ color: "#ef4444" }}>Spike: {spikePattern.spikeUsers} Users</span>
                          <span>➔</span>
                          <span>Hold: {spikePattern.hold}s</span>
                          <span>➔</span>
                          <span>Return: {spikePattern.normalUsers} Users</span>
                          <span>➔</span>
                          <span>Recovery: {spikePattern.recovery}s</span>
                          <span>➔</span>
                          <span style={{ color: "#FD6101" }}>Repeat: {spikePattern.cycles} Times</span>
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <button className="pe-btn-secondary" onClick={() => setSpikeStep(2)}>Back</button>
                        <button className="pe-btn-primary" onClick={() => setSpikeStep(4)}>Continue →</button>
                      </div>
                    </div>
                  )}

                  {/* STEP 4: SPIKE TIMELINE (INTERACTIVE) */}
                  {spikeStep === 4 && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "24px", borderRadius: "16px" }}>
                      <h4 style={{ margin: "0 0 10px 0", color: "#0f172a" }}>📈 Step 4 - Spike Timeline Editor</h4>
                      <p style={{ margin: "0 0 20px 0", color: "#64748b", fontSize: "13px" }}>Drag the slider to adjust spike height dynamically. Add or duplicate spikes in timeline sequence.</p>

                      {/* Interactive Timeline Graph */}
                      <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", marginBottom: "20px" }}>
                        <div style={{ height: "180px", borderLeft: "2px solid #94a3b8", borderBottom: "2px solid #94a3b8", position: "relative", margin: "10px 20px" }}>
                          <svg width="100%" height="100%" style={{ overflow: "visible" }}>
                            {/* Peak indicator line */}
                            <line x1="0" y1="20" x2="100%" y2="20" stroke="#fecaca" strokeDasharray="4,4" />
                            <text x="5" y="15" fill="#ef4444" fontSize="10" fontWeight="bold">Max Spike: {spikePattern.spikeUsers} VUs</text>

                            {/* Base indicator line */}
                            <line x1="0" y1="150" x2="100%" y2="150" stroke="#cbd5e1" strokeDasharray="2,2" />
                            <text x="5" y="145" fill="#64748b" fontSize="10">Base: {spikePattern.normalUsers} VUs</text>

                            {/* Spike path */}
                            <path
                              d={`M 0,150 L 80,150 L 160,20 L 320,20 L 400,150 L 520,150 L 600,20 L 760,20 L 840,150`}
                              fill="none"
                              stroke="#FD6101"
                              strokeWidth="3.5"
                            />
                          </svg>
                        </div>

                        <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "16px" }}>
                          <button className="pe-btn-secondary" style={{ fontSize: "11px", padding: "6px 12px" }} onClick={() => alert("Spike height adjusted.")}>Drag Spike Height</button>
                          <button className="pe-btn-secondary" style={{ fontSize: "11px", padding: "6px 12px" }} onClick={() => alert("Spike duration modified.")}>Adjust Duration</button>
                          <button className="pe-btn-secondary" style={{ fontSize: "11px", padding: "6px 12px" }} onClick={() => alert("New spike added.")}>＋ Add Spike</button>
                          <button className="pe-btn-secondary" style={{ fontSize: "11px", padding: "6px 12px" }} onClick={() => alert("Spike deleted.")}>🗑️ Delete</button>
                          <button className="pe-btn-secondary" style={{ fontSize: "11px", padding: "6px 12px" }} onClick={() => alert("Spike duplicated.")}>📋 Duplicate</button>
                          <button className="pe-btn-secondary" style={{ fontSize: "11px", padding: "6px 12px", background: "#fff7ed", color: "#FD6101" }} onClick={() => alert("Execution preview rendered successfully.")}>👁️ Preview Execution</button>
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <button className="pe-btn-secondary" onClick={() => setSpikeStep(3)}>Back</button>
                        <button className="pe-btn-primary" onClick={() => setSpikeStep(5)}>Continue →</button>
                      </div>
                    </div>
                  )}

                  {/* STEP 5: ENVIRONMENT CONFIGURATION */}
                  {spikeStep === 5 && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "24px", borderRadius: "16px" }}>
                      <h4 style={{ margin: "0 0 16px 0", color: "#0f172a" }}>🌐 Step 5 - Environment Configuration</h4>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
                        <div>
                          <div style={{ marginBottom: "16px" }}>
                            <label className="pe-label">Infrastructure Provider</label>
                            <select className="pe-input" value={envConfig.infra} onChange={e => setEnvConfig({ ...envConfig, infra: e.target.value })}>
                              <option>Kubernetes</option>
                              <option>Docker</option>
                              <option>AWS Agent Pool</option>
                              <option>Azure Container</option>
                              <option>Local Machine</option>
                            </select>
                          </div>
                          <div style={{ marginBottom: "16px" }}>
                            <label className="pe-label">Emulated Network Profile</label>
                            <select className="pe-input" value={envConfig.network} onChange={e => setEnvConfig({ ...envConfig, network: e.target.value })}>
                              <option>5G</option>
                              <option>4G LTE</option>
                              <option>WiFi</option>
                              <option>Slow 3G</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <div style={{ marginBottom: "16px" }}>
                            <label className="pe-label">Geographic Region</label>
                            <select className="pe-input" value={envConfig.region} onChange={e => setEnvConfig({ ...envConfig, region: e.target.value })}>
                              <option>India</option>
                              <option>US East</option>
                              <option>Europe Central</option>
                              <option>Singapore</option>
                            </select>
                          </div>
                          <div>
                            <label className="pe-label" style={{ marginBottom: "8px", display: "block" }}>Active Telemetry Monitoring</label>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "12px" }}>
                              {["CPU & Memory", "Database Locks", "API Gateway Latency", "Kubernetes Pod Health", "Docker Daemon Stats", "Network I/O"].map(opt => (
                                <label key={opt} style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                                  <input type="checkbox" defaultChecked={true} /> {opt}
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <button className="pe-btn-secondary" onClick={() => setSpikeStep(4)}>Back</button>
                        <button className="pe-btn-primary" onClick={() => setSpikeStep(6)}>Continue →</button>
                      </div>
                    </div>
                  )}

                  {/* STEP 6: AI RECOMMENDATIONS */}
                  {spikeStep === 6 && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "24px", borderRadius: "16px" }}>
                      <h4 style={{ margin: "0 0 10px 0", color: "#0f172a" }}>🤖 Step 6 - AI Recommendations</h4>
                      <p style={{ margin: "0 0 20px 0", color: "#64748b", fontSize: "13px" }}>Qintra has analyzed previous execution logs and cloud capacity to recommend bounds.</p>

                      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "24px", marginBottom: "20px" }}>
                        <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", padding: "20px", borderRadius: "16px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                            <span style={{ fontSize: "12px", background: "#FD6101", color: "#fff", padding: "3px 8px", borderRadius: "8px", fontWeight: "bold" }}>Optimal Settings Match</span>
                            <span style={{ fontSize: "13px", color: "#FD6101", fontWeight: "bold" }}>Confidence: 96%</span>
                          </div>

                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", fontSize: "13px" }}>
                            <div>Recommended Peak Users: <strong style={{ fontSize: "16px", color: "#FD6101" }}>2,500 VUs</strong></div>
                            <div>Expected Breaking Point: <strong style={{ fontSize: "16px", color: "#ef4444" }}>3,200 Users</strong></div>
                            <div>Recommended Spike Duration: <strong>150 Seconds</strong></div>
                            <div>Recommended Recovery: <strong>38 Seconds</strong></div>
                          </div>
                        </div>

                        <div style={{ background: "#f8fafc", border: "1px solid #cbd5e1", padding: "20px", borderRadius: "16px", fontSize: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
                          <div>☁️ Estimated Cloud Execution Cost: <strong style={{ color: "#16a34a" }}>$32.40 USD</strong></div>
                          <div>⏱️ Estimated Total Duration: <strong>12 Minutes</strong></div>
                          <div>🛡️ SLA Latency Bound: <strong>450 ms</strong></div>
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <button className="pe-btn-secondary" onClick={() => setSpikeStep(5)}>Back</button>
                        <button className="pe-btn-primary" onClick={() => { setSpikeStep(7); setSpikeIsRunning(true); }}>Run Spike Test ⚡</button>
                      </div>
                    </div>
                  )}

                  {/* STEP 7: EXECUTE SPIKE TEST */}
                  {spikeStep === 7 && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "24px", borderRadius: "16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                        <div>
                          <h4 style={{ margin: "0 0 4px 0", color: "#0f172a" }}>🚀 Step 7 - Execute Spike Test Dashboard</h4>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>Active Run ID: <strong>PERF-SPIKE-RUN</strong></div>
                        </div>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button className="pe-btn-secondary" style={{ padding: "6px 12px", fontSize: "11px" }} onClick={() => setSpikeIsRunning(false)}>Pause</button>
                          <button className="pe-btn-secondary" style={{ padding: "6px 12px", fontSize: "11px" }} onClick={() => setSpikeIsRunning(true)}>Resume</button>
                          <button className="pe-btn-secondary" style={{ padding: "6px 12px", fontSize: "11px", background: "#fecaca", color: "#dc2626" }} onClick={() => { setSpikeIsRunning(false); setSpikeStep(11); }}>Stop Test</button>
                          <button className="pe-btn-secondary" style={{ padding: "6px 12px", fontSize: "11px" }} onClick={() => alert("Logs downloaded.")}>Download Logs</button>
                        </div>
                      </div>

                      {/* Top KPI Cards Grid */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "12px", marginBottom: "20px" }}>
                        {[
                          { label: "Current Users", val: spikeCurrentUsers, color: "#FD6101" },
                          { label: "Peak Users", val: spikePattern.spikeUsers, color: "#1e293b" },
                          { label: "Current Latency", val: `${Math.floor(180 + Math.random() * 50)} ms`, color: "#16a34a" },
                          { label: "Throughput", val: `${Math.floor(spikeCurrentUsers * 12)} req/s`, color: "FD6101" },
                          { label: "Active Stage", val: spikeStage, color: "#f59e0b" }
                        ].map((kpi, idx) => (
                          <div key={idx} style={{ border: "1px solid #e2e8f0", padding: "12px", borderRadius: "10px", background: "#f8fafc" }}>
                            <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "4px" }}>{kpi.label}</div>
                            <div style={{ fontSize: "18px", fontWeight: "bold", color: kpi.color }}>{kpi.val}</div>
                          </div>
                        ))}
                      </div>

                      {/* Execution Stages flow */}
                      <div style={{ display: "flex", gap: "10px", alignItems: "center", background: "#f8fafc", padding: "16px", borderRadius: "12px", marginBottom: "20px" }}>
                        {[
                          { stage: "Normal Users", label: "Normal" },
                          { stage: "Spike", label: "Spike ⚡" },
                          { stage: "Hold", label: "Hold ⏳" },
                          { stage: "Ramp Down", label: "Ramp Down" },
                          { stage: "Recovery", label: "Recovery" }
                        ].map(st => (
                          <div
                            key={st.stage}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "8px",
                              fontSize: "12px",
                              fontWeight: "bold",
                              background: spikeStage === st.stage ? "#FD6101" : "#e2e8f0",
                              color: spikeStage === st.stage ? "#ffffff" : "#475569",
                              transition: "all 0.2s"
                            }}
                          >
                            {st.label}
                          </div>
                        ))}
                        <div style={{ marginLeft: "auto", fontSize: "12px", fontWeight: "bold", color: "#FD6101" }}>
                          Spike Cycle: {spikeCycle} of {spikePattern.cycles}
                        </div>
                      </div>

                      {/* Live Simulation Terminal logs */}
                      <div style={{ background: "#0f172a", color: "#38bdf8", padding: "16px", borderRadius: "12px", fontFamily: "monospace", fontSize: "11px", height: "120px", overflowY: "auto", lineHeight: "1.6" }}>
                        {spikeLogs.map((log, idx) => (
                          <div key={idx}>{log}</div>
                        ))}
                      </div>

                      <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end" }}>
                        <button className="pe-btn-primary" onClick={() => setSpikeStep(8)}>View Live Graphs →</button>
                      </div>
                    </div>
                  )}

                  {/* STEP 8: LIVE MONITORING DASHBOARD */}
                  {spikeStep === 8 && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "24px", borderRadius: "16px" }}>
                      <h4 style={{ margin: "0 0 16px 0", color: "#0f172a" }}>📊 Step 8 - Live Monitoring Dashboard</h4>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
                        <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", background: "#f8fafc" }}>
                          <strong style={{ fontSize: "12px", color: "#475569" }}>📈 Concurrent Users & Response Time</strong>
                          <div style={{ height: "130px", marginTop: "10px", position: "relative" }}>
                            {/* Users Wave line */}
                            <svg width="100%" height="100%">
                              <path d="M0,110 L50,110 L100,20 L250,20 L300,110 L350,110 L400,20 L450,20" fill="none" stroke="#FD6101" strokeWidth="2.5" />
                              <path d="M0,90 L50,90 L100,70 L250,75 L300,90 L350,90 L400,70 L450,70" fill="none" stroke="#ef4444" strokeWidth="2" strokeDasharray="3,3" />
                            </svg>
                          </div>
                        </div>

                        <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", background: "#f8fafc" }}>
                          <strong style={{ fontSize: "12px", color: "#475569" }}>📡 System Throughput (req/s) & Error Rate</strong>
                          <div style={{ height: "130px", marginTop: "10px", position: "relative" }}>
                            <svg width="100%" height="100%">
                              <path d="M0,110 L50,110 L100,30 L250,30 L300,110 L350,110 L400,30 L450,30" fill="none" stroke="FD6101" strokeWidth="2" />
                              <line x1="0" y1="120" x2="100%" y2="120" stroke="#cbd5e1" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <button className="pe-btn-secondary" onClick={() => setSpikeStep(7)}>Back to Execution</button>
                        <button className="pe-btn-primary" onClick={() => setSpikeStep(9)}>View Spike Analysis →</button>
                      </div>
                    </div>
                  )}

                  {/* STEP 9: SPIKE ANALYSIS */}
                  {spikeStep === 9 && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "24px", borderRadius: "16px" }}>
                      <h4 style={{ margin: "0 0 10px 0", color: "#0f172a" }}>🛡️ Step 9 - Qintra Spike Analysis</h4>
                      <p style={{ margin: "0 0 20px 0", color: "#64748b", fontSize: "13px" }}>Auto-detected performance bottlenecks and resource saturations during the spike windows.</p>

                      <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "20px" }}>
                        {[
                          { severity: "CRITICAL", confidence: "94%", title: "Thread Pool Starvation on Ingestion Router", cause: "Available worker thread execution pool saturated under 2,000 parallel requests.", evidence: "HTTP 503 Gateway Timeouts spike during 2nd and 3rd peak cycles.", impact: "High latency on critical client telemetry logs APIs.", fix: "Increase gunicorn worker class threads count or provision horizontal node replica sets." },
                          { severity: "WARNING", confidence: "89%", title: "Database Write Locks Blocked State", cause: "SQLite write buffers experiencing concurrent transaction lock waits.", evidence: "Locks count increased to 41, adding 120ms average query validation overhead.", impact: "Slower user authentication updates.", fix: "Migrate backend workspace storage tables to PostgreSQL clusters." }
                        ].map((issue, idx) => (
                          <div key={idx} style={{ border: "1px solid #e2e8f0", padding: "16px", borderRadius: "12px", background: issue.severity === "CRITICAL" ? "#fff5f5" : "#fffbeb" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px", fontSize: "12px" }}>
                              <span style={{ background: issue.severity === "CRITICAL" ? "#ef4444" : "#f59e0b", color: "#fff", padding: "2px 6px", borderRadius: "6px", fontWeight: "bold" }}>{issue.severity}</span>
                              <span style={{ color: "#64748b" }}>Confidence: {issue.confidence}</span>
                            </div>
                            <h5 style={{ margin: "0 0 6px 0", fontSize: "14px", color: "#1e293b" }}>{issue.title}</h5>
                            <p style={{ margin: "0 0 10px 0", fontSize: "12px", color: "#475569" }}><strong>Root Cause:</strong> {issue.cause}</p>
                            <div style={{ fontSize: "11px", color: "#64748b", borderTop: "1.5px dashed #cbd5e1", paddingTop: "8px" }}>
                              • <strong>Evidence:</strong> {issue.evidence}<br />
                              • <strong>Remediation:</strong> {issue.fix}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <button className="pe-btn-secondary" onClick={() => setSpikeStep(8)}>Back</button>
                        <button className="pe-btn-primary" onClick={() => setSpikeStep(10)}>Continue to Recovery Validation →</button>
                      </div>
                    </div>
                  )}

                  {/* STEP 10: RECOVERY VALIDATION */}
                  {spikeStep === 10 && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "24px", borderRadius: "16px" }}>
                      <h4 style={{ margin: "0 0 10px 0", color: "#0f172a" }}>✅ Step 10 - Recovery Validation Check</h4>
                      <p style={{ margin: "0 0 20px 0", color: "#64748b", fontSize: "13px" }}>Auto-assesses application health status after traffic spikes return to normal baseline.</p>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "20px" }}>
                        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "20px", borderRadius: "16px" }}>
                          <div style={{ fontSize: "28px", fontWeight: "bold", color: "#16a34a" }}>Successful</div>
                          <div style={{ fontSize: "12px", color: "#15803d", marginTop: "4px" }}>System fully recovered within limits.</div>

                          <div style={{ borderTop: "1.5px solid #bbf7d0", paddingTop: "10px", marginTop: "15px", fontSize: "13px" }}>
                            Recovery completed in: <strong style={{ fontSize: "16px", color: "#16a34a" }}>42 Seconds</strong>
                          </div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", fontSize: "12px" }}>
                          {[
                            { name: "Application Available", status: "Successful ✅" },
                            { name: "Login Working", status: "Successful ✅" },
                            { name: "APIs Responding", status: "Successful ✅" },
                            { name: "Database Healthy", status: "Successful ✅" },
                            { name: "CPU Returned to Normal", status: "Successful ✅" },
                            { name: "Memory Released", status: "Successful ✅" },
                            { name: "Thread Count Stable", status: "Successful ✅" },
                            { name: "Queue Cleared", status: "Successful ✅" }
                          ].map((chk, idx) => (
                            <div key={idx} style={{ background: "#f8fafc", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                              <span style={{ color: "#64748b", display: "block", marginBottom: "2px" }}>{chk.name}</span>
                              <strong style={{ color: "#16a34a" }}>{chk.status}</strong>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <button className="pe-btn-secondary" onClick={() => setSpikeStep(9)}>Back</button>
                        <button className="pe-btn-primary" onClick={() => setSpikeStep(11)}>Generate Executive Report →</button>
                      </div>
                    </div>
                  )}

                  {/* STEP 11: EXECUTIVE REPORT */}
                  {spikeStep === 11 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

                      {/* Executive Report Card */}
                      <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "30px", borderRadius: "16px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #f1f5f9", paddingBottom: "20px", marginBottom: "20px" }}>
                          <div>
                            <span style={{ background: "#22c55e", color: "#ffffff", padding: "3px 8px", borderRadius: "6px", fontSize: "10px", fontWeight: "bold" }}>APPROVED ✅</span>
                            <h3 style={{ margin: "6px 0 0 0", color: "#0f172a", fontSize: "20px" }}>📋 Executive Performance Audit Report (Spike)</h3>
                            <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: "13px" }}>Target: {spikeForm.baseUrl} | Run ID: <strong>PERF-SPIKE-RUN</strong></p>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#22c55e" }}>A+ Grade</div>
                            <div style={{ fontSize: "11px", color: "#94a3b8" }}>Health Score: 94/100</div>
                          </div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "24px" }}>
                          <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                            <span style={{ fontSize: "11px", color: "#64748b", display: "block", marginBottom: "4px" }}>Normal Users</span>
                            <strong style={{ fontSize: "16px", color: "#0f172a" }}>{spikePattern.normalUsers} VUs</strong>
                          </div>
                          <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                            <span style={{ fontSize: "11px", color: "#64748b", display: "block", marginBottom: "4px" }}>Peak Users</span>
                            <strong style={{ fontSize: "16px", color: "#ef4444" }}>{spikePattern.spikeUsers} VUs</strong>
                          </div>
                          <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                            <span style={{ fontSize: "11px", color: "#64748b", display: "block", marginBottom: "4px" }}>Avg Latency</span>
                            <strong style={{ fontSize: "16px", color: "#16a34a" }}>210 ms</strong>
                          </div>
                          <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                            <span style={{ fontSize: "11px", color: "#64748b", display: "block", marginBottom: "4px" }}>Avg Recovery Time</span>
                            <strong style={{ fontSize: "16px", color: "FD6101" }}>42 Seconds</strong>
                          </div>
                        </div>

                        {/* AI Root Cause Analysis explanation */}
                        <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", padding: "20px", borderRadius: "12px", marginBottom: "24px" }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#0f172a", fontSize: "14px" }}>🤖 AI Quality Explanation</h4>
                          <p style={{ margin: 0, fontSize: "12px", color: "#475569", lineHeight: "1.6" }}>
                            During the 2nd and 3rd waves of the 2,000 user spike, <strong>API response times increased</strong> to 540ms (up from baseline 180ms) due to thread saturation on the FastAPI ingestion worker threads.
                            Autoscaling groups provisioned 2 additional Kubernetes pods within 180s, which successfully stabilized the load.
                            Connection pools on sqlite storage experienced locks contention. Caching on dashboard queries remained highly effective.
                          </p>
                        </div>

                        {/* AI Optimization Recommendations Grid */}
                        <h4 style={{ margin: "0 0 12px 0", color: "#0f172a", fontSize: "14px" }}>💡 AI Optimization Recommendations</h4>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>
                          {[
                            { title: "Enable Redis Cache", diff: "Easy", imp: "+45%", desc: "Introduce key-value caching layer to bypass DB write read cycles." },
                            { title: "Optimize DB Queries", diff: "Medium", imp: "+35%", desc: "Add compound indexes on workspace tenant tables." },
                            { title: "Increase Connection Pool Size", diff: "Easy", imp: "+25%", desc: "Config db pool size dynamically to 50 active socket targets." },
                            { title: "Add Horizontal Scaling Pods", diff: "Medium", imp: "+55%", desc: "Modify HPA templates to scale out pods at 70% CPU usage." }
                          ].map((rec, i) => (
                            <div key={i} style={{ border: "1px solid #e2e8f0", padding: "16px", borderRadius: "12px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "12px" }}>
                                <strong style={{ color: "#FD6101" }}>{rec.title}</strong>
                                <span style={{ color: "#16a34a" }}>{rec.imp}</span>
                              </div>
                              <p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#64748b" }}>{rec.desc}</p>
                              <div style={{ fontSize: "11px", color: "#475569" }}>Effort: <strong>{rec.diff}</strong></div>
                            </div>
                          ))}
                        </div>

                        {/* Enterprise Scorecard table */}
                        <h4 style={{ margin: "0 0 12px 0", color: "#0f172a", fontSize: "14px" }}>📊 Enterprise Performance Scorecard</h4>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", textAlign: "left", marginBottom: "20px" }}>
                          <thead>
                            <tr style={{ background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0" }}>
                              <th style={{ padding: "10px" }}>Category</th>
                              <th style={{ padding: "10px", textAlign: "center" }}>Score</th>
                              <th style={{ padding: "10px", textAlign: "center" }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[
                              { name: "Spike Execution", score: 96, status: "✅" },
                              { name: "Response Time", score: 92, status: "✅" },
                              { name: "Throughput", score: 91, status: "✅" },
                              { name: "Error Rate", score: 95, status: "✅" },
                              { name: "API Performance", score: 90, status: "✅" },
                              { name: "Database Performance", score: 89, status: "🟡" },
                              { name: "Infrastructure", score: 91, status: "✅" },
                              { name: "Recovery Validation", score: 97, status: "✅" },
                              { name: "Stability", score: 94, status: "✅" },
                              { name: "AI Analysis", score: 98, status: "✅" }
                            ].map((row, idx) => (
                              <tr key={idx} style={{ borderBottom: "1px solid #e2e8f0" }}>
                                <td style={{ padding: "10px", fontWeight: "500" }}>{row.name}</td>
                                <td style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: row.score >= 90 ? "#16a34a" : "#ca8a04" }}>{row.score}/100</td>
                                <td style={{ padding: "10px", textAlign: "center" }}>{row.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button
                          className="pe-btn-secondary"
                          onClick={() => {
                            setSpikeStep(1);
                            setSpikeElapsed(0);
                            setSpikeLogs([]);
                          }}
                        >
                          🔄 Start Another Spike Analysis
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {creationMode === "single" && selectedTestType !== "spike" && (
            <div>
              {/* STEP 1: CHOOSE PERFORMANCE PROFILE (If wizardStep === 1) */}
              {wizardStep === 1 && (
                <div>
                  <h3 style={{ margin: "0 0 20px 0" }}>Choose Performance Profile</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
                    {[
                      { id: "stress", icon: "🔥", title: "Stress Test", desc: "Find breakpoint and failure behavior beyond scaling limits" },
                      { id: "spike", icon: "⚡", title: "Spike Test", desc: "Assess sudden massive traffic spikes and system recovery rate" }
                    ].map(item => (
                      <div
                        key={item.id}
                        className={`type-card ${selectedTestType === item.id ? "active" : ""}`}
                        onClick={() => setSelectedTestType(item.id)}
                      >
                        <div style={{ fontSize: "24px", marginBottom: "8px" }}>{item.icon}</div>
                        <h4 style={{ margin: "0 0 6px 0", color: "#0f172a" }}>{item.title}</h4>
                        <p style={{ margin: 0, fontSize: "12px", color: "#64748b", lineHeight: "1.4" }}>{item.desc}</p>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: "30px", display: "flex", justifyContent: "flex-end" }}>
                    <button className="pe-btn-primary" onClick={() => { setWizardStep(2); setStressStep(1); }}>
                      Continue to Config →
                    </button>
                  </div>
                </div>
              )}

              {/* STRESS TESTING MODULE (When wizardStep >= 2 and selectedTestType === 'stress') */}
              {wizardStep >= 2 && selectedTestType === "stress" && (
                <StressTestingWizard setWizardStep={setWizardStep} setSelectedTestType={setSelectedTestType} />
              )}
            </div>
          )}

          {/* ==================== CREATION MODE: ENTIRE USER JOURNEY WIZARD ==================== */}
          {creationMode === "journey" && (
            <div>
              {/* Journey Breadcrumbs (Steps 1 to 6) */}
              <div style={{ display: "flex", gap: "15px", marginBottom: "30px", borderBottom: "1.5px solid #f1f5f9", paddingBottom: "16px", overflowX: "auto" }}>
                {[
                  { step: 1, name: "1. Configuration" },
                  { step: 2, name: "2. Selection" },
                  { step: 3, name: "3. AI Discovery" },
                  { step: 4, name: "4. Journey Map" },
                  { step: 5, name: "5. Execution" },
                  { step: 6, name: "6. Report Dashboard" },
                  { step: 7, name: "7. Saved Previous Reports" }
                ].map(s => (
                  <div
                    key={s.step}
                    style={{
                      fontWeight: journeyStep === s.step ? "700" : "500",
                      color: journeyStep === s.step ? "#FD6101" : "#64748b",
                      borderBottom: journeyStep === s.step ? "3px solid #FD6101" : "3px solid transparent",
                      paddingBottom: "8px",
                      cursor: (s.step === 7 || journeyStep >= s.step || journeyStep === 6) ? "pointer" : "not-allowed",
                      whiteSpace: "nowrap"
                    }}
                    onClick={() => {
                      if (s.step === 7 || journeyStep >= s.step || journeyStep === 6) {
                        setJourneyStep(s.step);
                      }
                    }}
                  >
                    {s.name}
                  </div>
                ))}
              </div>

              {/* JOURNEY STEP 1: CONFIGURATION */}
              {journeyStep === 1 && (
                <div style={{ maxWidth: "800px", margin: "0 auto" }}>
                  <div style={{ background: "#ffffff", padding: "30px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                    <h3 style={{ margin: "0 0 20px 0", display: "flex", alignItems: "center", gap: "8px", color: "#0f172a" }}>
                      ⚙️ Configure Journey Analyzer
                    </h3>

                    <div style={{ marginBottom: "20px" }}>
                      <label className="pe-label">Application URL</label>
                      <input
                        type="text"
                        className="pe-input"
                        value={journeyForm.url}
                        onChange={e => {
                          setJourneyForm({ ...journeyForm, url: e.target.value });
                          setSelectedMapNode(null);
                        }}
                        placeholder="e.g. http://localhost:3000/#/dashboard"
                      />
                    </div>

                    <div className="pe-grid-2" style={{ marginBottom: "20px" }}>
                      <div>
                        <label className="pe-label">Login Username</label>
                        <input
                          type="text"
                          className="pe-input"
                          placeholder="e.g. admin@Qintra.com"
                          value={journeyForm.username}
                          onChange={e => setJourneyForm({ ...journeyForm, username: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="pe-label">Login Password</label>
                        <input
                          type="password"
                          className="pe-input"
                          placeholder="••••••••"
                          value={journeyForm.password}
                          onChange={e => setJourneyForm({ ...journeyForm, password: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="pe-grid-2" style={{ marginBottom: "20px" }}>
                      <div>
                        <label className="pe-label">Browser</label>
                        <select className="pe-input" value={journeyForm.browser} onChange={e => setJourneyForm({ ...journeyForm, browser: e.target.value })}>
                          <option>Chrome</option>
                          <option>Edge</option>
                          <option>Firefox</option>
                          <option>Safari</option>
                        </select>
                      </div>
                      <div>
                        <label className="pe-label">Device Profile</label>
                        <select className="pe-input" value={journeyForm.device} onChange={e => setJourneyForm({ ...journeyForm, device: e.target.value })}>
                          <option>Desktop</option>
                          <option>Tablet</option>
                          <option>Mobile</option>
                        </select>
                      </div>
                    </div>



                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <button
                        className="pe-btn-secondary"
                        onClick={() => {
                          setCreationMode("single");
                          setWizardStep(1);
                          setSelectedTestType("stress");
                        }}
                        style={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px 24px" }}
                      >
                        ← Back
                      </button>
                      <button
                        className="pe-btn-primary"
                        onClick={() => setJourneyStep(2)}
                        style={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px 24px" }}
                      >
                        Continue to Journey Selection →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* JOURNEY STEP 2: SELECTION */}
              {journeyStep === 2 && (
                <div>
                  <h3 style={{ margin: "0 0 24px 0", textAlign: "center", color: "#0f172a" }}>Select Audit Journey Flow</h3>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "24px", marginBottom: "35px" }}>
                    {/* Card 1: AI Auto Discover */}
                    <div
                      onClick={() => setSelectedJourneyType("auto")}
                      style={{
                        background: "#fff",
                        border: selectedJourneyType === "auto" ? "2.5px solid #FD6101" : "1px solid #e2e8f0",
                        borderRadius: "16px",
                        padding: "24px",
                        cursor: "pointer",
                        position: "relative",
                        transition: "all 0.2s"
                      }}
                    >
                      <span style={{ position: "absolute", top: "16px", right: "16px", background: "#FD6101", color: "#fff", padding: "2px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "bold" }}>
                        Recommended
                      </span>
                      <div style={{ fontSize: "36px", marginBottom: "16px" }}>🤖</div>
                      <h4 style={{ margin: "0 0 10px 0", fontSize: "18px", color: "#0f172a" }}>AI Auto Discover</h4>
                      <p style={{ margin: "0 0 16px 0", fontSize: "13px", color: "#64748b", lineHeight: "1.5" }}>
                        Qintra automatically logs in, discovers application routes, menus, forms, and business flows.
                      </p>
                      <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "8px", fontSize: "12px", color: "#475569" }}>
                        ⏱️ Estimated Time: <strong>5-10 minutes</strong>
                      </div>
                    </div>

                    {/* Card 2: Recorded User Journey */}
                    <div
                      onClick={() => setSelectedJourneyType("recorded")}
                      style={{
                        background: "#fff",
                        border: selectedJourneyType === "recorded" ? "2.5px solid #FD6101" : "1px solid #e2e8f0",
                        borderRadius: "16px",
                        padding: "24px",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                    >
                      <div style={{ fontSize: "36px", marginBottom: "16px" }}>📹</div>
                      <h4 style={{ margin: "0 0 10px 0", fontSize: "18px", color: "#0f172a" }}>Recorded User Journey</h4>
                      <p style={{ margin: "0 0 16px 0", fontSize: "13px", color: "#64748b", lineHeight: "1.5" }}>
                        Reuse an existing recorded flow from Playwright, Selenium, Cypress, or Qintra Recorder.
                      </p>
                      <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "8px", fontSize: "12px", color: "#475569" }}>
                        ⚙️ Import formats: <strong>JSON, JS, Spec.ts</strong>
                      </div>
                    </div>

                    {/* Card 3: Manual Journey */}
                    <div
                      onClick={() => setSelectedJourneyType("manual")}
                      style={{
                        background: "#fff",
                        border: selectedJourneyType === "manual" ? "2.5px solid #FD6101" : "1px solid #e2e8f0",
                        borderRadius: "16px",
                        padding: "24px",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                    >
                      <div style={{ fontSize: "36px", marginBottom: "16px" }}>✍️</div>
                      <h4 style={{ margin: "0 0 10px 0", fontSize: "18px", color: "#0f172a" }}>Manual Journey</h4>
                      <p style={{ margin: "0 0 16px 0", fontSize: "13px", color: "#64748b", lineHeight: "1.5" }}>
                        Manually list the target URLs and API paths to build a customized business flow.
                      </p>
                      <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "8px", fontSize: "12px", color: "#475569" }}>
                        🔧 Configuration: <strong>Step-by-step editor</strong>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <button className="pe-btn-secondary" onClick={() => setJourneyStep(1)}>
                      Back
                    </button>
                    <button
                      className="pe-btn-primary"
                      onClick={() => {
                        try {
                          if (journeyForm.url) {
                            window.open(journeyForm.url.startsWith("http") ? journeyForm.url : "https://" + journeyForm.url, "_blank");
                          }
                        } catch (err) {
                          console.error("Failed to open URL", err);
                        }
                        setSelectedMapNode(null);
                        setJourneyStep(3);
                        setIsDiscovering(true);
                      }}
                    >
                      Launch AI Discovery Process →
                    </button>
                  </div>
                </div>
              )}

              {/* JOURNEY STEP 3: AI DISCOVERY PROGRESS */}
              {journeyStep === 3 && (
                <div style={{ maxWidth: "700px", margin: "0 auto" }}>
                  <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "35px", textAlign: "center", boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                    <h3 style={{ margin: "0 0 10px 0", color: "#0f172a" }}>🤖 Discovering Business Journeys</h3>
                    <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "30px" }}>
                      Qintra agent is logging into {journeyForm.url} and crawling flows...
                    </p>

                    {/* Progress Circle & Text */}
                    <div style={{ position: "relative", width: "160px", height: "160px", margin: "0 auto 30px auto" }}>
                      <svg width="160" height="160" viewBox="0 0 160 160">
                        <circle cx="80" cy="80" r="70" fill="transparent" stroke="#f1f5f9" strokeWidth="8" />
                        <circle
                          cx="80" cy="80" r="70" fill="transparent" stroke="#FD6101" strokeWidth="8"
                          strokeDasharray={`${2 * Math.PI * 70}`}
                          strokeDashoffset={`${2 * Math.PI * 70 * (1 - discoveryPercent / 100)}`}
                          strokeLinecap="round"
                          style={{ transition: "stroke-dashoffset 0.4s", transform: "rotate(-90deg)", transformOrigin: "80px 80px" }}
                        />
                      </svg>
                      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                        <span style={{ fontSize: "28px", fontWeight: "700", color: "#0f172a" }}>{discoveryPercent}%</span>
                        <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", fontWeight: "600", marginTop: "2px" }}>Progress</div>
                      </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-around", marginBottom: "25px", borderBottom: "1px solid #f1f5f9", paddingBottom: "16px" }}>
                      <div>
                        <div style={{ fontSize: "20px", fontWeight: "bold", color: "#FD6101" }}>{journeyData.screens.length}</div>
                        <div style={{ fontSize: "12px", color: "#64748b" }}>Screens Cataloged</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "20px", fontWeight: "bold", color: "#FD6101" }}>{journeyData.screens.reduce((acc, s) => acc + s.apis, 0)}</div>
                        <div style={{ fontSize: "12px", color: "#64748b" }}>APIs Intercepted</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "20px", fontWeight: "bold", color: "#FD6101" }}>{journeyData.screens.length * 40}</div>
                        <div style={{ fontSize: "12px", color: "#64748b" }}>Resources Checked</div>
                      </div>
                    </div>

                    {/* Scanner Terminal Logs */}
                    <div style={{ background: "#0f172a", color: "#38bdf8", padding: "16px", borderRadius: "10px", textAlign: "left", fontFamily: "monospace", fontSize: "12px", height: "160px", overflowY: "auto", marginBottom: "24px" }}>
                      {discoveryLog.map((log, index) => (
                        <div key={index} style={{ marginBottom: "6px" }}>{log}</div>
                      ))}
                      {isDiscovering && <div style={{ color: "#a855f7", animation: "pulse 1.5s infinite" }}>⚡ AI Agent Crawling Pages...</div>}
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "13px", color: "#64748b" }}>
                        ⏱️ Est. Remaining: <strong>2 Minutes</strong>
                      </span>
                      <button
                        className="pe-btn-secondary"
                        onClick={() => {
                          setIsDiscovering(false);
                          setDiscoveryPercent(100);
                          setJourneyStep(4);
                        }}
                      >
                        Bypass to Map →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* JOURNEY STEP 4: JOURNEY MAP */}
              {journeyStep === 4 && (
                <div>
                  <h3 style={{ margin: "0 0 10px 0", color: "#0f172a" }}>🗺️ AI Discovered Journey Flow Map</h3>
                  <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "24px" }}>
                    Click any screen node card below to verify cataloged page parameters and resource endpoints.
                  </p>

                  {/* Horizontal flowchart */}
                  <div style={{ display: "flex", gap: "12px", alignItems: "center", overflowX: "auto", padding: "20px 10px", background: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0", marginBottom: "30px" }}>
                    {journeyData.screens.map((node, index, arr) => (
                      <React.Fragment key={node.id}>
                        <div
                          onClick={() => setSelectedMapNode(node)}
                          style={{
                            background: "#ffffff",
                            border: selectedMapNode?.id === node.id ? "2.5px solid #FD6101" : "1px solid #e2e8f0",
                            borderRadius: "12px",
                            padding: "16px",
                            minWidth: "165px",
                            cursor: "pointer",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                            transition: "all 0.2s"
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                            <span style={{ fontSize: "20px" }}>{node.icon}</span>
                            <span style={{
                              fontSize: "11px", fontWeight: "bold", padding: "2px 6px", borderRadius: "10px",
                              background: node.status === "Excellent" ? "#ecfdf5" : node.status === "Good" ? "#eff6ff" : node.status === "Warning" ? "#fffbeb" : "#fef2f2",
                              color: node.status === "Excellent" ? "#10b981" : node.status === "Good" ? "#3b82f6" : node.status === "Warning" ? "#f59e0b" : "#ef4444"
                            }}>{node.status}</span>
                          </div>
                          <h5 style={{ margin: "0 0 6px 0", color: "#0f172a", fontSize: "14px" }}>{node.name}</h5>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>⏱️ Load: <strong>{node.time}</strong></div>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>🔌 APIs: <strong>{node.apis} calls</strong></div>
                        </div>
                        {index < arr.length - 1 && (
                          <span style={{ fontSize: "20px", color: "#94a3b8" }}>→</span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Popover Node Detail Modal */}
                  {selectedMapNode && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", marginBottom: "30px", boxShadow: "0 4px 16px rgba(0,0,0,0.03)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                        <h4 style={{ margin: 0, color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
                          {selectedMapNode.icon} Screen Parameters for: {selectedMapNode.name}
                        </h4>
                        <button style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontWeight: "bold" }} onClick={() => setSelectedMapNode(null)}>✕ Close</button>
                      </div>

                      <div className="pe-grid-3" style={{ fontSize: "13px", color: "#475569" }}>
                        <div>• Total Resource Payload: <strong>{selectedMapNode.size}</strong></div>
                        <div>• DOM Content Loaded: <strong>{(parseFloat(selectedMapNode.time) * 0.7).toFixed(1)}s</strong></div>
                        <div>• First Contentful Paint: <strong>{(parseFloat(selectedMapNode.time) * 0.4).toFixed(1)}s</strong></div>
                        <div>• First Input Delay: <strong>{Math.floor(20 + Math.random() * 50)}ms</strong></div>
                        <div>• Time to First Byte: <strong>{Math.floor(120 + Math.random() * 150)}ms</strong></div>
                        <div>• SLA Requirement: <strong>&lt; 3.0s</strong></div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <button className="pe-btn-secondary" onClick={() => setJourneyStep(2)}>
                      Back to Selection
                    </button>
                    <button
                      className="pe-btn-primary"
                      onClick={() => {
                        setJourneyStep(5);
                        setIsRunningAnalysis(true);
                      }}
                    >
                      Continue to Execute Analysis →
                    </button>
                  </div>
                </div>
              )}

              {/* JOURNEY STEP 5: EXECUTE PERFORMANCE ANALYSIS */}
              {journeyStep === 5 && (
                <div style={{ maxWidth: "800px", margin: "0 auto" }}>
                  <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "30px", boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                      <div>
                        <h3 style={{ margin: 0, color: "#0f172a" }}>🚀 Executing Journey Performance Run</h3>
                        <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: "13px" }}>Measuring page loading times and database endpoints</p>
                      </div>
                      <span style={{ background: "rgba(124, 58, 237, 0.1)", color: "#FD6101", padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: "bold", animation: "pulse 1.5s infinite" }}>
                        RUNNING
                      </span>
                    </div>

                    {/* Progress details */}
                    <div className="pe-grid-3" style={{ marginBottom: "24px" }}>
                      <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", textAlign: "center" }}>
                        <div style={{ fontSize: "12px", color: "#64748b" }}>Elapsed Time</div>
                        <div style={{ fontSize: "22px", fontWeight: "700", color: "#0f172a", marginTop: "4px" }}>{runElapsed}s</div>
                      </div>
                      <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", textAlign: "center" }}>
                        <div style={{ fontSize: "12px", color: "#64748b" }}>Current Screen</div>
                        <div style={{ fontSize: "18px", fontWeight: "700", color: "#FD6101", marginTop: "6px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {journeyData.screens[activeScreenIndex]?.name || "Home Page"}
                        </div>
                      </div>
                      <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", textAlign: "center" }}>
                        <div style={{ fontSize: "12px", color: "#64748b" }}>Overall Journey Health</div>
                        <div style={{ fontSize: "22px", fontWeight: "700", color: "#10b981", marginTop: "4px" }}>92/100</div>
                      </div>
                    </div>

                    <div style={{ marginBottom: "24px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#475569", marginBottom: "8px" }}>
                        <span>Progress status: Auditing screen {activeScreenIndex + 1} of {journeyData.screens.length}</span>
                        <span>{Math.round(runPercent)}% Complete</span>
                      </div>
                      <div style={{ height: "8px", background: "#f1f5f9", borderRadius: "4px", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${runPercent}%`, background: "#FD6101", borderRadius: "4px", transition: "width 0.3s" }}></div>
                      </div>
                    </div>

                    {/* Live AI Audit Checklist */}
                    <div style={{ marginBottom: "20px", background: "#f8fafc", border: "1px solid #e2e8f0", padding: "16px", borderRadius: "12px" }}>
                      <strong style={{ fontSize: "12px", color: "#475569", display: "block", marginBottom: "10px" }}>🛡️ Master Performance Engineering Audit Checklist</strong>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px", fontSize: "11px" }}>
                        {[
                          "Frontend Performance Audit", "User Journey Performance Audit", "API Performance Audit",
                          "Database Performance Audit", "Server Performance Audit", "Network Performance Audit",
                          "Load Testing Audit", "Stress Testing Audit", "Spike Testing Audit",
                          "Endurance (Soak) Testing", "Volume Testing Audit", "Scalability Audit"
                        ].map((auditName, idx) => {
                          const isChecking = activeScreenIndex === idx % 4;
                          const isDone = activeScreenIndex > idx % 4;
                          return (
                            <div key={idx} style={{ display: "flex", alignItems: "center", gap: "8px", color: isDone ? "#10b981" : isChecking ? "#FD6101" : "#94a3b8", fontWeight: isChecking ? "bold" : "normal" }}>
                              <span>{isDone ? "✅" : isChecking ? "⏳" : "⚫"}</span>
                              <span>{auditName}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Live Metrics Terminal Grid */}
                    <h4 style={{ color: "#0f172a", fontSize: "14px", margin: "0 0 12px 0" }}>📡 Real-time Client Telemetry</h4>
                    <div style={{ background: "#0f172a", color: "#38bdf8", padding: "20px", borderRadius: "12px", fontFamily: "monospace", fontSize: "12px", lineHeight: "1.6", marginBottom: "24px" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                        <div>• Active TCP connections: <span style={{ color: "#fff" }}>{12 + Math.floor(Math.random() * 8)}</span></div>
                        <div>• Memory footprint: <span style={{ color: "#fff" }}>{142 + Math.floor(Math.random() * 20)} MB</span></div>
                        <div>• Latency overhead: <span style={{ color: "#f59e0b" }}>{24 + Math.floor(Math.random() * 5)} ms</span></div>
                        <div>• Client CPU load: <span style={{ color: "#fff" }}>{18 + Math.floor(Math.random() * 15)}%</span></div>
                        <div>• Network speed (sim): <span style={{ color: "#10b981" }}>45.2 Mbps</span></div>
                        <div>• JS runtime execution: <span style={{ color: "#fff" }}>{120 + Math.floor(Math.random() * 15)} ms</span></div>
                        <div>• DOM Load complete: <span style={{ color: "#10b981" }}>Yes</span></div>
                        <div>• Decoded CSS layouts: <span style={{ color: "#fff" }}>41 files</span></div>
                      </div>
                      <div style={{ marginTop: "12px", borderTop: "1px dashed rgba(255,255,255,0.1)", paddingTop: "10px", color: "#a855f7" }}>
                        → INTERCEPTING API: {journeyData.apis[activeScreenIndex] || "/api/v1/init"}
                      </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <button
                        className="pe-btn-primary"
                        onClick={() => {
                          setIsRunningAnalysis(false);
                          setRunPercent(100);
                          setJourneyStep(6);
                        }}
                      >
                        Bypass to Report Dashboard →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* JOURNEY STEP 6: JOURNEY REPORT DASHBOARD */}
              {journeyStep === 6 && (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                    <div>
                      <h3 style={{ margin: 0, color: "#0f172a", fontSize: "22px" }}>📋 Executive Performance Audit Report</h3>
                      <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: "13px" }}>
                        Target Journey Flow: {journeyForm.url} {journeyData?.run_id && `| Run ID: ${journeyData.run_id}`}
                      </p>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: "flex", gap: "10px" }}>
                      <button className="pe-btn-secondary" style={{ fontSize: "12px", padding: "6px 12px" }} onClick={() => alert("PDF report generated and saved to file system.")}>Download PDF</button>
                      <button
                        className="pe-btn-secondary"
                        style={{ fontSize: "12px", padding: "6px 12px" }}
                        onClick={() => {
                          if (journeyData?.json_report_url) {
                            window.open(`${API_BASE}${journeyData.json_report_url}`, "_blank");
                          } else {
                            alert("Report JSON is generating. Please try again.");
                          }
                        }}
                      >
                        Download JSON
                      </button>
                      <button
                        className="pe-btn-secondary"
                        style={{ fontSize: "12px", padding: "6px 12px" }}
                        onClick={() => {
                          if (journeyData?.html_report_url) {
                            window.open(`${API_BASE}${journeyData.html_report_url}`, "_blank");
                          } else {
                            alert("Report HTML is generating. Please try again.");
                          }
                        }}
                      >
                        Download HTML
                      </button>
                      <button className="pe-btn-primary" style={{ fontSize: "12px", padding: "6px 12px" }} onClick={() => {
                        navigator.clipboard.writeText(`${API_BASE}${journeyData?.html_report_url || ""}`);
                        alert("Report share link copied to clipboard!");
                      }}>🔗 Share</button>
                    </div>
                  </div>

                  {/* Tabs container */}
                  <div style={{ display: "flex", borderBottom: "1.5px solid #e2e8f0", marginBottom: "24px", gap: "8px", overflowX: "auto" }}>
                    {[
                      { id: "screens", label: "🖥️ Screen Performance" },
                      { id: "overview", label: "📊 Overview" }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setSelectedReportTab(tab.id)}
                        style={{
                          border: "none",
                          padding: "10px 16px",
                          background: "transparent",
                          fontWeight: selectedReportTab === tab.id ? "700" : "500",
                          color: selectedReportTab === tab.id ? "#FD6101" : "#64748b",
                          borderBottom: selectedReportTab === tab.id ? "3px solid #FD6101" : "3px solid transparent",
                          cursor: "pointer",
                          fontSize: "13px",
                          transition: "all 0.2s",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* REPORT TAB 1: OVERVIEW */}
                  {selectedReportTab === "overview" && (
                    <div>
                      {/* Overall score & Grade card */}
                      <div className="pe-grid-3" style={{ marginBottom: "24px" }}>
                        <div style={{ background: "#ffffff", padding: "20px", borderRadius: "16px", border: "1px solid #e2e8f0", display: "flex", gap: "16px", alignItems: "center" }}>
                          <div style={{
                            width: "80px", height: "80px", borderRadius: "50%", background: "#10b981",
                            display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "32px", fontWeight: "bold"
                          }}>
                            A+
                          </div>
                          <div>
                            <div style={{ fontSize: "12px", color: "#64748b" }}>Overall Journey Score</div>
                            <div style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginTop: "2px" }}>96 / 100</div>
                          </div>
                        </div>

                        <div style={{ background: "#ffffff", padding: "20px", borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>Total Journey Duration</div>
                          <div style={{ fontSize: "24px", fontWeight: "700", color: "#0f172a", marginTop: "6px" }}>21.1 seconds</div>
                          <div style={{ fontSize: "11px", color: "#10b981", marginTop: "4px" }}>✓ Passes 30s business SLA</div>
                        </div>

                        <div style={{ background: "#ffffff", padding: "20px", borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>Success Rate & SLAs</div>
                          <div style={{ fontSize: "24px", fontWeight: "700", color: "#0f172a", marginTop: "6px" }}>98.2%</div>
                          <div style={{ fontSize: "11px", color: "#10b981", marginTop: "4px" }}>✓ 214 of 218 queries succeeded</div>
                        </div>
                      </div>

                      {/* Performance Indicators grid */}
                      <div className="pe-grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "24px" }}>
                        <div style={{ background: "#fff", border: "1px solid #e2e8f0", padding: "16px", borderRadius: "12px", textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>Performance Score</div>
                          <div style={{ fontSize: "20px", fontWeight: "700", color: "#FD6101", marginTop: "4px" }}>92/100</div>
                        </div>
                        <div style={{ background: "#fff", border: "1px solid #e2e8f0", padding: "16px", borderRadius: "12px", textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>Accessibility</div>
                          <div style={{ fontSize: "20px", fontWeight: "700", color: "#10b981", marginTop: "4px" }}>98/100</div>
                        </div>
                        <div style={{ background: "#fff", border: "1px solid #e2e8f0", padding: "16px", borderRadius: "12px", textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>SEO Audit</div>
                          <div style={{ fontSize: "20px", fontWeight: "700", color: "#3b82f6", marginTop: "4px" }}>95/100</div>
                        </div>
                        <div style={{ background: "#fff", border: "1px solid #e2e8f0", padding: "16px", borderRadius: "12px", textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>Best Practices</div>
                          <div style={{ fontSize: "20px", fontWeight: "700", color: "#10b981", marginTop: "4px" }}>100/100</div>
                        </div>
                      </div>

                      {/* Comparison / Regression Alert */}
                      <div style={{ background: "#fffbeb", border: "1px solid #fde047", padding: "16px", borderRadius: "12px", display: "flex", gap: "12px", alignItems: "center", marginBottom: "24px" }}>
                        <span style={{ fontSize: "24px" }}>📊</span>
                        <div>
                          <strong style={{ color: "#b45309" }}>Regression Analysis against Baseline Run (July 15, 2026)</strong>
                          <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#475569" }}>
                            Reports load time increased by <strong>18% (1.1s slower)</strong> due to cold-start DB indexes. Other screens improved by 5% average.
                          </p>
                        </div>
                      </div>

                      {/* Journey timeline graph (simulated SVG) */}
                      <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "20px", borderRadius: "16px" }}>
                        <h4 style={{ margin: "0 0 16px 0", color: "#0f172a" }}>Journey Navigation & Response Time Trend</h4>
                        <div style={{ overflowX: "auto", paddingBottom: "25px", paddingLeft: "10px" }}>
                          <div style={{ height: "180px", width: `${Math.max(journeyData.screens.length * 80 + 100, 600)}px`, position: "relative", borderLeft: "1px solid #cbd5e1", borderBottom: "1px solid #cbd5e1", padding: "10px 0", marginLeft: "45px" }}>

                            {/* Y-Axis scale count markings */}
                            {[0, 1, 2, 3, 4, 5, 6, 7].map(sec => {
                              const y = 160 - (sec * 22);
                              return (
                                <div key={sec} style={{ position: "absolute", left: "-45px", top: `${y - 6}px`, fontSize: "10px", color: "#64748b", width: "35px", textAlign: "right" }}>
                                  {sec}s
                                </div>
                              );
                            })}

                            {/* Simulated Chart Line & Gridlines */}
                            <svg width="100%" height="100%" style={{ position: "absolute", top: 0, left: 0 }}>
                              {/* Horizontal Gridlines */}
                              {[0, 1, 2, 3, 4, 5, 6, 7].map(sec => {
                                const y = 160 - (sec * 22);
                                return (
                                  <line
                                    key={sec}
                                    x1="0"
                                    y1={y}
                                    x2="100%"
                                    y2={y}
                                    stroke="#f1f5f9"
                                    strokeWidth="1"
                                    strokeDasharray="3,3"
                                  />
                                );
                              })}

                              {/* Current Run Line */}
                              <path
                                d={journeyData.screens.map((s, idx) => {
                                  const x = 50 + (idx * 80);
                                  const loadSec = parseFloat(s.time);
                                  const y = Math.max(160 - (loadSec * 22), 10);
                                  return `${idx === 0 ? "M" : "L"} ${x} ${y}`;
                                }).join(" ")}
                                fill="none" stroke="#FD6101" strokeWidth="3"
                              />
                              {/* Baseline Dash Path */}
                              <path
                                d={journeyData.screens.map((s, idx) => {
                                  const x = 50 + (idx * 80);
                                  const loadSec = parseFloat(s.time);
                                  const y = Math.max(165 - (loadSec * 24), 15);
                                  return `${idx === 0 ? "M" : "L"} ${x} ${y}`;
                                }).join(" ")}
                                fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="5,5"
                              />

                              {/* Baseline Dots */}
                              {journeyData.screens.map((s, idx) => {
                                const x = 50 + (idx * 80);
                                const loadSec = parseFloat(s.time);
                                const y = Math.max(165 - (loadSec * 24), 15);
                                const baseSec = (loadSec * 0.95).toFixed(1);
                                return (
                                  <circle
                                    key={`base-dot-${idx}`}
                                    cx={x}
                                    cy={y}
                                    r="4"
                                    fill="#94a3b8"
                                    stroke="#ffffff"
                                    strokeWidth="1.5"
                                    style={{ cursor: "pointer" }}
                                  >
                                    <title>{`Baseline - ${s.name.includes(".") ? s.name.split(".")[1]?.trim() : s.name}: ${baseSec}s`}</title>
                                  </circle>
                                );
                              })}

                              {/* Current Run Dots */}
                              {journeyData.screens.map((s, idx) => {
                                const x = 50 + (idx * 80);
                                const loadSec = parseFloat(s.time);
                                const y = Math.max(160 - (loadSec * 22), 10);
                                const screenLabel = s.name.includes(".") ? s.name.split(".")[1]?.trim() : s.name;
                                return (
                                  <circle
                                    key={`current-dot-${idx}`}
                                    cx={x}
                                    cy={y}
                                    r="5"
                                    fill="#FD6101"
                                    stroke="#ffffff"
                                    strokeWidth="2"
                                    style={{ cursor: "pointer", transition: "r 0.1s" }}
                                    onMouseEnter={(e) => { e.target.setAttribute("r", "7"); }}
                                    onMouseLeave={(e) => { e.target.setAttribute("r", "5"); }}
                                  >
                                    <title>{`Current Run - ${screenLabel}: ${s.time}`}</title>
                                  </circle>
                                );
                              })}
                            </svg>

                            {/* X-Axis labels centered under points */}
                            {journeyData.screens.map((screen, idx) => {
                              const x = 50 + (idx * 80);
                              const labelText = screen.name.includes(".") ? screen.name.split(".")[1]?.trim() : screen.name;
                              return (
                                <div
                                  key={idx}
                                  style={{
                                    position: "absolute",
                                    bottom: "-25px",
                                    left: `${x - 40}px`,
                                    width: "80px",
                                    textAlign: "center",
                                    fontSize: "10px",
                                    color: "#64748b",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                  }}
                                  title={labelText}
                                >
                                  {labelText}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "20px", marginTop: "35px", fontSize: "12px", justifyContent: "center" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ width: "12px", height: "4px", background: "#FD6101", display: "inline-block" }}></span> Current Run
                          </span>
                          <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ width: "12px", height: "4px", borderTop: "2px dashed #94a3b8", display: "inline-block" }}></span> Baseline Run (Previous)
                          </span>
                        </div>
                      </div>

                      {/* --- MASTER PERFORMANCE ENGINEERING AUDIT FRAMEWORK --- */}
                      <div style={{ marginTop: "32px", display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "24px" }}>

                        {/* Column 1: AI Audit Checklist & Interactive Categories */}
                        <div>
                          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "24px", borderRadius: "16px", height: "100%" }}>
                            <h4 style={{ margin: "0 0 8px 0", color: "#0f172a", fontSize: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                              🛡️ Master Performance Engineering Audit Framework
                            </h4>
                            <p style={{ margin: "0 0 20px 0", color: "#64748b", fontSize: "13px" }}>
                              Qintra's automated checklist evaluating frontend rendering, infrastructure limits, database bottlenecks, and cost allocations.
                            </p>

                            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                              {[
                                {
                                  id: 1,
                                  name: "1. Frontend Performance Audit",
                                  score: 94,
                                  status: "✅",
                                  checks: [
                                    "DNS Lookup Time", "TCP Connection Time", "SSL Handshake", "Redirect Time",
                                    "TTFB (Time To First Byte)", "FCP (First Contentful Paint)", "LCP (Largest Contentful Paint)",
                                    "CLS (Cumulative Layout Shift)", "INP (Interaction to Next Paint)", "DOM Content Loaded",
                                    "DOM Interactive", "Page Load Time", "Fully Loaded Time"
                                  ],
                                  findings: "FCP is fast (0.5s), but static assets can be optimized using compression.",
                                  remediation: "Enable Gzip/Brotli compression and use lazy loading for heavy scripts."
                                },
                                {
                                  id: 2,
                                  name: "2. User Journey Performance Audit",
                                  score: 92,
                                  status: "✅",
                                  checks: [
                                    "Screen Load Time", "Navigation Time", "API Time per Screen", "Rendering Time",
                                    "Business Flow Duration", "Resource Usage per Screen"
                                  ],
                                  findings: "Login page navigation to PM Dashboard completes within 2.1s.",
                                  remediation: "Prefetch dashboard layout routes during user password typing."
                                },
                                {
                                  id: 3,
                                  name: "3. API Performance Audit",
                                  score: 90,
                                  status: "✅",
                                  checks: [
                                    "Endpoint Latency", "HTTP Method Check", "Authentication Overhead", "P95 Latency (180ms)",
                                    "P99 Latency (340ms)", "Payload Size Compression", "Throughput (Requests/sec)",
                                    "Concurrent Request Capacity", "Error Rates & Status Codes"
                                  ],
                                  findings: "P95 latency is stable at 180ms. Authentication JWT validation adds minor overhead.",
                                  remediation: "Introduce API gateway caching for static dashboard analytics endpoints."
                                },
                                {
                                  id: 4,
                                  name: "4. Database Performance Audit",
                                  score: 91,
                                  status: "✅",
                                  checks: [
                                    "Query Execution Time", "Slow Queries Audit", "Missing Indexes Detection", "Index Usage",
                                    "Lock Contention & Deadlocks", "Transaction Rollbacks", "Connection Pool Saturation",
                                    "N+1 Queries Detection", "Large Table Full Scans"
                                  ],
                                  findings: "All queries resolved within 45ms. Connection pool utilization is at 35%.",
                                  remediation: "Add indexes on tenant_id column in workspace tables."
                                },
                                {
                                  id: 5,
                                  name: "5. Server Performance Audit",
                                  score: 89,
                                  status: "🟡",
                                  checks: [
                                    "Average CPU Utilization", "Peak CPU Saturation", "Memory Footprint Growth", "Heap Allocation & GC",
                                    "Thread Count & Blocking", "Process Spawning Limits"
                                  ],
                                  findings: "CPU spikes to 82% during simultaneous mock logins. Garbage Collection frequency is normal.",
                                  remediation: "Scale container memory allocations and review thread pool execution sizes."
                                },
                                {
                                  id: 6,
                                  name: "6. Network Performance Audit",
                                  score: 95,
                                  status: "✅",
                                  checks: [
                                    "DNS Lookup Speed", "TCP/TLS Negotiation", "Packet Loss Metrics", "Jitter & Latency Variance",
                                    "Bandwidth Footprint", "CDN Edge Delivery Rate"
                                  ],
                                  findings: "Edge delivery score is excellent. 98% of assets served from regional edge cache.",
                                  remediation: "No immediate remediation needed."
                                },
                                {
                                  id: 7,
                                  name: "7. Load Testing Audit",
                                  score: 93,
                                  status: "✅",
                                  checks: [
                                    "Concurrent User Scaling", "Ramp-up/Ramp-down Behavior", "Requests Per Second (RPS)",
                                    "Transactions Per Second (TPS)", "Error Rates under Load"
                                  ],
                                  findings: "System handles 5,000 concurrent user threads without error rate growth.",
                                  remediation: "Verify load balancer distribution weights."
                                },
                                {
                                  id: 8,
                                  name: "8. Stress Testing Audit",
                                  score: 88,
                                  status: "🟡",
                                  checks: [
                                    "Breaking Point Threshold", "Maximum Supported Users", "Failure Recovery Time",
                                    "Auto-recovery Triggers", "Service Availability Under stress"
                                  ],
                                  findings: "System degradation begins above 15,000 concurrent users. Web socket connection drop rates rise.",
                                  remediation: "Increase load balancer max connections and scale out socket service servers."
                                },
                                {
                                  id: 9,
                                  name: "9. Spike Testing Audit",
                                  score: 92,
                                  status: "✅",
                                  checks: [
                                    "Sudden User Increase", "Response Degradation Window", "Auto Scaling Warmup Time", "Queue Growth Limits"
                                  ],
                                  findings: "Autoscaling groups add instances within 3 minutes of load spikes.",
                                  remediation: "Pre-warm autoscaling instances based on scheduled high-volume campaigns."
                                },
                                {
                                  id: 10,
                                  name: "10. Endurance (Soak) Testing Audit",
                                  score: 90,
                                  status: "✅",
                                  checks: [
                                    "4h/8h/12h Stability Runs", "Memory Leak Accumulation", "Resource Decay Rates",
                                    "Connection Pool Exhaustion", "Thread Growth Leakage"
                                  ],
                                  findings: "No memory leaks detected over 12-hour simulation run.",
                                  remediation: "Regularly recycle background processing workers."
                                },
                                {
                                  id: 11,
                                  name: "11. Volume Testing Audit",
                                  score: 91,
                                  status: "✅",
                                  checks: [
                                    "Large Database Execution", "Million Records Query Performance", "Bulk Upload & Insert Processing",
                                    "Large Payload Performance"
                                  ],
                                  findings: "Bulk exports complete within SLA limits. DB read speeds stay below 120ms for large tables.",
                                  remediation: "Implement partitioning on audit logs and report archive tables."
                                },
                                {
                                  id: 12,
                                  name: "12. Scalability Audit",
                                  score: 89,
                                  status: "🟡",
                                  checks: [
                                    "Horizontal/Vertical Scaling Limits", "Container Orchestration Scaling", "Kubernetes Pod Auto-scaling",
                                    "Load Balancer Allocation"
                                  ],
                                  findings: "Pods scale perfectly. Nodes scale out slightly slower due to VM provision latencies.",
                                  remediation: "Optimize node startup templates to decrease VM initialization times."
                                },
                                {
                                  id: 13,
                                  name: "13. Stability Audit",
                                  score: 93,
                                  status: "✅",
                                  checks: [
                                    "Continuous Page Refresh Runs", "Multi-tab Session Persistence", "Rapid Component Navigation",
                                    "UI Memory Leak Checks"
                                  ],
                                  findings: "Browser tab memory footprint stays below 200MB during 100 navigation cycles.",
                                  remediation: "No critical improvements required."
                                },
                                {
                                  id: 14,
                                  name: "14. Infrastructure Audit",
                                  score: 90,
                                  status: "✅",
                                  checks: [
                                    "VM/Instance Resource Limits", "Kubernetes Pod Allocation", "Autoscaling Groups Health",
                                    "CDN Edge Allocation"
                                  ],
                                  findings: "All cloud VMs are utilizing correct family types. Network throughput limits are not throttled.",
                                  remediation: "Review node resource requests/limits in Helm charts."
                                },
                                {
                                  id: 15,
                                  name: "15. Observability Audit",
                                  score: 94,
                                  status: "✅",
                                  checks: [
                                    "Structured Logs Format", "Prometheus Metrics Coverage", "APM Tracing Instrumentation",
                                    "Error Logging Alerting"
                                  ],
                                  findings: "APM tracing spans are correctly sent. Log aggregation indexing completes within 10s.",
                                  remediation: "No critical observability issues."
                                },
                                {
                                  id: 16,
                                  name: "16. Security Performance Audit",
                                  score: 92,
                                  status: "✅",
                                  checks: [
                                    "TLS Handshake Latency", "JWT Validation Speed", "OAuth Overhead Time", "API Security Headers Overhead"
                                  ],
                                  findings: "TLS handshake overhead is sub-15ms. JWT encryption has minimal payload overhead.",
                                  remediation: "Ensure secure HTTP headers don't disable browser asset caching."
                                },
                                {
                                  id: 17,
                                  name: "17. Browser Performance Audit",
                                  score: 91,
                                  status: "✅",
                                  checks: [
                                    "Chrome/Edge/Firefox/Safari Compare", "JS Engine Compilation Times", "Layout Engine Render Matching"
                                  ],
                                  findings: "Consistent sub-2s rendering across all modern browser engines.",
                                  remediation: "Audit old polyfills that add processing overhead in Safari versions."
                                },
                                {
                                  id: 18,
                                  name: "18. Mobile Performance Audit",
                                  score: 88,
                                  status: "🟡",
                                  checks: [
                                    "iOS/Android Touch Delay", "Slow 3G Network Emulation", "Mobile Rendering Reflows", "Battery Usage"
                                  ],
                                  findings: "Reflow times increase on mobile viewport under slow network simulation.",
                                  remediation: "Compress bundle payloads and lazy-load high-resolution dashboard graphics."
                                },
                                {
                                  id: 19,
                                  name: "19. AI Performance Audit",
                                  score: 95,
                                  status: "✅",
                                  checks: [
                                    "Model Inference Latency", "Token Generation Speed", "Prompt Processing Overheads", "AI Retries/Timeouts"
                                  ],
                                  findings: "AI inference latency is within standard limits (1.2s average). Prompt retrieval has high confidence.",
                                  remediation: "No improvements needed."
                                },
                                {
                                  id: 20,
                                  name: "20. Cost Optimization Audit",
                                  score: 87,
                                  status: "🟡",
                                  checks: [
                                    "Compute/VM Resource Allocations", "Database Provision Limits", "CDN Cache-Control Tuning", "Storage Tiers"
                                  ],
                                  findings: "Idle VMs are adding unnecessary infrastructure cost. Database read replication can be optimized.",
                                  remediation: "Use Reserved Instances and configure dynamic database replication auto-scale limits."
                                },
                                {
                                  id: 21,
                                  name: "21. Performance Regression Audit",
                                  score: 93,
                                  status: "✅",
                                  checks: [
                                    "Comparison against Baseline Run", "Slower Pages Detection", "API Response Time Drift"
                                  ],
                                  findings: "Reports page load time drift is flagged (+1.1s). All other pages improved by 5% average.",
                                  remediation: "Prioritize index optimization on the reporting query tables."
                                },
                                {
                                  id: 22,
                                  name: "22. SLA Compliance Audit",
                                  score: 94,
                                  status: "✅",
                                  checks: [
                                    "Response Time SLA Check", "Availability SLA (99.99%)", "Throughput/Concurrent Users SLA"
                                  ],
                                  findings: "Journey duration (21.1s) easily passes the 30s business SLA threshold.",
                                  remediation: "Maintain current caching parameters."
                                },
                                {
                                  id: 23,
                                  name: "23. AI Root Cause Analysis",
                                  score: 96,
                                  status: "✅",
                                  checks: [
                                    "Slow Query Trace Matching", "Render-blocking Asset Flags", "Payload Heavy Endpoint Alerts"
                                  ],
                                  findings: "Accurately flagged slow database indexes and rendering polyfills.",
                                  remediation: "Apply suggested dynamic index optimization."
                                },
                                {
                                  id: 24,
                                  name: "24. AI Optimization Recommendations",
                                  score: 97,
                                  status: "✅",
                                  checks: [
                                    "Development Effort Estimates", "Business Impact Score Alignment", "Optimization Recommendations"
                                  ],
                                  findings: "Recommendations are categorized clearly by priority (High, Medium, Low) and development effort.",
                                  remediation: "No optimizations required."
                                }
                              ].map(cat => {
                                const isExpanded = expandedAuditCategory === cat.id;
                                return (
                                  <div
                                    key={cat.id}
                                    style={{
                                      border: "1px solid #e2e8f0",
                                      borderRadius: "12px",
                                      overflow: "hidden",
                                      background: isExpanded ? "#f8fafc" : "#ffffff",
                                      transition: "all 0.2s"
                                    }}
                                  >
                                    <div
                                      onClick={() => setExpandedAuditCategory(isExpanded ? null : cat.id)}
                                      style={{
                                        padding: "14px 18px",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        cursor: "pointer"
                                      }}
                                    >
                                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <span style={{ fontSize: "14px" }}>{cat.status}</span>
                                        <span style={{ fontWeight: "600", fontSize: "13px", color: "#1e293b" }}>{cat.name}</span>
                                      </div>
                                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                        <span style={{
                                          fontSize: "12px",
                                          fontWeight: "bold",
                                          padding: "3px 8px",
                                          borderRadius: "12px",
                                          background: cat.score >= 90 ? "#dcfce7" : "#fef9c3",
                                          color: cat.score >= 90 ? "#15803d" : "#854d0e"
                                        }}>
                                          {cat.score}/100
                                        </span>
                                        <span style={{ fontSize: "12px", color: "#64748b" }}>{isExpanded ? "▲" : "▼"}</span>
                                      </div>
                                    </div>

                                    {isExpanded && (
                                      <div style={{ padding: "16px 18px", borderTop: "1px solid #e2e8f0", background: "#ffffff", fontSize: "12px", color: "#475569", lineHeight: "1.6" }}>
                                        <div style={{ fontWeight: "700", color: "#334155", marginBottom: "8px" }}>Audit Checklist Sub-Items:</div>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px", marginBottom: "12px", paddingLeft: "10px" }}>
                                          {cat.checks.map((ch, i) => (
                                            <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                              <span style={{ color: "#10b981" }}>✓</span> {ch}
                                            </div>
                                          ))}
                                        </div>
                                        <div style={{ borderTop: "1px dashed #e2e8f0", paddingTop: "10px", marginTop: "10px" }}>
                                          <strong>AI Findings:</strong> {cat.findings}
                                        </div>
                                        <div style={{ marginTop: "6px" }}>
                                          <strong>Remediation:</strong> <span style={{ color: "#FD6101" }}>{cat.remediation}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Column 2: Scorecard Table & Final Executive Summary Report */}
                        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

                          {/* Enterprise Performance Scorecard */}
                          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "24px", borderRadius: "16px" }}>
                            <h4 style={{ margin: "0 0 16px 0", color: "#0f172a", fontSize: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
                              📊 Enterprise Performance Scorecard
                            </h4>
                            <div style={{ maxHeight: "320px", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "12px" }}>
                              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", textAlign: "left" }}>
                                <thead>
                                  <tr style={{ background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", color: "#475569" }}>
                                    <th style={{ padding: "10px 14px", fontWeight: "600" }}>Category</th>
                                    <th style={{ padding: "10px 14px", fontWeight: "600", width: "80px", textAlign: "center" }}>Score</th>
                                    <th style={{ padding: "10px 14px", fontWeight: "600", width: "60px", textAlign: "center" }}>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[
                                    { name: "Frontend Performance", score: 94, status: "✅" },
                                    { name: "User Journey", score: 92, status: "✅" },
                                    { name: "API Performance", score: 90, status: "✅" },
                                    { name: "Database Performance", score: 91, status: "✅" },
                                    { name: "Server Performance", score: 89, status: "🟡" },
                                    { name: "Network Performance", score: 95, status: "✅" },
                                    { name: "Load Testing", score: 93, status: "✅" },
                                    { name: "Stress Testing", score: 88, status: "🟡" },
                                    { name: "Spike Testing", score: 92, status: "✅" },
                                    { name: "Endurance Testing", score: 90, status: "✅" },
                                    { name: "Volume Testing", score: 91, status: "✅" },
                                    { name: "Scalability", score: 89, status: "🟡" },
                                    { name: "Stability", score: 93, status: "✅" },
                                    { name: "Infrastructure", score: 90, status: "✅" },
                                    { name: "Observability", score: 94, status: "✅" },
                                    { name: "Security Performance", score: 92, status: "✅" },
                                    { name: "Browser Performance", score: 91, status: "✅" },
                                    { name: "Mobile Performance", score: 88, status: "🟡" },
                                    { name: "AI Performance", score: 95, status: "✅" },
                                    { name: "Cost Optimization", score: 87, status: "🟡" },
                                    { name: "Performance Regression", score: 93, status: "✅" },
                                    { name: "SLA Compliance", score: 94, status: "✅" },
                                    { name: "AI Root Cause Analysis", score: 96, status: "✅" },
                                    { name: "AI Recommendations", score: 97, status: "✅" }
                                  ].map((row, i) => (
                                    <tr key={i} style={{ borderBottom: "1px solid #e2e8f0" }}>
                                      <td style={{ padding: "10px 14px", color: "#1e293b", fontWeight: "500" }}>{row.name}</td>
                                      <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: "bold", color: row.score >= 90 ? "#16a34a" : "#ca8a04" }}>{row.score}/100</td>
                                      <td style={{ padding: "10px 14px", textAlign: "center" }}>{row.status}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Final Performance Engineering Report Card */}
                          <div style={{ background: "#002247", border: "1px solid #334155", padding: "24px", borderRadius: "16px", color: "#f8fafc" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
                              <div>
                                <span style={{ background: "#22c55e", color: "#ffffff", padding: "3px 8px", borderRadius: "6px", fontSize: "10px", fontWeight: "bold" }}>APPROVED ✅</span>
                                <h4 style={{ margin: "4px 0 0 0", color: "#ffffff", fontSize: "16px" }}>🏁 Final Performance Engineering Report</h4>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <div style={{ fontSize: "24px", fontWeight: "bold", color: "#22c55e" }}>A+ Grade</div>
                                <div style={{ fontSize: "10px", color: "#94a3b8" }}>Health Score: 96/100</div>
                              </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", fontSize: "11px", borderBottom: "1px solid #334155", paddingBottom: "14px", marginBottom: "14px" }}>
                              <div>
                                <span style={{ color: "#94a3b8" }}>Production Readiness:</span>
                                <div style={{ color: "#22c55e", fontWeight: "bold" }}>READY TO DEPLOY</div>
                              </div>
                              <div>
                                <span style={{ color: "#94a3b8" }}>Estimated System Capacity:</span>
                                <div style={{ color: "#ffffff", fontWeight: "bold" }}>50,000 requests / minute</div>
                              </div>
                              <div>
                                <span style={{ color: "#94a3b8" }}>Max Concurrent Threads:</span>
                                <div style={{ color: "#ffffff", fontWeight: "bold" }}>15,000 Users</div>
                              </div>
                              <div>
                                <span style={{ color: "#94a3b8" }}>Est. Cost Savings:</span>
                                <div style={{ color: "#38bdf8", fontWeight: "bold" }}>$4,200 / Month</div>
                              </div>
                            </div>

                            <div style={{ fontSize: "11px", color: "#cbd5e1", display: "flex", flexDirection: "column", gap: "10px" }}>
                              <div>
                                <strong style={{ color: "#38bdf8" }}>Frontend Summary:</strong> Real-browser audits (FCP 0.5s, LCP 1.2s) pass key SLAs. Recommendations generated for bundle lazy-loading.
                              </div>
                              <div>
                                <strong style={{ color: "#38bdf8" }}>Backend Summary:</strong> API latencies are highly stable. Caching layer holds p95 at 180ms under 5k user loads.
                              </div>
                              <div>
                                <strong style={{ color: "#38bdf8" }}>Infrastructure Summary:</strong> Auto-scaling policies provision nodes within 180 seconds. DB pool shows zero deadlocks.
                              </div>
                              <div style={{ borderTop: "1px solid #334155", paddingTop: "10px", marginTop: "4px" }}>
                                <strong style={{ color: "#f59e0b" }}>Recommended Roadmap:</strong>
                                <ul style={{ margin: "4px 0 0 0", paddingLeft: "15px", listStyleType: "circle", color: "#94a3b8" }}>
                                  <li><span style={{ color: "#f8fafc" }}>Short-term (1-2 days):</span> Enable Brotli compression & configure database indexes.</li>
                                  <li><span style={{ color: "#f8fafc" }}>Mid-term (1-2 weeks):</span> Set up reserved cloud node instances & clean unused VM capacities.</li>
                                  <li><span style={{ color: "#f8fafc" }}>Long-term (1 month):</span> Partition large volume tables & optimize old Safari engine polyfills.</li>
                                </ul>
                              </div>
                            </div>
                          </div>

                        </div>
                      </div>

                    </div>
                  )}

                  {/* REPORT TAB 2: SCREENS */}
                  {selectedReportTab === "screens" && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "20px", borderRadius: "16px" }}>
                      <h4 style={{ margin: "0 0 16px 0", color: "#0f172a" }}>Audited Screens Breakdown</h4>

                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                        <thead>
                          <tr style={{ borderBottom: "1px solid #e2e8f0", color: "#64748b", textAlign: "left" }}>
                            <th style={{ padding: "12px 8px" }}>Screen Name</th>
                            <th style={{ padding: "12px 8px" }}>Load Time</th>
                            <th style={{ padding: "12px 8px" }}>TTFB</th>
                            <th style={{ padding: "12px 8px" }}>FCP</th>
                            <th style={{ padding: "12px 8px" }}>LCP</th>
                            <th style={{ padding: "12px 8px" }}>API Count</th>
                            <th style={{ padding: "12px 8px" }}>Status</th>
                            <th style={{ padding: "12px 8px" }}>AI Rating</th>
                          </tr>
                        </thead>
                        <tbody>
                          {journeyData.screens.map(screen => (
                            <React.Fragment key={screen.id}>
                              <tr
                                onClick={() => setExpandedScreen(expandedScreen === screen.id ? null : screen.id)}
                                style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer", background: expandedScreen === screen.id ? "#fcfaff" : "transparent" }}
                              >
                                <td style={{ padding: "14px 8px", fontWeight: "bold", color: "#FD6101" }}>
                                  {expandedScreen === screen.id ? "▼" : "▶"} {screen.name}
                                </td>
                                <td style={{ padding: "14px 8px" }}>{screen.time}</td>
                                <td style={{ padding: "14px 8px" }}>{screen.ttfb}</td>
                                <td style={{ padding: "14px 8px" }}>{screen.fcp}</td>
                                <td style={{ padding: "14px 8px" }}>{screen.lcp}</td>
                                <td style={{ padding: "14px 8px" }}>{screen.apis} calls</td>
                                <td style={{ padding: "14px 8px" }}>
                                  <span style={{
                                    fontSize: "11px", fontWeight: "bold", padding: "2px 8px", borderRadius: "12px",
                                    background: screen.status === "Excellent" ? "#ecfdf5" : screen.status === "Good" ? "#eff6ff" : screen.status === "Warning" ? "#fffbeb" : "#fef2f2",
                                    color: screen.status === "Excellent" ? "#10b981" : screen.status === "Good" ? "#3b82f6" : screen.status === "Warning" ? "#f59e0b" : "#ef4444"
                                  }}>{screen.status}</span>
                                </td>
                                <td style={{ padding: "14px 8px", fontWeight: "bold" }}>{screen.rating}</td>
                              </tr>

                              {/* Expanded Section */}
                              {expandedScreen === screen.id && (
                                <tr>
                                  <td colSpan="8" style={{ background: "#fdfdfd", padding: "20px", borderBottom: "1.5px solid #FD6101" }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
                                      {/* Column 1: Network Waterfall */}
                                      <div>
                                        <h5 style={{ margin: "0 0 10px 0", color: "#0f172a" }}>🌐 Network Waterfall</h5>
                                        <div style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "11px", fontFamily: "monospace" }}>
                                          <div>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}><span>index.html (TTFB)</span><span>120ms</span></div>
                                            <div style={{ height: "4px", background: "#FD6101", width: "12%" }}></div>
                                          </div>
                                          <div>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}><span>main.bundle.js (JS size)</span><span>650ms</span></div>
                                            <div style={{ height: "4px", background: "#a855f7", width: "65%" }}></div>
                                          </div>
                                          <div>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}><span>GET /api/v1/profile (API)</span><span>310ms</span></div>
                                            <div style={{ height: "4px", background: "#f59e0b", width: "31%" }}></div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Column 2: Slow Resources & CPU */}
                                      <div>
                                        <h5 style={{ margin: "0 0 10px 0", color: "#0f172a" }}>⚙️ Resource breakdown</h5>
                                        <div style={{ fontSize: "12px", color: "#475569", lineHeight: "1.5" }}>
                                          • Total Requests: <strong>62 network connections</strong><br />
                                          • Slow JS bundle: <code>vendor.bundle.js</code> (480 KB - uncompressed)<br />
                                          • CPU core rendering overhead: <strong>320 ms blocking</strong><br />
                                          <div style={{ background: "#faf5ff", border: "1px dashed #e9d5ff", padding: "10px", borderRadius: "6px", marginTop: "10px" }}>
                                            💡 <strong>AI Explanation:</strong> Render blockage occurred due to synchronous loading of Google Fonts and large bundle sizes. Compress static resources to reduce FCP.
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* REPORT TAB 3: APIS */}
                  {selectedReportTab === "apis" && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "20px", borderRadius: "16px" }}>
                      <h4 style={{ margin: "0 0 16px 0", color: "#0f172a" }}>🔌 API Performance Metrics</h4>

                      <div className="pe-grid-3" style={{ marginBottom: "24px" }}>
                        <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px" }}>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>Average API Response Time</div>
                          <div style={{ fontSize: "24px", fontWeight: "700", color: "#0f172a", marginTop: "4px" }}>245 ms</div>
                        </div>
                        <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px" }}>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>Max Response Time (Peak)</div>
                          <div style={{ fontSize: "24px", fontWeight: "700", color: "#ef4444", marginTop: "4px" }}>4,300 ms</div>
                        </div>
                        <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px" }}>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>API Error Rate</div>
                          <div style={{ fontSize: "24px", fontWeight: "700", color: "#10b981", marginTop: "4px" }}>0.4%</div>
                        </div>
                      </div>

                      {/* Heatmap & Latency Lists */}
                      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: "24px" }}>
                        <div>
                          <h5 style={{ margin: "0 0 12px 0" }}>Latency Heatmap Distribution</h5>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(10, 1fr)", gap: "6px", background: "#f8fafc", padding: "12px", borderRadius: "10px" }}>
                            {Array.from({ length: 40 }).map((_, i) => {
                              const heat = i % 7;
                              const colors = ["#ecfdf5", "#d1fae5", "#a7f3d0", "#6ee7b7", "#fef3c7", "#fde047", "#fee2e2", "#fecaca"];
                              return (
                                <div
                                  key={i}
                                  title={`Latency bucket: ${200 + i * 50}ms`}
                                  style={{ height: "30px", background: colors[heat] || "#ecfdf5", borderRadius: "4px" }}
                                ></div>
                              );
                            })}
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "#64748b", marginTop: "6px" }}>
                            <span>&lt; 100ms (Optimal)</span>
                            <span>&gt; 2000ms (SLA Breach)</span>
                          </div>
                        </div>

                        <div>
                          <h5 style={{ margin: "0 0 12px 0" }}>Top Slow APIs</h5>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            {journeyData.topApis.map((api, idx) => (
                              <div
                                key={idx}
                                style={{
                                  background: idx === 0 ? "#fdf2f2" : "#fffbeb",
                                  border: idx === 0 ? "1px solid #fecaca" : "1px solid #fef3c7",
                                  padding: "10px",
                                  borderRadius: "8px"
                                }}
                              >
                                <div style={{ fontSize: "11px", color: idx === 0 ? "#b91c1c" : "#d97706", fontWeight: "bold" }}>{api.path}</div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#475569", marginTop: "4px" }}>
                                  <span>Max latency:</span><strong>{api.time}</strong>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* REPORT TAB 4: RESOURCES */}
                  {selectedReportTab === "resources" && (
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "20px", borderRadius: "16px" }}>
                      <h4 style={{ margin: "0 0 16px 0", color: "#0f172a" }}>📦 Resource Size & Weight Analysis</h4>

                      <div className="pe-grid-2">
                        {/* Resource breakdown list */}
                        <div>
                          <h5 style={{ margin: "0 0 12px 0" }}>Asset Category Breakdown</h5>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "13px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f1f5f9", paddingBottom: "6px" }}>
                              <span>🖼️ Total Images</span><strong>{journeyData.resources.images}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f1f5f9", paddingBottom: "6px" }}>
                              <span>⚡ JavaScript Bundles</span><strong>{journeyData.resources.js}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f1f5f9", paddingBottom: "6px" }}>
                              <span>🎨 CSS Stylesheets</span><strong>{journeyData.resources.css}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f1f5f9", paddingBottom: "6px" }}>
                              <span>🔤 Web Fonts</span><strong>{journeyData.resources.fonts}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f1f5f9", paddingBottom: "6px" }}>
                              <span>🔌 Third-Party Scripts</span><strong>{journeyData.resources.thirdparty}</strong>
                            </div>
                          </div>
                        </div>

                        {/* Chart visualization */}
                        <div style={{ background: "#f8fafc", padding: "20px", borderRadius: "12px", textAlign: "center" }}>
                          <h5 style={{ margin: "0 0 16px 0", textAlign: "left" }}>Largest Resource Weight Payload</h5>
                          {/* Pie Chart Representation */}
                          <div style={{ display: "flex", gap: "12px", justifyContent: "center", alignItems: "center" }}>
                            <div style={{ width: "100px", height: "100px", borderRadius: "50%", background: journeyData.resources.pie }}></div>
                            <div style={{ textAlign: "left", fontSize: "11px", color: "#64748b" }}>
                              {journeyData.resources.pieLabel}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* REPORT TAB 5: BOTTLENECKS */}
                  {selectedReportTab === "bottlenecks" && (
                    <div>
                      <h4 style={{ margin: "0 0 16px 0", color: "#0f172a" }}>🤖 AI-Detected Bottlenecks</h4>

                      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        {journeyData.bottlenecks.map((issue, idx) => (
                          <div
                            key={idx}
                            style={{
                              background: "#fff",
                              border: issue.severity === "CRITICAL" ? "1px solid #fecaca" : "1px solid #fde047",
                              borderRadius: "12px",
                              padding: "20px"
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                              <span style={{
                                background: issue.severity === "CRITICAL" ? "#fef2f2" : "#fffbeb",
                                color: issue.severity === "CRITICAL" ? "#ef4444" : "#d97706",
                                padding: "4px 10px",
                                borderRadius: "6px",
                                fontSize: "11px",
                                fontWeight: "bold"
                              }}>
                                {issue.severity}
                              </span>
                              <span style={{ fontSize: "12px", color: "#64748b" }}>Confidence: <strong>{issue.confidence}</strong></span>
                            </div>
                            <h5 style={{ margin: "0 0 6px 0", fontSize: "16px", color: "#0f172a" }}>{issue.title}</h5>
                            <p style={{ margin: "0 0 12px 0", fontSize: "13px", color: "#475569", lineHeight: "1.5" }}>
                              <strong>Root Cause:</strong> {issue.rootCause}
                            </p>
                            <div style={{ fontSize: "12px", color: "#64748b", background: "#f8fafc", padding: "10px", borderRadius: "6px" }}>
                              • <strong>Evidence:</strong> {issue.evidence}<br />
                              • <strong>Suggested Fix:</strong> <code>{issue.fix}</code><br />
                              • <strong>Est. Improvement:</strong> {issue.improvement}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* REPORT TAB 6: RECOMMENDATIONS */}
                  {selectedReportTab === "recommendations" && (
                    <div>
                      <h4 style={{ margin: "0 0 16px 0", color: "#0f172a" }}>💡 AI Optimization Recommendations</h4>

                      <div className="pe-grid-3">
                        {journeyData.recommendations.map((rec, i) => (
                          <div key={i} style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "20px", borderRadius: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.01)" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                              <span style={{ fontSize: "11px", padding: "2px 6px", borderRadius: "4px", background: "#fff7ed", color: "#FD6101", fontWeight: "bold" }}>
                                {rec.time}
                              </span>
                              <span style={{ fontSize: "12px", color: rec.prio === "High" ? "#ef4444" : rec.prio === "Medium" ? "#f59e0b" : "#64748b", fontWeight: "bold" }}>
                                {rec.prio} Priority
                              </span>
                            </div>
                            <h5 style={{ margin: "0 0 6px 0", fontSize: "15px", color: "#0f172a" }}>{rec.title}</h5>
                            <p style={{ margin: "0 0 12px 0", fontSize: "12px", color: "#64748b", lineHeight: "1.4" }}>{rec.desc}</p>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "#475569", borderTop: "1px dashed #f1f5f9", paddingTop: "8px" }}>
                              <span>Difficulty: <strong>{rec.diff}</strong></span>
                              <span>Expected Imp: <strong style={{ color: "#10b981" }}>+{rec.imp}</strong></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reset analysis button */}
                  <div style={{ marginTop: "35px", display: "flex", justifyContent: "flex-end" }}>
                    <button
                      className="pe-btn-secondary"
                      onClick={() => {
                        setJourneyStep(1);
                        setDiscoveryPercent(0);
                        setRunPercent(0);
                      }}
                    >
                      🔄 Start Another Journey Analysis
                    </button>
                  </div>
                </div>
              )}

              {/* JOURNEY STEP 7: SAVED PREVIOUS REPORTS */}
              {journeyStep === 7 && (
                <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", padding: "30px", borderRadius: "16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                    <div>
                      <h3 style={{ margin: 0, color: "#0f172a", fontSize: "20px" }}>🗄️ Saved Previous Reports</h3>
                      <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: "13px" }}>Browse and retrieve all performance audits run on this workspace.</p>
                    </div>
                    <button
                      className="pe-btn-primary"
                      style={{ fontSize: "12px", padding: "6px 12px" }}
                      onClick={() => {
                        setIsLoadingReports(true);
                        axios.get(`${API_BASE}/api/performance/reports`)
                          .then(res => {
                            if (res.data && res.data.reports) {
                              setPreviousReports(res.data.reports);
                            }
                            setIsLoadingReports(false);
                          })
                          .catch(() => setIsLoadingReports(false));
                      }}
                    >
                      🔄 Refresh List
                    </button>
                  </div>

                  {isLoadingReports ? (
                    <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                      <div style={{ fontSize: "16px", marginBottom: "10px" }}>⏳ Loading saved reports...</div>
                    </div>
                  ) : previousReports.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "40px", border: "1px dashed #cbd5e1", borderRadius: "12px", color: "#64748b" }}>
                      <div style={{ fontSize: "24px", marginBottom: "10px" }}>📂</div>
                      <div style={{ fontSize: "14px" }}>No previous reports found. Run a new Performance test to save one.</div>
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", textAlign: "left" }}>
                        <thead>
                          <tr style={{ background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", color: "#475569" }}>
                            <th style={{ padding: "12px 16px", fontWeight: "600" }}>Run ID</th>
                            <th style={{ padding: "12px 16px", fontWeight: "600" }}>Target Site URL</th>
                            <th style={{ padding: "12px 16px", fontWeight: "600" }}>Audited Timestamp</th>
                            <th style={{ padding: "12px 16px", fontWeight: "600", width: "100px", textAlign: "center" }}>Screens</th>
                            <th style={{ padding: "12px 16px", fontWeight: "600", width: "240px", textAlign: "center" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previousReports.map((report, idx) => (
                            <tr key={idx} style={{ borderBottom: "1px solid #e2e8f0" }}>
                              <td style={{ padding: "14px 16px", fontWeight: "bold", color: "#FD6101" }}>
                                {report.run_id}
                              </td>
                              <td style={{ padding: "14px 16px", color: "#1e293b" }}>
                                <a href={report.target_url} target="_blank" rel="noreferrer" style={{ color: "FD6101", textDecoration: "none" }}>
                                  {report.target_url}
                                </a>
                              </td>
                              <td style={{ padding: "14px 16px", color: "#64748b" }}>
                                {new Date(report.timestamp).toLocaleString()}
                              </td>
                              <td style={{ padding: "14px 16px", textAlign: "center", fontWeight: "600", color: "#0f172a" }}>
                                {report.screens_count}
                              </td>
                              <td style={{ padding: "14px 16px", textAlign: "center" }}>
                                <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                  <button
                                    className="pe-btn-secondary"
                                    style={{ fontSize: "11px", padding: "4px 8px" }}
                                    onClick={() => {
                                      setIsLoadingReports(true);
                                      axios.get(`${API_BASE}${report.html_url.replace(".html", ".json")}`)
                                        .then(res => {
                                          if (res.data) {
                                            setJourneyData(res.data);
                                            setJourneyStep(6); // Go back to view active report
                                          }
                                          setIsLoadingReports(false);
                                        })
                                        .catch(() => setIsLoadingReports(false));
                                    }}
                                  >
                                    👁️ View Report
                                  </button>
                                  <button
                                    className="pe-btn-secondary"
                                    style={{ fontSize: "11px", padding: "4px 8px" }}
                                    onClick={() => window.open(`${API_BASE}${report.html_url}`, "_blank")}
                                  >
                                    🌐 HTML
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* TAB CONTENT: 3. SCENARIOS */}
      {subPage === "scenarios" && (
        <div className="pe-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h3 style={{ margin: 0 }}>📋 Reusable Performance Scenarios</h3>
            <button className="pe-btn-primary" onClick={() => setSubPage("create")}>Create Scenario</button>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #e2e8f0", color: "#64748b", textAlign: "left" }}>
                <th style={{ padding: "12px 8px" }}>Scenario Name</th>
                <th style={{ padding: "12px 8px" }}>Type</th>
                <th style={{ padding: "12px 8px" }}>Created By</th>
                <th style={{ padding: "12px 8px" }}>Version</th>
                <th style={{ padding: "12px 8px" }}>Last Modified</th>
                <th style={{ padding: "12px 8px", textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map(sc => (
                <tr key={sc.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "12px 8px", fontWeight: "bold" }}>{sc.name}</td>
                  <td style={{ padding: "12px 8px", textTransform: "capitalize" }}>{sc.type}</td>
                  <td style={{ padding: "12px 8px" }}>{sc.author}</td>
                  <td style={{ padding: "12px 8px" }}><span style={{ background: "#f1f5f9", padding: "2px 6px", borderRadius: "4px" }}>{sc.version}</span></td>
                  <td style={{ padding: "12px 8px" }}>{sc.updated}</td>
                  <td style={{ padding: "12px 8px", textAlign: "right" }}>
                    <button style={{ background: "none", border: "none", color: "#FD6101", cursor: "pointer", marginRight: "10px", fontWeight: 600 }} onClick={() => alert(`Cloned: ${sc.name}`)}>Clone</button>
                    <button style={{ background: "none", border: "none", color: "#FD6101", cursor: "pointer", fontWeight: 600 }} onClick={() => alert(`Exported: ${sc.name}`)}>Export</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB CONTENT: 4. LIVE RUN */}
      {subPage === "execution" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <div className="pe-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <h3 style={{ margin: 0 }}>Live Performance Execution</h3>
                <span style={{
                  background: execStatus === "running" ? "rgba(16,185,129,0.12)" : "#f1f5f9",
                  color: execStatus === "running" ? "#10b981" : "#64748b",
                  padding: "4px 10px",
                  borderRadius: "12px",
                  fontSize: "12px",
                  fontWeight: "bold"
                }}>
                  {execStatus.toUpperCase()}
                </span>
              </div>
              <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: "13px" }}>
                Running k6 engine on {form.url || "https://api.crm.platform/v1"}
              </p>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              {execStatus === "idle" && (
                <button className="pe-btn-primary" onClick={handleStartExec}>
                  ▶ Start Run
                </button>
              )}
              {execStatus === "running" && (
                <>
                  <button className="pe-btn-secondary" onClick={() => setExecStatus("paused")}>
                    ⏸ Pause
                  </button>
                  <button className="pe-btn-secondary" style={{ borderColor: "#ef4444", color: "#ef4444" }} onClick={() => setExecStatus("stopped")}>
                    ⏹ Stop
                  </button>
                </>
              )}
              {execStatus === "paused" && (
                <button className="pe-btn-primary" onClick={() => setExecStatus("running")}>
                  ▶ Resume
                </button>
              )}
              {execStatus === "stopped" && (
                <button className="pe-btn-secondary" onClick={handleStartExec}>
                  Reset & Rerun
                </button>
              )}
            </div>
          </div>

          <div className="pe-grid-3">
            <div className="pe-card" style={{ textAlign: "center" }}>
              <div className="pe-kpi-label">Active Virtual Users</div>
              <div className="pe-kpi-value" style={{ color: "#3b82f6" }}>{execMetrics.activeVus}</div>
              <div style={{ fontSize: "12px", color: "#64748b", marginTop: "6px" }}>Target: {form.vus} VUs</div>
            </div>
            <div className="pe-card" style={{ textAlign: "center" }}>
              <div className="pe-kpi-label">Live Response Time</div>
              <div className="pe-kpi-value" style={{ color: "#f59e0b" }}>{execMetrics.responseTime} ms</div>
              <div style={{ fontSize: "12px", color: "#64748b", marginTop: "6px" }}>p95: {Math.floor(execMetrics.responseTime * 1.15)} ms</div>
            </div>
            <div className="pe-card" style={{ textAlign: "center" }}>
              <div className="pe-kpi-label">Throughput / Error Rate</div>
              <div className="pe-kpi-value" style={{ color: "#10b981" }}>
                {execMetrics.throughput}/s <span style={{ color: execMetrics.errorRate > 0 ? "#ef4444" : "#10b981", fontSize: "16px" }}>({execMetrics.errorRate}%)</span>
              </div>
              <div style={{ fontSize: "12px", color: "#64748b", marginTop: "6px" }}>SLA Limit: &lt; 1%</div>
            </div>
          </div>

          <div className="pe-grid-3">
            <div className="pe-card">
              <h4 style={{ margin: "0 0 10px 0" }}>Host CPU</h4>
              <div style={{ fontSize: "20px", fontWeight: "bold" }}>{execMetrics.cpu}%</div>
            </div>
            <div className="pe-card">
              <h4 style={{ margin: "0 0 10px 0" }}>Host Memory</h4>
              <div style={{ fontSize: "20px", fontWeight: "bold" }}>{execMetrics.memory}%</div>
            </div>
            <div className="pe-card">
              <h4 style={{ margin: "0 0 10px 0" }}>Network I/O</h4>
              <div style={{ fontSize: "20px", fontWeight: "bold" }}>{execMetrics.network} MB/s</div>
            </div>
          </div>

          <div className="pe-card" style={{ background: "#0f172a", color: "#38bdf8", padding: "16px", borderRadius: "8px" }}>
            <h4 style={{ color: "#fff", margin: "0 0 12px 0", fontSize: "14px" }}>Live Engine Logs</h4>
            <div style={{ fontFamily: "monospace", fontSize: "12px", height: "180px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "6px" }}>
              {execMetrics.logs.length === 0 ? (
                <span style={{ color: "#64748b" }}>Logs will appear here once execution starts...</span>
              ) : (
                execMetrics.logs.map((log, idx) => <div key={idx}>{log}</div>)
              )}
            </div>
          </div>

          {execStatus === "stopped" && (
            <div style={{ background: "#ffffff", border: "1.5px solid #e2e8f0", padding: "28px", borderRadius: "16px", marginTop: "16px", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.05)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #f1f5f9", paddingBottom: "20px", marginBottom: "24px" }}>
                <div>
                  <span style={{ background: "#dcfce7", color: "#16a34a", padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase" }}>Test Completed ✅</span>
                  <h3 style={{ margin: "8px 0 4px 0", color: "#0f172a", fontSize: "20px", fontWeight: 800 }}>📋 AI Performance Audit Report</h3>
                  <p style={{ margin: 0, color: "#64748b", fontSize: "13px" }}>Target: {form.url || "https://api.crm.platform/v1"} | Run ID: <strong>PERF-EXEC-RUN-LAST</strong></p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: "#16a34a" }}>Grade A</div>
                  <div style={{ fontSize: "12px", color: "#94a3b8" }}>Health Score: 95/100</div>
                </div>
              </div>

              {/* KPI metrics */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "12px", marginBottom: "28px" }}>
                {[
                  { label: "Peak Load Reached", val: `${form.vus || 100} VUs` },
                  { label: "Avg Latency", val: "215 ms" },
                  { label: "Max Throughput", val: "185 req/s" },
                  { label: "SLA Adherence", val: "99.2%" },
                  { label: "Success Rate", val: "100%" }
                ].map((k, idx) => (
                  <div key={idx} style={{ background: "#f8fafc", padding: "12px", borderRadius: "10px", border: "1px solid #e2e8f0", textAlign: "center" }}>
                    <span style={{ fontSize: "10px", color: "#64748b", display: "block", marginBottom: "4px", textTransform: "uppercase" }}>{k.label}</span>
                    <strong style={{ fontSize: "14px", color: "#1e293b" }}>{k.val}</strong>
                  </div>
                ))}
              </div>

              {/* Recommendations */}
              <h4 style={{ margin: "0 0 14px 0", color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>💡 Qintra Recommendations</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>
                <div style={{ border: "1px solid #e2e8f0", padding: "16px", borderRadius: "12px", background: "#ffffff" }}>
                  <span style={{ background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: "6px", fontSize: "10px", fontWeight: "bold" }}>High Priority</span>
                  <h5 style={{ margin: "8px 0 6px 0", color: "#0f172a", fontSize: "13px", fontWeight: 700 }}>Enable Response Compression</h5>
                  <div style={{ fontSize: "12px", color: "#475569" }}>
                    Compressing text assets will yield ~22% bandwidth reduction under peak load.
                  </div>
                </div>
                <div style={{ border: "1px solid #e2e8f0", padding: "16px", borderRadius: "12px", background: "#ffffff" }}>
                  <span style={{ background: "#fef3c7", color: "#d97706", padding: "2px 8px", borderRadius: "6px", fontSize: "10px", fontWeight: "bold" }}>Medium Priority</span>
                  <h5 style={{ margin: "8px 0 6px 0", color: "#0f172a", fontSize: "13px", fontWeight: 700 }}>Increase Keep-Alive Timeout</h5>
                  <div style={{ fontSize: "12px", color: "#475569" }}>
                    Optimizing connection reuse avoids HTTP handshake overheads for fast client bursts.
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button className="pe-btn-secondary" style={{ padding: "8px 16px", fontSize: "12px" }} onClick={() => alert("Report PDF downloaded successfully.")}>Download PDF</button>
                <button className="pe-btn-secondary" style={{ padding: "8px 16px", fontSize: "12px" }} onClick={() => alert("Shareable link copied.")}>Share Report</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB CONTENT: 5. SCHEDULER */}
      {subPage === "scheduler" && (
        <div className="pe-card">
          <h3 style={{ margin: "0 0 20px 0" }}>📅 Schedule Runs</h3>
          <div className="pe-grid-2" style={{ marginBottom: "20px" }}>
            <div>
              <label className="pe-label">Frequency</label>
              <select className="pe-input">
                <option>One Time Only</option>
                <option>Daily</option>
                <option>Weekly</option>
                <option>Monthly</option>
                <option>Custom Cron Expression</option>
              </select>
            </div>
            <div>
              <label className="pe-label">Cron Expression (if selected)</label>
              <input type="text" className="pe-input" placeholder="e.g. 0 0 * * *" />
            </div>
          </div>

          <div className="pe-grid-2" style={{ marginBottom: "20px" }}>
            <div>
              <label className="pe-label">Target Hour / Trigger Time</label>
              <input type="time" className="pe-input" defaultValue="00:00" />
            </div>
            <div>
              <label className="pe-label">Recipient Slack / Email Alert Notifications</label>
              <input type="text" className="pe-input" defaultValue="qa-alerts@crm.com" />
            </div>
          </div>

          <button className="pe-btn-primary" onClick={() => alert("Schedule Saved Successfully")}>
            Save Cron Schedule
          </button>
        </div>
      )}

      {/* TAB CONTENT: 6. RESULTS */}
      {subPage === "results" && (
        <div className="pe-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h3 style={{ margin: 0 }}>📑 Results Repository</h3>
            <div>
              <label style={{ marginRight: "10px", fontSize: "14px", fontWeight: "600" }}>Filter by Test Type:</label>
              <select
                className="pe-input"
                style={{ width: "200px", display: "inline-block" }}
                value={resultsFilter}
                onChange={e => setResultsFilter(e.target.value)}
              >
                <option value="all">All Test Types</option>
                <option value="stress">Stress Tests</option>
                <option value="spike">Spike Tests</option>
                <option value="soak">Soak Tests</option>
                <option value="volume">Volume Tests</option>
                <option value="scalability">Scalability Tests</option>
              </select>
            </div>
          </div>

          <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "20px" }}>
            <h4 style={{ margin: "0 0 10px 0" }}>Filtered Test Metrics Summary</h4>
            <div className="pe-grid-3">
              <div>• Filtered Average Latency: <strong>312 ms</strong></div>
              <div>• Max Virtual Users peak: <strong>4,200 VUs</strong></div>
              <div>• Threshold pass achievements: <strong>96.8%</strong></div>
            </div>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #e2e8f0", color: "#64748b", textAlign: "left" }}>
                <th style={{ padding: "12px 8px" }}>Run ID</th>
                <th style={{ padding: "12px 8px" }}>Scenario</th>
                <th style={{ padding: "12px 8px" }}>Test Type</th>
                <th style={{ padding: "12px 8px" }}>Date</th>
                <th style={{ padding: "12px 8px" }}>Latency (p95)</th>
                <th style={{ padding: "12px 8px" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {[
                { id: "RUN-1024", name: "CRM_Checkout_Flow", type: "scalability", date: "2026-07-20", latency: "245 ms", status: "PASS" },
                { id: "RUN-1025", name: "Leads_Spike_Test", type: "spike", date: "2026-07-19", latency: "890 ms", status: "PASS" },
                { id: "RUN-1026", name: "Auth_Breakpoint_Stress", type: "stress", date: "2026-07-18", latency: "2450 ms", status: "FAIL" }
              ].filter(item => resultsFilter === "all" || item.type === resultsFilter).map(item => (
                <tr key={item.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "12px 8px", fontWeight: "bold" }}>{item.id}</td>
                  <td style={{ padding: "12px 8px" }}>{item.name}</td>
                  <td style={{ padding: "12px 8px", textTransform: "capitalize" }}>{item.type}</td>
                  <td style={{ padding: "12px 8px" }}>{item.date}</td>
                  <td style={{ padding: "12px 8px" }}>{item.latency}</td>
                  <td style={{ padding: "12px 8px", color: item.status === "PASS" ? "#10b981" : "#ef4444", fontWeight: "bold" }}>{item.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB CONTENT: 7. REPORTS */}
      {subPage === "reports" && (
        <div className="pe-card">
          <h3 style={{ margin: "0 0 20px 0" }}>📈 Individual Reports</h3>

          <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
            <div style={{ flex: 1 }}>
              <label className="pe-label">Select Report Type</label>
              <select className="pe-input" value={reportFilter} onChange={e => setReportFilter(e.target.value)}>
                <option value="stress">Stress Test Report</option>
                <option value="spike">Spike Test Report</option>
                <option value="soak">Soak Test Report</option>
                <option value="volume">Volume Test Report</option>
                <option value="scalability">Scalability Test Report</option>
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button className="pe-btn-primary" onClick={() => alert(`Generated ${reportFilter.toUpperCase()} PDF Report`)}>
                Generate Report
              </button>
            </div>
          </div>

          <div style={{ border: "1px solid #e2e8f0", padding: "20px", borderRadius: "10px", marginTop: "24px" }}>
            <h4 style={{ margin: "0 0 12px 0", textTransform: "capitalize" }}>Preview of latest {reportFilter} report:</h4>
            <div style={{ fontSize: "14px", color: "#475569", lineHeight: "1.6" }}>
              • Total Requests: <strong>1,204,500</strong><br />
              • Success Rate: <strong>99.85%</strong><br />
              • Average Throughput: <strong>250 RPS</strong><br />
              • Error Rate: <strong>0.15%</strong>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: 8. ANALYTICS */}
      {subPage === "analytics" && (
        <div className="pe-card">
          <h3 style={{ margin: "0 0 20px 0" }}>📊 Analytics Overview</h3>
          <div className="pe-tabs-container" style={{ borderBottomColor: "#f1f5f9" }}>
            {["Overview", "Stress Analytics", "Spike Analytics", "Capacity Analysis"].map(an => (
              <button key={an} className="pe-tab-btn" style={{ fontSize: "13px" }} onClick={() => alert(`View ${an}`)}>{an}</button>
            ))}
          </div>

          <div className="pe-grid-2">
            <div style={{ background: "#f8fafc", padding: "20px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <h4 style={{ margin: "0 0 10px 0" }}>Trend Analysis</h4>
              <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
                Response latency has decreased by 18% over the last 3 runs due to Redis Query cache implementation.
              </p>
            </div>
            <div style={{ background: "#f8fafc", padding: "20px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <h4 style={{ margin: "0 0 10px 0" }}>Historical Comparison</h4>
              <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
                Comparison of <code>RUN-1024</code> vs <code>RUN-1025</code> shows a stable baseline throughput.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: 9. AI INSIGHTS */}
      {subPage === "ai-insights" && (
        <div className="pe-card">
          <h3 style={{ margin: "0 0 20px 0" }}>🤖 AI Insights</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ borderLeft: "4px solid #FD6101", paddingLeft: "16px", background: "#fbf7ff", padding: "16px", borderRadius: "0 8px 8px 0" }}>
              <strong>Root Cause analysis</strong>
              <p style={{ margin: "6px 0 0 0", fontSize: "13px", color: "#475569" }}>
                The spike in <code>POST /api/v1/auth/login</code> was caused by unindexed password hashing fields in DB. Adding B-Tree index improves throughput by 30%.
              </p>
            </div>

            <div style={{ borderLeft: "4px solid #f59e0b", paddingLeft: "16px", background: "#fffbeb", padding: "16px", borderRadius: "0 8px 8px 0" }}>
              <strong>Optimization & Cost Estimation</strong>
              <p style={{ margin: "6px 0 0 0", fontSize: "13px", color: "#475569" }}>
                Scaling workers to 8 agents on spike runs increases target throughput capacity to 10,000 requests/sec. Estimated infra cost increment is $0.20/hour.
              </p>
            </div>

            <div style={{ borderLeft: "4px solid #10b981", paddingLeft: "16px", background: "#f0fdf4", padding: "16px", borderRadius: "0 8px 8px 0" }}>
              <strong>Failure Prediction & Business Impact</strong>
              <p style={{ margin: "6px 0 0 0", fontSize: "13px", color: "#475569" }}>
                With current scaling rate, Staging DB is predicted to exhaust connection pools at 5,500 VUs. Business impact is moderate (limited to CRM module).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: 10. SETTINGS */}
      {subPage === "settings" && (
        <div className="pe-card">
          <h3 style={{ margin: "0 0 20px 0" }}>⚙️ Settings & Defaults</h3>
          <div className="pe-grid-2" style={{ marginBottom: "20px" }}>
            <div>
              <label className="pe-label">Global SLA Response Threshold (ms)</label>
              <input type="number" className="pe-input" defaultValue="1500" />
            </div>
            <div>
              <label className="pe-label">AI Suggestion Mode</label>
              <select className="pe-input">
                <option>Conservative Suggestions</option>
                <option>Aggressive Load Recommendations</option>
                <option>Disabled</option>
              </select>
            </div>
          </div>

          <div className="pe-grid-3" style={{ marginBottom: "20px" }}>
            <div>
              <label className="pe-label">Distributed Workers Max Ceiling</label>
              <input type="number" className="pe-input" defaultValue="8" />
            </div>
            <div>
              <label className="pe-label">Default Execution Protocol</label>
              <select className="pe-input">
                <option>HTTPS</option>
                <option>HTTP</option>
                <option>gRPC</option>
              </select>
            </div>
            <div>
              <label className="pe-label">Environments Allowed</label>
              <input type="text" className="pe-input" defaultValue="Development, QA, Staging" />
            </div>
          </div>

          <button className="pe-btn-primary" onClick={() => alert("Settings Updated Successfully")}>
            Save Settings
          </button>
        </div>
      )}
    </div>
  );
}

